import { SvgIcon, SvgIconProps } from '@mui/material';
import { lightenColor } from '@shared/utils/color-helpers.utils';

interface StrokeOpacityIconProps extends SvgIconProps {
  strokeOpacity?: number | string;
}

interface StrokeColorIconProps extends SvgIconProps {
  strokeColor?: string;
}

export const SophieRobotIcon = ({
  color = '#7B5CFF',
  animate = true,
  size = 45,
  ...props
}) => {
  const body = lightenColor(color, 0.1);
  const antenna = lightenColor(color, 0.25);
  const dot = lightenColor(color, 0.35);
  const smile = lightenColor(color, 0.55);

  const eyeStyle: React.CSSProperties | undefined = animate
    ? {
        animation: 'sophie-blink 4020ms ease-in-out infinite',
        transformBox: 'fill-box',
        transformOrigin: 'center'
      }
    : undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <style>{`
        @keyframes sophie-blink {
          0%, 94.5% { transform: scaleY(1); }
          97.25%    { transform: scaleY(0.11); }
          100%      { transform: scaleY(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes sophie-blink {
            0%, 100% { transform: scaleY(1); }
          }
        }
      `}</style>

      {/* Antenna */}
      <line
        x1="12"
        y1="5"
        x2="12"
        y2="2.9"
        stroke={antenna}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="12" cy="2.2" r="1.2" fill={dot} />

      <rect x="3.4" y="5" width="17.2" height="14.6" rx="5.6" fill={body} />
      <rect x="1.5" y="10" width="2.1" height="4.6" rx="1.05" fill={color} />
      <rect x="20.4" y="10" width="2.1" height="4.6" rx="1.05" fill={color} />

      <rect x="5.7" y="7.5" width="12.6" height="9.3" rx="4" fill="#140F26" />

      <ellipse
        cx="9.6"
        cy="11.2"
        rx="1.65"
        ry="1.65"
        fill="#ffffff"
        style={eyeStyle}
      />
      <ellipse
        cx="14.4"
        cy="11.2"
        rx="1.65"
        ry="1.65"
        fill="#ffffff"
        style={eyeStyle}
      />

      <path
        d="M9.4 13.9c1 1 4.2 1 5.2 0"
        stroke={smile}
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default SophieRobotIcon;

export const RoomIcon = ({
  stroke,
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 13;

  return (
    <SvgIcon
      {...props}
      sx={{
        fontSize: size,
        width: size,
        height: height ?? size,
        '& path': { fill: color ?? 'currentColor' },
        ...sx
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="37"
        height="37"
        viewBox="0 0 37 37"
        fill="none"
      >
        <path
          d="M15.9673 0.347972C16.1448 0.203677 16.3522 0.100342 16.5747 0.0454094C16.7972 -0.00952278 17.0291 -0.0146842 17.2538 0.0302967L32.7538 3.11452C33.105 3.18437 33.4211 3.3731 33.6483 3.64861C33.8755 3.92413 33.9998 4.26942 34 4.6258V32.3746C33.9998 32.731 33.8755 33.0763 33.6483 33.3518C33.4211 33.6273 33.105 33.816 32.7538 33.8859L17.2538 36.9701C17.029 37.0148 16.7971 37.0093 16.5747 36.9541C16.3522 36.8989 16.1449 36.7953 15.9676 36.6507C15.7903 36.5062 15.6474 36.3243 15.5493 36.1182C15.4511 35.9121 15.4001 35.6869 15.4 35.4588V1.54157M24.7 18.4987C24.7 18.0897 24.5367 17.6974 24.246 17.4082C23.9553 17.119 23.5611 16.9565 23.15 16.9565C22.7389 16.9565 22.3447 17.119 22.054 17.4082C21.7633 17.6974 21.6 18.0897 21.6 18.4987C21.6 18.9076 21.7633 19.2999 22.054 19.5891C22.3447 19.8783 22.7389 20.0408 23.15 20.0408C23.5611 20.0408 23.9553 19.8783 24.246 19.5891C24.5367 19.2999 24.7 18.9076 24.7 18.4987ZM12.3 3.08368H4.55C4.13891 3.08368 3.74467 3.24615 3.45398 3.53536C3.1633 3.82456 3 4.2168 3 4.6258V32.3746C3 32.7836 3.1633 33.1758 3.45398 33.465C3.74467 33.7542 4.13891 33.9167 4.55 33.9167H12.3V3.08368ZM15.9673 0.347972C15.7898 0.492732 15.6469 0.674897 15.5488 0.881311C15.4507 1.08773 15.3998 1.31324 15.4 1.54157L15.9673 0.347972Z"
          fillOpacity="0.8"
        />
      </svg>
    </SvgIcon>
  );
};

export const ArchiveIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.6393 6.39551H8.53225C8.2684 6.39551 8.02114 6.26358 7.875 6.04398L6.76432 4.37796C6.61739 4.15756 6.37092 4.02563 6.10708 4.02563H2.57992C1.70701 4.02563 1 4.73265 1 5.60555V14.2951C1 15.168 1.70701 15.875 2.57992 15.875H13.6393C14.5122 15.875 15.2192 15.168 15.2192 14.2951V7.97542C15.2192 7.10252 14.5122 6.39551 13.6393 6.39551Z"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.58008 4.02563V3.23568C2.58008 2.36277 3.28709 1.65576 4.15999 1.65576H12.0596C12.9325 1.65576 13.6395 2.36277 13.6395 3.23568V6.39551"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.01095 12.7153V8.76554"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.52954 11.2342L8.01071 12.7153L9.49188 11.2342"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const UnarchiveIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.9508 6.39551H8.84377C8.57992 6.39551 8.33267 6.26358 8.18653 6.04398L7.07584 4.37796C6.92891 4.15756 6.68245 4.02563 6.4186 4.02563H2.89144C2.01854 4.02563 1.31152 4.73265 1.31152 5.60555V14.2951C1.31152 15.168 2.01854 15.875 2.89144 15.875H13.9508C14.8237 15.875 15.5308 15.168 15.5308 14.2951V7.97542C15.5308 7.10252 14.8237 6.39551 13.9508 6.39551Z"
        stroke="#727475"
        stroke-width="1.20665"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2.8916 4.02563V3.23568C2.8916 2.36277 3.59861 1.65576 4.47152 1.65576H12.3711C13.244 1.65576 13.951 2.36277 13.951 3.23568V6.39551"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.32247 8.76538V12.7152"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.84131 10.2466L8.32248 8.76538L9.80365 10.2466"
        stroke="#727475"
        strokeWidth="1.20665"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const ActiveIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.99985 16.8479C13.0584 16.8479 16.3486 13.5577 16.3486 9.49914C16.3486 5.44056 13.0584 2.15042 8.99985 2.15042C4.94126 2.15042 1.65112 5.44056 1.65112 9.49914C1.65112 13.5577 4.94126 16.8479 8.99985 16.8479ZM13.0848 7.66216C13.2879 7.43538 13.2686 7.08694 13.0418 6.8839C12.8151 6.68086 12.4666 6.70011 12.2636 6.92689L9.78767 9.69235C9.28597 10.2527 8.94811 10.6281 8.65925 10.8709C8.38407 11.1022 8.22245 11.1526 8.08125 11.1526C7.94007 11.1526 7.77844 11.1022 7.50326 10.8709C7.2144 10.6281 6.87654 10.2527 6.37484 9.69235L5.73611 8.97892C5.53307 8.75214 5.18463 8.73289 4.95785 8.93593C4.73107 9.13897 4.71182 9.48741 4.91486 9.71419L5.58087 10.4581C6.04814 10.98 6.43789 11.4154 6.79402 11.7147C7.17074 12.0314 7.57708 12.2549 8.08125 12.2549C8.58543 12.2549 8.99177 12.0314 9.36849 11.7147C9.72462 11.4154 10.1144 10.9801 10.5816 10.4581L13.0848 7.66216Z"
        fill="#34C759"
        fillOpacity="0.7"
      />
    </svg>
  </SvgIcon>
);

export const ReloadIcon = ({
  strokeOpacity = 0.6,
  ...props
}: StrokeOpacityIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
    >
      <path
        d="M18.2719 11.9542C18.0988 13.2742 17.5696 14.5221 16.7412 15.5642C15.9128 16.6063 14.8163 17.4032 13.5694 17.8695C12.3224 18.3358 10.9721 18.4538 9.66316 18.211C8.35423 17.9682 7.1361 17.3736 6.1394 16.4911C5.1427 15.6086 4.40503 14.4714 4.00551 13.2015C3.60599 11.9316 3.55968 10.5769 3.87157 9.28267C4.18345 7.98846 4.84175 6.80356 5.77587 5.85505C6.70999 4.90653 7.88468 4.23019 9.17398 3.89855C12.7481 2.98189 16.4477 4.82164 17.8136 8.24997"
        stroke="#727475"
        strokeOpacity={strokeOpacity}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.3333 3.66663V8.24996H13.75"
        stroke="#727475"
        strokeOpacity={strokeOpacity}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const DeactiveIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.9999 16.8497C13.0592 16.8497 16.3499 13.559 16.3499 9.49972C16.3499 5.44043 13.0592 2.14972 8.9999 2.14972C4.94061 2.14972 1.6499 5.44043 1.6499 9.49972C1.6499 13.559 4.94061 16.8497 8.9999 16.8497ZM10.9489 8.50868C11.1641 8.29341 11.1641 7.94437 10.9489 7.7291C10.7336 7.51382 10.3845 7.51382 10.1693 7.7291L8.99989 8.89847L7.83051 7.7291C7.61524 7.51382 7.2662 7.51382 7.05093 7.7291C6.83565 7.94437 6.83565 8.29341 7.05093 8.50868L8.2203 9.67806L7.05093 10.8474C6.83565 11.0627 6.83565 11.4117 7.05093 11.627C7.2662 11.8423 7.61524 11.8423 7.83051 11.627L8.99989 10.4576L10.1693 11.627C10.3845 11.8423 10.7336 11.8423 10.9489 11.627C11.1641 11.4117 11.1641 11.0627 10.9489 10.8474L9.77947 9.67806L10.9489 8.50868Z"
        fill="#9AA5B1"
      />
    </svg>
  </SvgIcon>
);

export const ScheduledIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        d="M8.92993 16.18C5.20191 16.18 2.17993 13.158 2.17993 9.42999C2.17993 5.70197 5.20191 2.67999 8.92993 2.67999C12.658 2.67999 15.6799 5.70197 15.6799 9.42999C15.6799 13.158 12.658 16.18 8.92993 16.18ZM9.60493 9.42999V6.05499H8.25493V10.78H12.3049V9.42999H9.60493Z"
        fill="#9A7FDC"
      />
    </svg>
  </SvgIcon>
);

export const DraftIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        d="M9.73487 7.29496V3.25246L13.7774 7.29496M4.58987 2.14996C3.77402 2.14996 3.11987 2.80411 3.11987 3.61996V15.38C3.11987 15.7698 3.27475 16.1437 3.55043 16.4194C3.8261 16.6951 4.2 16.85 4.58987 16.85H13.4099C13.7997 16.85 14.1736 16.6951 14.4493 16.4194C14.725 16.1437 14.8799 15.7698 14.8799 15.38V6.55996L10.4699 2.14996H4.58987Z"
        fill="#EB5757"
      />
    </svg>
  </SvgIcon>
);

export const TranslateIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="9"
      viewBox="0 0 10 9"
      fill="none"
    >
      <path
        d="M0.450195 1.725H3.4252M3.4252 1.725H5.3377M3.4252 1.725V0.875M6.4002 1.725H5.3377M5.3377 1.725C4.9892 2.88738 4.25862 3.986 3.4252 4.95203M3.4252 4.95203C2.735 5.75145 1.97382 6.45993 1.3002 7.0375M3.4252 4.95203C3.0002 4.4875 2.3202 3.5525 2.1502 3.2125M3.4252 4.95203L4.7002 6.1875M5.3377 8.525L5.82347 7.25M5.82347 7.25L7.0377 4.0625L8.25192 7.25M5.82347 7.25H8.25192M8.7377 8.525L8.25192 7.25"
        stroke="#727475"
        strokeWidth="0.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const CheckIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="7"
      viewBox="0 0 9 7"
      fill="none"
    >
      <path
        d="M8.00022 1.1875L3.37522 5.8125L1.27295 3.71023"
        stroke={props.color || '#7136ED'}
        strokeWidth="1.26136"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const HelpCenterWebIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="46"
      height="42"
      viewBox="0 0 46 42"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_2865)">
        <path
          d="M18.1207 35.3092C19.4581 33.8961 20.7558 32.5243 22.0536 31.1508C22.8212 30.3384 23.5906 29.5295 24.3514 28.7119C24.8058 28.2249 25.3565 27.9822 26.0209 27.9529C26.6835 27.9236 27.2911 27.7051 27.7817 27.2558C29.4082 25.767 31.0226 24.2662 32.6371 22.7619C33.0037 22.4211 33.0915 21.9977 32.9159 21.5949C32.7438 21.199 32.3445 20.9271 31.9211 21.008C31.6715 21.0562 31.4133 21.199 31.2223 21.3712C30.7748 21.7705 30.3669 22.2162 29.9366 22.6362C29.6009 22.965 29.2292 22.9873 28.9555 22.7119C28.6749 22.4314 28.6939 22.0682 29.0261 21.7309C29.718 21.0269 30.4271 20.3367 31.1138 19.6276C31.5252 19.2042 31.5372 18.6448 31.1792 18.2335C30.8522 17.8582 30.2601 17.7877 29.8574 18.0837C29.7662 18.1508 29.6887 18.2386 29.6078 18.3195C28.9796 18.946 28.3531 19.576 27.7231 20.2025C27.2877 20.6362 26.9228 20.6947 26.6199 20.3798C26.3255 20.0768 26.3841 19.724 26.7989 19.3057C27.6767 18.4211 28.5734 17.5553 29.4202 16.6431C29.6422 16.4039 29.8023 16.0321 29.8298 15.7085C29.8608 15.3212 29.5854 15.0218 29.2016 14.8755C28.8144 14.7274 28.4615 14.8049 28.1706 15.0854C27.6233 15.6121 27.0915 16.1526 26.5545 16.6896C26.1276 17.1147 25.7076 17.545 25.2756 17.9632C24.9193 18.3092 24.5872 18.3333 24.2946 18.0579C24.0054 17.7842 24.0278 17.3884 24.372 17.0407C25.2894 16.1147 26.2206 15.2042 27.1328 14.2713C27.7524 13.6379 27.5149 12.6896 26.6818 12.5502C26.4013 12.5037 26.002 12.6207 25.7885 12.8101C24.8901 13.607 24.0364 14.4589 23.1758 15.2989C23.0984 15.3746 23.0416 15.5175 23.045 15.6276C23.0794 16.9185 22.4856 17.736 21.2171 18.1216C21.1861 18.4865 21.2033 18.8617 21.1173 19.2128C20.8884 20.1457 20.2756 20.7051 19.3376 20.9219C19.2154 20.9512 19.0364 21.0596 19.0157 21.1595C18.6233 22.9667 17.9865 23.4865 16.2085 23.4245C15.4736 25.2076 14.2395 25.7498 12.6422 24.9512C11.9366 25.6138 11.2188 26.2868 10.465 26.9959C10.878 27.4555 11.2911 27.9047 11.6939 28.3625C11.9779 28.6861 11.9865 29.0166 11.7369 29.2851C11.4529 29.588 11.0639 29.5691 10.7249 29.2059C10.1104 28.545 9.49938 27.8823 8.90386 27.2042C7.15515 25.2162 5.32899 23.2885 3.68527 21.2162C-2.01525 14.0286 1.82985 3.27477 10.7954 1.32812C15.1947 0.372875 19.0605 1.51229 22.3238 4.62933C22.8987 5.17838 23.1293 5.18699 23.6835 4.63621C25.4357 2.89611 27.5269 1.78596 29.9366 1.26616C30.2739 1.19387 30.6354 1.1715 30.7989 1.53294C30.8849 1.72227 30.8763 2.01831 30.7834 2.20248C30.7042 2.35738 30.4546 2.47442 30.2601 2.51401C28.0123 2.98389 26.0897 4.04069 24.4701 5.66547C23.5717 6.56737 22.4564 6.56565 21.5493 5.66547C18.9813 3.11986 15.8935 2.04413 12.3014 2.35566C7.68871 2.7567 3.4684 6.5622 2.55274 11.1594C1.80231 14.9236 2.7369 18.2386 5.24637 21.1181C6.61986 22.6947 8.01229 24.2524 9.39267 25.8221C9.44947 25.8858 9.48389 25.9667 9.55274 26.0803C10.3014 25.3763 10.9848 24.7326 11.699 24.0613C10.9658 23.4211 10.4856 22.6775 10.6422 21.6878C10.7954 20.7206 11.3892 20.1095 12.3393 19.786C12.0983 19.0562 12.1379 18.3522 12.5545 17.6982C12.9761 17.0372 13.6095 16.7171 14.3565 16.6224C14.5803 14.9477 15.119 14.3746 16.7231 14.1061C16.8298 13.1818 17.2309 12.4245 18.1414 12.0424C19.0243 11.6706 19.8436 11.8514 20.5596 12.3901C20.8212 12.008 21.0278 11.6138 21.3204 11.2971C21.8884 10.6827 22.8298 10.6706 23.4426 11.2438C23.7851 11.5639 24.0932 11.9219 24.446 12.2937C25.286 11.3643 26.224 10.9116 27.3944 11.409C28.2808 11.786 28.7111 12.5123 28.7903 13.4916C29.5734 13.5364 30.2584 13.8083 30.7231 14.483C31.1827 15.1508 31.2653 15.8789 30.9951 16.6328C32.5166 17.3625 32.9434 18.2059 32.5786 19.7481C33.5596 20.1181 34.2016 20.7636 34.2808 21.872C34.3634 22.9959 33.6336 23.6465 32.9211 24.2644C33.8178 25.1577 34.6973 26.0338 35.6061 26.9409C36.2068 26.2765 36.8385 25.5983 37.4495 24.9013C38.7679 23.3935 40.1379 21.9219 41.3789 20.3522C46.4977 13.8737 42.7782 4.04413 34.6508 2.5209C34.0209 2.40213 33.3755 2.36083 32.7369 2.29198C32.255 2.24035 32.0071 2.01143 32.0312 1.6104C32.0536 1.21969 32.3462 0.985612 32.8144 1.00454C36.1311 1.13707 39.0002 2.34189 41.3393 4.69646C43.9348 7.30919 45.1569 10.4951 44.9916 14.1715C44.866 16.9564 43.9039 19.4641 42.0622 21.5656C39.2033 24.829 36.3066 28.0579 33.3892 31.2679C30.8953 34.0114 28.3479 36.7085 25.8212 39.4228C25.5286 39.736 25.2429 40.0665 24.9073 40.3281C23.6422 41.3178 21.9004 41.2076 20.7782 40.0286C18.9899 38.1526 17.236 36.2438 15.4701 34.3471C14.5132 33.3195 13.5562 32.2954 12.6147 31.2541C12.4632 31.0854 12.3032 30.8324 12.3187 30.6293C12.3324 30.4348 12.5304 30.2008 12.7128 30.082C12.9727 29.9116 13.2378 30.0493 13.4391 30.2627C14.1052 30.9736 14.7679 31.6878 15.4288 32.4004C16.3135 33.3539 17.1965 34.3092 18.1207 35.3057V35.3092ZM31.8935 25.2317C30.8367 26.2042 29.7851 27.1732 28.7317 28.1405C27.9882 28.8238 27.1001 29.1629 26.1001 29.2524C25.8694 29.273 25.5837 29.3298 25.4323 29.4796C24.6543 30.2524 23.9107 31.0631 23.1276 31.8875C24.5424 33.2421 25.9297 34.5708 27.3376 35.9185C29.8092 33.2421 32.2412 30.6104 34.656 27.9959C33.7111 27.051 32.8126 26.1526 31.8918 25.2317H31.8935ZM26.4546 36.8617C25.0398 35.5071 23.6508 34.1767 22.2429 32.829C21.1483 33.9873 20.0846 35.1147 19.002 36.261C19.9624 37.2834 20.866 38.2954 21.8281 39.249C22.4736 39.8875 23.551 39.8737 24.2033 39.2369C24.9779 38.4796 25.6973 37.6655 26.4529 36.8617H26.4546ZM15.6044 16.4675C15.7558 16.7274 15.8712 17.0218 16.0674 17.2421C16.4977 17.7188 16.9675 18.1612 17.4237 18.6121C17.718 18.903 18.0002 19.2111 18.3238 19.4675C18.718 19.7825 19.2636 19.724 19.6095 19.378C19.9693 19.02 20.0553 18.4658 19.7111 18.0975C18.9211 17.2541 18.0966 16.44 17.2584 15.6431C16.9675 15.366 16.5803 15.3643 16.2171 15.5553C15.8712 15.736 15.6904 16.0252 15.6044 16.4693V16.4675ZM17.7094 21.039C17.6749 20.9546 17.6457 20.7137 17.5114 20.5725C16.7197 19.7481 15.9211 18.9288 15.0897 18.1474C14.7076 17.7894 14.1328 17.8686 13.773 18.2369C13.4064 18.6121 13.3496 19.1698 13.7162 19.5536C14.4942 20.3712 15.2963 21.1663 16.1121 21.946C16.403 22.2249 16.7903 22.2317 17.1551 22.0579C17.5252 21.8823 17.7042 21.5743 17.7111 21.0372L17.7094 21.039ZM19.0725 13.0751C18.6164 13.1577 18.3169 13.3522 18.1431 13.7188C17.952 14.1233 18.0209 14.5088 18.3273 14.8255C18.8729 15.3918 19.4305 15.9477 19.9916 16.4985C20.4736 16.9718 21.0467 16.9908 21.4736 16.5656C21.8866 16.1526 21.8677 15.5519 21.4064 15.0768C20.8884 14.5433 20.3703 14.008 19.823 13.5054C19.6147 13.3144 19.3255 13.2162 19.0725 13.0751ZM13.9159 24.0132C14.3066 24.0097 14.5975 23.8359 14.7851 23.4951C14.9813 23.1354 14.9813 22.7567 14.7163 22.4538C14.3342 22.0149 13.9176 21.6001 13.4787 21.2162C13.1758 20.9512 12.7937 20.9477 12.4374 21.1491C12.0863 21.3453 11.9039 21.65 11.9176 22.0613C11.9366 22.6827 13.2997 24.0149 13.9159 24.0132ZM22.4288 14.2507C22.7817 13.8496 23.1173 13.4693 23.4667 13.0734C23.2446 12.8531 22.9641 12.5897 22.7025 12.3092C22.4598 12.0493 22.267 12.1061 22.0794 12.366C21.6681 12.9409 21.7782 13.6414 22.4288 14.249V14.2507Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_2865">
          <rect width="46" height="42" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterDemoIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="45"
      height="44"
      viewBox="0 0 45 44"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_2889)">
        <path
          d="M29.5209 35.6978C29.3183 37.3204 28.5403 38.2891 27.1782 38.6058C26.8803 38.6756 26.5602 38.6739 26.2503 38.6756C23.9281 38.6807 21.6024 38.6279 19.2818 38.6943C17.4465 38.7471 16.0572 37.4327 16.013 35.7114C15.9125 35.6978 15.807 35.6706 15.6997 35.6706C12.2299 35.6689 8.75844 35.657 5.28866 35.6757C3.61336 35.6859 2.30751 36.9918 2.27686 38.6194C2.24621 40.3475 3.59803 41.7214 5.35335 41.7214C14.1504 41.7248 22.9474 41.7231 31.7427 41.7231C32.989 41.7231 34.2353 41.7197 35.4832 41.7248C35.9242 41.7265 36.2119 41.953 36.2272 42.3156C36.2426 42.6731 36.0689 42.9098 35.7131 42.9864C35.6041 43.0102 35.4866 42.9983 35.3743 42.9983C25.4025 42.9983 15.429 43 5.45721 42.9983C3.54015 42.9983 1.99594 41.9836 1.32003 40.3049C0.562401 38.4236 1.17872 36.3329 2.86424 35.1598C3.11792 34.9845 3.20475 34.8074 3.20475 34.5061C3.19623 27.3247 3.19453 20.1417 3.19964 12.9604C3.20134 10.3947 4.70979 8.26477 7.08995 7.53438C7.7267 7.33859 8.42133 7.31646 9.09384 7.25006C9.48712 7.2109 9.88722 7.22963 10.2822 7.24665C11.079 7.279 11.7719 7.19898 12.4649 6.63204C13.4166 5.85057 14.6135 5.74842 15.8087 6.09063C16.0623 6.16384 16.2343 6.12638 16.4607 5.98507C17.2507 5.49474 18.0696 5.05038 18.8681 4.57537C19.006 4.49364 19.161 4.35744 19.2018 4.21443C19.7807 2.21054 21.313 0.993218 23.2726 1.00003C25.1982 1.00684 26.7254 2.24799 27.2719 4.23486C27.3059 4.35914 27.4353 4.48343 27.5511 4.55323C28.4007 5.05548 29.2553 5.54922 30.1168 6.02934C30.2411 6.09914 30.4301 6.13149 30.5646 6.09574C32.0781 5.68883 33.4129 6.0055 34.5996 7.02362C34.7426 7.1462 34.9674 7.21771 35.1597 7.23303C36.1438 7.31135 37.1619 7.22793 38.1103 7.44926C40.6726 8.04685 42.3206 10.267 42.3223 12.9621C42.3292 20.0872 42.3274 27.2124 42.3172 34.3375C42.3172 34.7393 42.4126 34.993 42.7599 35.2398C44.2632 36.3107 44.8762 38.238 44.3211 39.9899C43.761 41.7572 42.13 42.9694 40.2725 42.9949C39.6068 43.0034 38.9411 43.0017 38.2754 42.9966C37.7953 42.9932 37.5348 42.7736 37.528 42.3786C37.5212 41.9734 37.8021 41.7282 38.2924 41.7231C38.9445 41.718 39.5949 41.7265 40.2469 41.7197C41.8831 41.701 43.2213 40.3747 43.2451 38.7522C43.2707 37.0871 41.9835 35.6927 40.3253 35.6808C36.7976 35.6536 33.2716 35.6706 29.744 35.6689C29.6759 35.6689 29.6078 35.6859 29.5192 35.6978H29.5209ZM11.8673 19.8846C11.8622 21.9345 13.8814 23.3033 15.7831 22.5185C16.219 22.338 16.5186 22.4231 16.9051 22.6819C17.77 23.2608 18.6621 23.8141 19.6002 24.2602C20.1059 24.5002 20.3732 24.7454 20.4617 25.3123C20.6813 26.7339 21.9378 27.7384 23.3305 27.684C24.7453 27.6278 25.9115 26.5603 26.0631 25.137C26.1073 24.7284 26.2759 24.5019 26.659 24.364C27.7605 23.969 28.765 23.3987 29.652 22.6308C29.9534 22.3703 30.2428 22.3635 30.6259 22.4793C31.1145 22.6257 31.6542 22.7568 32.1497 22.6972C33.3942 22.5508 34.2234 21.8051 34.5264 20.5895C34.8295 19.3756 34.4362 18.3523 33.4317 17.5964C33.1048 17.3495 33.006 17.0754 33.0809 16.6804C33.2921 15.5721 33.2938 14.4518 33.0963 13.3452C33.0128 12.8787 33.1116 12.5824 33.4964 12.2947C34.6575 11.4247 34.9554 9.84473 34.2336 8.60869C33.5168 7.38286 32.0186 6.86018 30.7042 7.42202C30.2445 7.61951 29.9346 7.50204 29.5448 7.24155C28.702 6.68141 27.832 6.14681 26.9263 5.69904C26.4325 5.45558 26.0086 5.27341 26.0392 4.63496C26.046 4.51407 25.9899 4.38638 25.9507 4.2655C25.5523 2.98349 24.3656 2.1884 22.9849 2.27523C21.7505 2.35355 20.6524 3.32059 20.4685 4.59239C20.3817 5.19509 20.0889 5.45047 19.5577 5.70245C18.6332 6.1417 17.7564 6.69503 16.9034 7.26538C16.5186 7.52247 16.2139 7.60589 15.778 7.42202C14.4415 6.85847 12.9263 7.39478 12.2231 8.64104C11.5166 9.89581 11.8366 11.4434 13.0148 12.3151C13.3604 12.5705 13.4745 12.865 13.3979 13.2651C13.1816 14.4041 13.1833 15.5431 13.3979 16.6821C13.4728 17.0805 13.3621 17.3734 13.0114 17.6288C12.2555 18.177 11.8724 18.9397 11.8656 19.8829L11.8673 19.8846ZM22.7193 31.8399C23.8957 31.8399 25.0705 31.8399 26.2469 31.8399C29.2366 31.8399 32.2246 31.8092 35.2125 31.8535C36.6273 31.8739 37.7851 30.992 37.6523 29.3882C37.6318 29.1499 37.6489 28.9064 37.6489 28.6663C37.6489 23.8226 37.6506 18.9772 37.6489 14.1334C37.6489 12.9178 36.9049 12.0648 35.7863 11.9406C35.5326 11.9116 35.4066 11.9967 35.2755 12.1993C35.0916 12.482 34.8856 12.7612 34.6388 12.9876C34.4294 13.1783 34.4106 13.369 34.414 13.621C34.4311 14.5233 34.4328 15.4274 34.414 16.3297C34.4089 16.5885 34.4549 16.7656 34.6507 16.958C35.5224 17.8143 35.9668 18.8699 35.885 20.0923C35.6995 22.8504 33.2478 24.5683 30.5714 23.8549C30.4318 23.8175 30.2411 23.8464 30.1134 23.9179C29.2519 24.3998 28.4007 24.8969 27.5528 25.4026C27.437 25.4724 27.3076 25.5949 27.2736 25.7175C26.739 27.6789 25.1794 28.9507 23.2709 28.9643C21.3504 28.9796 19.7773 27.7214 19.2069 25.7363C19.1695 25.6035 19.0333 25.4707 18.9073 25.3957C18.073 24.8969 17.232 24.4066 16.3841 23.9299C16.2564 23.8584 16.0777 23.8328 15.9278 23.8464C15.4375 23.8924 14.9421 24.0541 14.4637 24.0133C12.7151 23.8652 11.4433 22.9747 10.8458 21.3114C10.2499 19.6565 10.6329 18.1702 11.8707 16.9069C11.9796 16.7962 12.0512 16.597 12.0494 16.4404C12.0426 15.7474 11.9184 15.0443 12.0001 14.3667C12.0903 13.6312 11.9643 13.0915 11.4536 12.5126C11.0756 12.0836 10.7623 11.8333 10.2056 11.9235C10.1358 11.9355 10.0643 11.9235 9.99278 11.9269C8.69033 11.9865 7.87652 12.8276 7.87652 14.1351C7.87311 19.2904 7.87482 24.4474 7.87652 29.6027C7.87652 29.8275 7.88503 30.059 7.94292 30.2735C8.20852 31.2695 9.00871 31.8416 10.1409 31.8416C14.3343 31.845 18.5259 31.8416 22.7193 31.8416V31.8399ZM35.9446 10.6449C37.9621 10.9752 38.97 12.5637 38.9479 14.4382C38.8866 19.4386 38.9275 24.4389 38.9241 29.441C38.9241 29.7645 38.9258 30.0982 38.8577 30.4114C38.495 32.0901 37.1551 33.1151 35.3385 33.1151C28.5811 33.1168 21.8237 33.1151 15.0664 33.1151C13.3945 33.1151 11.7226 33.127 10.0507 33.1117C8.10296 33.0946 6.61494 31.6935 6.60813 29.8155C6.5894 24.5172 6.5928 19.2189 6.60813 13.9206C6.61153 12.4088 7.62795 11.1387 9.10065 10.7726C9.57395 10.6551 10.0745 10.6483 10.5393 10.5939C10.6142 9.91964 10.6891 9.24033 10.7742 8.481C9.84636 8.52356 8.8708 8.45546 7.93951 8.63423C5.81304 9.03943 4.47995 10.7726 4.47654 12.991C4.47144 20.0038 4.47484 27.0166 4.47484 34.0276C4.47484 34.1604 4.47484 34.2932 4.47484 34.4005H41.0318C41.0403 34.3341 41.0488 34.2915 41.0488 34.2507C41.0488 27.083 41.0488 19.9136 41.0437 12.7459C41.0437 11.6018 40.596 10.6279 39.8009 9.81579C38.6568 8.64614 37.2317 8.40268 35.7216 8.5508C35.7982 9.26246 35.8697 9.92986 35.9463 10.6449H35.9446ZM28.1981 35.6978H17.3307C17.3137 36.607 17.9811 37.3782 18.8511 37.385C21.456 37.4055 24.0609 37.4055 26.6641 37.385C27.5358 37.3782 28.2032 36.6206 28.1981 35.6978Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M23.2246 11.9193C21.7094 11.9193 20.1924 11.9279 18.6771 11.9125C18.3928 11.9091 18.2413 12.0079 18.1187 12.253C17.2896 13.9062 17.2232 15.5815 17.9212 17.2926C17.9944 17.4713 18.1153 17.7097 18.054 17.8459C17.9553 18.0621 17.7493 18.3005 17.5347 18.3771C17.2215 18.4894 16.9746 18.2732 16.8333 17.9753C16.3668 16.9861 16.1352 15.939 16.1693 14.8494C16.28 11.4137 18.5563 8.73218 21.9341 8.02733C25.6048 7.25948 29.4372 9.8133 30.1574 13.5044C30.9508 17.5684 28.3901 21.302 24.2836 21.9609C22.1776 22.298 20.2741 21.7175 18.6346 20.3367C18.2941 20.0507 18.2481 19.7017 18.4848 19.4071C18.7248 19.1075 19.1096 19.0751 19.4603 19.368C20.5091 20.2414 21.7077 20.684 23.0697 20.7657C25.8602 20.9309 28.6302 18.6205 28.9741 15.8437C29.1307 14.5787 28.9298 13.3988 28.3663 12.265C28.2352 12.0028 28.0667 11.9091 27.7704 11.9125C26.2552 11.9279 24.7382 11.9193 23.2229 11.9193H23.2246ZM26.9958 10.6152C24.7961 8.56533 21.2003 8.87009 19.542 10.6152H26.9958Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_2889">
          <rect width="45" height="44" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterRequestFeatureIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_2905)">
        <path
          d="M22.4242 41.6495C23.4911 41.4078 24.4873 41.1859 25.4819 40.9574C28.8174 40.1946 32.1528 39.4286 35.49 38.6707C35.7711 38.6066 35.9437 38.5261 35.942 38.1743C35.9289 34.8503 35.9338 31.5264 35.9338 28.2024C35.9338 28.135 35.9272 28.0692 35.919 27.9509C35.0461 28.1432 34.1995 28.3273 33.3529 28.5147C32.8844 28.6183 32.554 28.4605 32.4783 28.0955C32.3961 27.7043 32.5967 27.438 33.0734 27.3295C35.5853 26.7574 38.0988 26.1886 40.6124 25.6182C40.6387 25.6116 40.6633 25.6001 40.7274 25.5754C40.6403 25.4768 40.5696 25.3847 40.4874 25.3025C39.192 24.0055 37.8999 22.7068 36.593 21.4213C36.4829 21.3128 36.2593 21.2257 36.1179 21.2569C31.8471 22.2153 27.5812 23.1901 23.3136 24.1633C23.2478 24.1781 23.1854 24.2094 23.0851 24.2472C23.1591 24.3376 23.2182 24.4181 23.2873 24.4888C24.5728 25.776 25.8616 27.0599 27.1406 28.3536C27.328 28.5427 27.4907 28.5838 27.7538 28.5197C28.5478 28.3224 29.3467 28.1481 30.1506 27.9953C30.5385 27.9213 30.818 28.1333 30.887 28.4736C30.9495 28.7778 30.7555 29.0589 30.4021 29.1789C30.3758 29.1871 30.3511 29.2002 30.3248 29.2068C29.3401 29.4287 28.3554 29.6507 27.3707 29.8709C27.0502 29.9416 26.8282 29.7805 26.6145 29.5652C25.3602 28.3043 24.101 27.05 22.8451 25.7908C22.7399 25.6839 22.6495 25.5623 22.5508 25.4489C22.5147 25.4587 22.4785 25.4669 22.4423 25.4768C22.4357 25.6264 22.4226 25.776 22.4226 25.9272C22.4226 28.0068 22.4226 30.0847 22.4226 32.1642C22.4226 32.3006 22.4275 32.4387 22.4127 32.5735C22.3766 32.9335 22.1925 33.1571 21.8127 33.1702C21.4675 33.1818 21.2423 32.9681 21.1995 32.59C21.1831 32.4552 21.188 32.3171 21.188 32.1806C21.188 30.1011 21.188 28.0232 21.188 25.9437C21.188 25.7974 21.188 25.6494 21.188 25.388C21.0088 25.559 20.9069 25.6543 20.8066 25.7546C19.5392 27.0221 18.2685 28.2846 17.0092 29.5586C16.7314 29.8397 16.4536 29.9219 16.0558 29.8298C13.3992 29.215 10.7361 28.6249 8.07628 28.0281C7.95957 28.0018 7.83792 27.9936 7.69654 27.9739C7.68997 28.1153 7.6801 28.2353 7.6801 28.3553C7.6801 31.597 7.68503 34.8388 7.67188 38.0806C7.67188 38.4521 7.78367 38.605 8.1519 38.6806C9.22044 38.8992 10.2791 39.159 11.3411 39.4056C11.8046 39.5141 12.0299 39.805 11.9477 40.17C11.8655 40.5366 11.5219 40.7092 11.0682 40.6056C9.93882 40.3475 8.80946 40.0845 7.6801 39.8231C6.86144 39.6341 6.45047 39.1294 6.44882 38.2811C6.44553 34.9161 6.44718 31.551 6.44718 28.1859C6.44718 28.0232 6.44718 27.8605 6.44718 27.6681C5.48385 27.4478 4.5419 27.2308 3.59994 27.0188C2.94896 26.8725 2.28811 26.7558 1.64535 26.5749C1.42342 26.5125 1.1719 26.3382 1.06176 26.1426C0.90559 25.8664 1.07984 25.6067 1.30177 25.3864C3.03773 23.6603 4.76382 21.9227 6.50471 20.2015C6.65924 20.0486 6.88445 19.9303 7.09652 19.8793C8.43301 19.5555 9.77608 19.2546 11.2128 18.9242C10.843 18.5593 10.4961 18.2601 10.2035 17.9149C9.90923 17.568 9.5673 17.2113 9.42592 16.797C9.12673 15.9225 9.70703 14.9805 10.6112 14.7931C11.3181 14.6451 12.0381 14.5547 12.7548 14.4643C13.1806 14.4101 13.4666 14.6221 13.5159 14.9673C13.5686 15.3422 13.3368 15.62 12.9028 15.6857C12.3093 15.7745 11.7093 15.8353 11.1208 15.9504C10.935 15.9866 10.6967 16.1279 10.6293 16.2841C10.575 16.4074 10.6983 16.6754 10.8232 16.7986C12.2501 18.214 13.6951 19.613 15.1286 21.0235C15.3292 21.2224 15.4755 21.4755 15.6662 21.686C15.7582 21.7879 15.8881 21.8882 16.018 21.9178C17.8641 22.3468 19.7118 22.7644 21.5612 23.1786C21.7141 23.2131 21.8883 23.2247 22.0396 23.1918C23.8758 22.7792 25.7104 22.3616 27.5401 21.926C27.7094 21.8849 27.8639 21.7271 27.9971 21.5956C28.1614 21.4344 28.2831 21.229 28.4475 21.0679C29.8728 19.6689 31.3079 18.2814 32.7233 16.8726C32.8745 16.723 33.0323 16.4206 32.9764 16.2677C32.9173 16.1066 32.6131 15.9767 32.3994 15.9422C30.3626 15.6265 28.3226 15.3372 26.2841 15.0315C25.6824 14.941 25.2731 14.5876 25.0085 14.05C24.1092 12.227 23.2084 10.4055 22.3075 8.58244C22.1974 8.36051 22.0987 8.06954 21.7996 8.15174C21.6138 8.2027 21.4182 8.39832 21.3278 8.57751C20.4483 10.32 19.5721 12.0626 18.7353 13.8265C18.3786 14.5777 17.8526 15.0117 17.0257 15.0956C16.6328 15.135 16.2432 15.2123 15.8503 15.2632C15.4705 15.3126 15.1516 15.0775 15.1023 14.724C15.0563 14.4018 15.2897 14.1092 15.6662 14.0468C16.0706 13.9777 16.475 13.9202 16.8827 13.8725C17.2246 13.8331 17.4383 13.6736 17.5945 13.3514C18.4739 11.5349 19.3699 9.72495 20.2691 7.91666C20.708 7.03553 21.576 6.67387 22.4177 7.00265C22.8763 7.18184 23.1656 7.52541 23.3793 7.9594C24.249 9.73646 25.1416 11.502 25.9948 13.2856C26.1888 13.69 26.4617 13.8298 26.8726 13.8873C28.8338 14.1553 30.7917 14.4413 32.7496 14.7273C33.4565 14.8309 33.971 15.2008 34.1929 15.8978C34.4165 16.5981 34.1896 17.18 33.6784 17.6798C33.2723 18.076 32.8827 18.4886 32.452 18.9275C33.8329 19.2464 35.1727 19.5473 36.5042 19.8727C36.7295 19.927 36.9662 20.0536 37.1306 20.2147C38.8632 21.926 40.5795 23.6537 42.3056 25.3699C42.5242 25.5886 42.7198 25.845 42.562 26.1229C42.4453 26.3283 42.1856 26.5223 41.9538 26.5799C40.4973 26.9349 39.0342 27.2654 37.5646 27.5695C37.2292 27.6385 37.1651 27.7783 37.1667 28.0857C37.1766 31.4228 37.1733 34.7599 37.1717 38.0987C37.1717 39.182 36.8149 39.6357 35.7596 39.8774C31.2405 40.9081 26.7197 41.9388 22.1974 42.958C21.9541 43.0123 21.6763 43.0156 21.4346 42.9613C19.0131 42.4188 16.595 41.8615 14.1768 41.3059C13.705 41.1974 13.465 40.9179 13.539 40.5662C13.6212 40.1716 13.9565 40.0007 14.4365 40.1108C16.59 40.6023 18.7435 41.0922 20.8971 41.5821C20.9743 41.6002 21.0532 41.6067 21.193 41.6281C21.193 41.444 21.193 41.2845 21.193 41.1251C21.193 39.4023 21.193 37.6778 21.193 35.955C21.193 35.8597 21.193 35.7643 21.193 35.6673C21.1995 35.1281 21.4264 34.8207 21.8144 34.824C22.2007 34.8273 22.4259 35.1331 22.4259 35.6772C22.4292 37.4822 22.4259 39.2872 22.4259 41.0938C22.4259 41.2549 22.4259 41.4177 22.4259 41.6528L22.4242 41.6495ZM2.8832 25.5705C2.94403 25.6017 2.95553 25.61 2.96868 25.6132C7.23625 26.5749 11.5038 27.53 15.7681 28.5049C16.1067 28.5821 16.3122 28.5213 16.5522 28.278C17.7671 27.0369 19 25.8154 20.2329 24.5924C20.3365 24.4905 20.491 24.4395 20.6209 24.3639C20.4335 24.211 20.3579 24.1732 20.2806 24.1551C16.0426 23.1901 11.803 22.2285 7.56667 21.2536C7.3168 21.1961 7.17378 21.2438 6.99953 21.4197C5.71071 22.7216 4.41203 24.0137 3.11828 25.3091C3.04266 25.3847 2.97526 25.4669 2.88156 25.5705H2.8832ZM33.6241 20.5467C33.6208 20.5204 33.6176 20.4925 33.6159 20.4662C32.9271 20.3084 32.24 20.1489 31.5512 19.996C31.4772 19.9796 31.3588 19.9599 31.3227 19.9993C30.892 20.4514 30.4728 20.915 30.0634 21.3621C31.2257 21.0958 32.4257 20.8229 33.6274 20.5484L33.6241 20.5467ZM13.6113 21.3736C13.1789 20.9133 12.7828 20.4843 12.3751 20.0667C12.3192 20.0092 12.2008 19.9648 12.1269 19.9812C11.415 20.1391 10.7065 20.31 9.998 20.4777C9.998 20.5007 9.99965 20.5237 10.0013 20.5451C11.2013 20.8213 12.4014 21.0958 13.6113 21.3736Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M22.4208 2.89725C22.4208 3.29343 22.4323 3.69125 22.4175 4.08579C22.4011 4.49183 22.1364 4.76801 21.7977 4.76308C21.4591 4.75814 21.201 4.48361 21.1944 4.06935C21.1813 3.27699 21.1813 2.48299 21.1944 1.69063C21.201 1.26157 21.441 1.00676 21.7945 1.00019C22.1643 0.991969 22.406 1.25335 22.4208 1.70707C22.4339 2.10325 22.4241 2.49943 22.4241 2.89725H22.4208Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M16.2384 7.44815C16.5267 7.71983 16.8242 7.98426 17.1012 8.26558C17.3855 8.55599 17.405 8.93801 17.1692 9.18111C16.9333 9.4242 16.5565 9.42379 16.2505 9.1445C15.6648 8.61071 15.0869 8.06622 14.5192 7.51329C14.2114 7.21428 14.1905 6.86486 14.4281 6.60311C14.6758 6.32826 15.0317 6.33161 15.3721 6.63198C15.6695 6.89409 15.9511 7.17295 16.2406 7.44576L16.2384 7.44815Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M27.3729 7.44815C27.0846 7.71983 26.7871 7.98426 26.5101 8.26558C26.2259 8.55599 26.2064 8.93801 26.4422 9.18111C26.678 9.4242 27.0548 9.42379 27.3608 9.1445C27.9465 8.61071 28.5244 8.06622 29.0921 7.51329C29.3999 7.21428 29.4208 6.86486 29.1832 6.60311C28.9355 6.32826 28.5796 6.33161 28.2392 6.63198C27.9418 6.89409 27.6602 7.17295 27.3707 7.44576L27.3729 7.44815Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M9.23839 10.4481C9.52674 10.7198 9.82418 10.9843 10.1012 11.2656C10.3855 11.556 10.405 11.938 10.1692 12.1811C9.93334 12.4242 9.55654 12.4238 9.25052 12.1445C8.66479 11.6107 8.08688 11.0662 7.51919 10.5133C7.21142 10.2143 7.19055 9.86486 7.42813 9.60311C7.67579 9.32826 8.03175 9.33161 8.37213 9.63198C8.6695 9.89409 8.95109 10.1729 9.24064 10.4458L9.23839 10.4481Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M34.3729 10.4481C34.0846 10.7198 33.7871 10.9843 33.5101 11.2656C33.2259 11.556 33.2064 11.938 33.4422 12.1811C33.678 12.4242 34.0548 12.4238 34.3608 12.1445C34.9465 11.6107 35.5244 11.0662 36.0921 10.5133C36.3999 10.2143 36.4208 9.86486 36.1832 9.60311C35.9355 9.32826 35.5796 9.33161 35.2392 9.63198C34.9418 9.89409 34.6602 10.1729 34.3707 10.4458L34.3729 10.4481Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_2905">
          <rect width="44" height="44" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterConnectSupportIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_2932)">
        <path
          d="M34.9239 25.6359C35.9839 26.0887 36.9601 26.5006 37.9332 26.9223C39.9087 27.7771 41.1738 29.2342 41.6348 31.3475C41.7267 31.7692 41.7645 32.2123 41.7661 32.6454C41.7776 35.5119 41.7727 38.3785 41.7727 41.2466V41.7224C42.0024 41.7224 42.1911 41.7142 42.3781 41.7241C42.7522 41.7454 43.0082 42.0047 43.0016 42.3443C42.9951 42.6692 42.7506 42.917 42.3978 42.9448C42.2895 42.953 42.1796 42.9481 42.0697 42.9481C28.6855 42.9481 15.3014 42.9481 1.91886 42.9481C1.80892 42.9481 1.69899 42.9547 1.59069 42.9448C1.2412 42.912 1 42.6561 1 42.3312C1 42.0063 1.24612 41.752 1.59562 41.7241C1.71868 41.7142 1.84174 41.7208 1.9648 41.7208C6.39337 41.7208 10.8236 41.7208 15.2521 41.7208H15.7624C15.6377 40.541 15.518 39.4056 15.3982 38.2685C15.221 36.6014 15.0421 34.9344 14.8649 33.2673C14.8255 32.9014 14.7894 32.5355 14.7599 32.168C14.6713 31.0342 15.4244 30.1776 16.5632 30.1383C17.1735 30.1169 17.7856 30.135 18.4189 30.135V26.4694C18.2844 26.5153 18.1826 26.5416 18.0875 26.5826C16.8503 27.1192 15.6049 27.6393 14.3776 28.2005C12.448 29.0816 11.4619 30.6157 11.4307 32.7193C11.3962 35.0115 11.4225 37.3054 11.4225 39.5992C11.4225 39.7354 11.4668 39.9093 11.3995 40.0012C11.2666 40.185 11.0877 40.4262 10.8991 40.4557C10.712 40.4836 10.4692 40.3048 10.2903 40.1637C10.2083 40.098 10.1951 39.9126 10.1951 39.7813C10.1919 37.3234 10.1229 34.8638 10.2181 32.4108C10.3116 29.9939 11.5275 28.207 13.7262 27.1503C14.8042 26.6318 15.9183 26.1855 17.0816 25.6769C16.9258 25.603 16.8273 25.5505 16.724 25.5079C13.6687 24.2609 12.6334 20.4919 14.6483 17.8781C15.4704 16.8115 16.1333 15.6827 16.2891 14.3273C16.3597 13.7202 16.34 13.1017 16.3449 12.488C16.3548 11.0703 16.3039 9.6543 16.6583 8.25797C17.6362 4.41353 20.9901 1.49288 24.9412 1.07283C29.9834 0.537921 34.4924 3.80479 35.4998 8.76498C35.659 9.54601 35.659 10.3631 35.6934 11.1655C35.7394 12.2829 35.6918 13.4052 35.7853 14.516C35.8854 15.6974 36.4121 16.7311 37.157 17.6533C38.788 19.6731 38.765 22.2476 37.0947 24.2067C36.5335 24.8647 35.8198 25.3093 34.9223 25.6359H34.9239ZM35.5901 41.7027C35.6573 41.0809 35.7213 40.5 35.7837 39.9192C35.9642 38.2259 36.1463 36.5325 36.3252 34.8376C36.422 33.9154 36.527 32.9949 36.6025 32.0712C36.6402 31.6084 36.4154 31.3919 35.9576 31.3672C35.8214 31.359 35.6836 31.3656 35.5474 31.3656C29.4386 31.3656 23.3282 31.3771 17.2195 31.3558C15.8707 31.3508 15.9282 31.6347 16.0414 32.716C16.2202 34.4241 16.404 36.1305 16.5861 37.8386C16.724 39.1234 16.8634 40.4065 17.0045 41.7044H35.5901V41.7027ZM20.1795 11.9104C20.1795 13.5759 20.15 15.1986 20.1861 16.8214C20.2337 18.9922 21.7301 20.8939 23.873 21.291C25.0495 21.5092 26.2916 21.4551 27.5008 21.4058C28.7741 21.355 29.8144 20.7249 30.6446 19.7699C31.173 19.1612 31.5356 18.4639 31.6997 17.5745C31.3371 17.842 31.0598 18.0602 30.7677 18.2587C30.474 18.4573 30.159 18.623 29.8702 18.8281C29.7159 18.938 29.5551 19.0759 29.4698 19.24C29.1695 19.8093 28.7364 20.2195 28.0883 20.2687C27.4106 20.3196 26.7198 20.3344 26.0471 20.2556C25.1545 20.1506 24.5063 19.2908 24.5638 18.431C24.6278 17.4859 25.3497 16.764 26.2834 16.7246C26.6246 16.7098 26.9676 16.7344 27.3089 16.7131C28.1818 16.6573 28.9431 16.8411 29.4272 17.6812C30.4051 17.2086 31.15 16.5375 31.6193 15.5842C31.7128 15.3939 31.8014 15.1773 31.8063 14.9706C31.826 14.0418 31.8063 13.1131 31.8211 12.1861C31.8244 11.9301 31.7292 11.8432 31.4864 11.7824C30.5511 11.5494 29.6831 11.1606 28.9103 10.5781C28.5395 10.2991 28.4525 9.92996 28.6789 9.63133C28.8955 9.34747 29.2647 9.31465 29.6241 9.57882C30.4674 10.1974 31.4175 10.5633 32.4397 10.7225C33.1059 10.8275 33.7835 10.9243 34.4743 10.7126C34.4202 8.19069 33.4997 6.08717 31.639 4.36759C28.9037 8.52378 25.0987 11.0326 20.1812 11.9121L20.1795 11.9104ZM30.6118 3.60461C27.829 1.71111 23.9747 1.79807 21.1574 3.77689C18.5732 5.59328 17.4361 8.69442 17.6477 10.7208C22.3733 11.1474 28.0079 8.09224 30.6118 3.60461ZM40.5421 41.6995V41.3598C40.5421 38.4392 40.5699 35.5201 40.5322 32.5995C40.506 30.5091 39.4936 29.0045 37.5902 28.1233C36.7747 27.746 35.9396 27.4096 35.1142 27.0568C34.6072 26.8402 34.0986 26.6269 33.5637 26.4005V30.1317C33.7359 30.1317 33.882 30.1317 34.0297 30.1317C34.6712 30.1317 35.3144 30.1219 35.956 30.135C37.1144 30.158 37.8774 30.9455 37.8314 32.0958C37.8101 32.6536 37.7281 33.2082 37.669 33.7628C37.4606 35.7269 37.2489 37.6909 37.0422 39.6567C36.9716 40.3294 36.906 41.0038 36.8354 41.7011H40.5404L40.5421 41.6995ZM23.3003 22.4904C22.9344 23.9294 22.0484 24.9041 20.6767 25.4636C19.6577 25.8803 19.6791 25.8984 19.6709 27.0059C19.6676 27.3111 19.7923 27.3981 20.0482 27.4621C21.694 27.8789 23.366 28.125 25.0609 28.1791C27.4221 28.253 29.7422 27.9609 32.018 27.3193C32.2559 27.2521 32.3084 27.1421 32.3134 26.9206C32.3347 25.8705 32.3396 25.8705 31.3748 25.457C31.237 25.3979 31.0991 25.3389 30.9646 25.2749C29.8784 24.7695 29.1515 23.9475 28.7823 22.8104C28.7462 22.6988 28.7036 22.5889 28.6675 22.4937C26.8773 22.8022 25.1167 22.8005 23.302 22.4921L23.3003 22.4904ZM17.5805 12.0007C17.5805 12.4568 17.5985 12.89 17.5755 13.3215C17.5476 13.8646 17.5312 14.4127 17.4328 14.9443C17.1801 16.3291 16.5106 17.5171 15.6344 18.6066C14.9371 19.473 14.7189 20.4771 14.9043 21.5666C15.3227 24.0098 18.1695 25.4931 20.6028 24.1378C21.3035 23.7473 21.7826 23.1943 22.0287 22.4281C22.1272 22.1229 22.0812 21.9588 21.7908 21.7685C20.0154 20.6133 19.0145 18.9856 18.9555 16.8509C18.9144 15.3791 18.9423 13.9057 18.9391 12.4322C18.9391 12.2878 18.9391 12.1451 18.9391 12.0007H17.5788H17.5805ZM33.0484 12.0433C33.0484 12.2534 33.0484 12.4027 33.0484 12.5503C33.0484 13.8597 33.055 15.1691 33.0468 16.4785C33.032 18.7116 32.1263 20.487 30.2459 21.716C29.8915 21.9473 29.8078 22.1573 29.9768 22.5003C30.1212 22.7923 30.241 23.1139 30.4543 23.3486C31.2764 24.2527 32.2969 24.7006 33.544 24.6202C35.0273 24.525 36.1184 23.8277 36.7485 22.5003C37.3917 21.1466 37.2916 19.7962 36.3383 18.6197C34.9961 16.9625 34.3644 15.1018 34.4661 12.9819C34.4809 12.6603 34.4678 12.3387 34.4678 12.0417H33.0468L33.0484 12.0433ZM32.3068 28.5122C28.0735 29.6805 23.896 29.7034 19.707 28.6468V30.1022H32.2773C32.2887 29.9807 32.3035 29.8889 32.3052 29.7953C32.3084 29.3884 32.3052 28.9799 32.3052 28.5122H32.3068ZM27.0988 19.0841C27.0988 19.0841 27.0988 19.0841 27.0988 19.0824C27.3433 19.0824 27.5911 19.1021 27.8323 19.0775C28.1736 19.0431 28.4 18.8363 28.382 18.4885C28.3639 18.1488 28.1473 17.9503 27.788 17.9503C27.3253 17.9503 26.8626 17.9503 26.3999 17.9503C26.0372 17.9503 25.8256 18.1554 25.8108 18.4934C25.796 18.8412 26.0225 19.0463 26.3638 19.0808C26.6066 19.1054 26.8527 19.0857 27.0972 19.0857L27.0988 19.0841Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M13.1796 13.1942C13.452 13.3583 13.7096 13.5059 13.9606 13.6667C14.3479 13.9145 14.4808 14.2377 14.3758 14.6397C14.2757 15.022 13.9688 15.2534 13.5176 15.255C11.6192 15.2583 9.71747 15.2961 7.82069 15.2452C4.45373 15.155 1.49534 12.4131 1.06873 9.05439C0.502643 4.57988 4.08783 0.750217 8.60008 1.01275C11.9326 1.20636 14.8089 3.91699 15.2011 7.22324C15.4603 9.41045 14.8385 11.3122 13.3798 12.9513C13.3174 13.0202 13.26 13.0957 13.1796 13.1942ZM12.0294 14.0294C11.5601 13.268 11.5962 12.9825 12.2296 12.3688C13.3371 11.2941 13.9426 9.97817 14.0312 8.44893C14.2478 4.657 10.7807 1.63462 7.03966 2.32868C4.44225 2.80944 2.43717 5.04916 2.24027 7.69087C2.04337 10.3326 3.68255 12.8463 6.20776 13.6848C6.81979 13.8882 7.48596 13.99 8.1308 14.0146C9.42048 14.0654 10.7151 14.0294 12.031 14.0294H12.0294Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M26.2608 38.8619C24.9613 38.8504 23.9538 37.8102 23.9669 36.4909C23.9801 35.2308 25.0515 34.1823 26.3117 34.1954C27.5898 34.2086 28.6564 35.2931 28.64 36.5648C28.6236 37.8577 27.5718 38.8734 26.2608 38.8619ZM26.3067 37.6313C26.9089 37.628 27.3995 37.144 27.4077 36.55C27.4143 35.9544 26.8843 35.4244 26.2887 35.4293C25.7013 35.4359 25.2025 35.938 25.1992 36.527C25.1943 37.1538 25.6783 37.6346 26.3067 37.6297V37.6313Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M4.66993 8.72781C4.31387 8.72781 4.05627 8.46035 4.06775 8.1043C4.07924 7.76957 4.33849 7.5218 4.67485 7.52345C5.02927 7.52345 5.28524 7.79254 5.27375 8.15024C5.26227 8.49153 5.0145 8.72945 4.66993 8.72781Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M7.66993 8.72781C7.31387 8.72781 7.05627 8.46035 7.06775 8.1043C7.07924 7.76957 7.33849 7.5218 7.67485 7.52345C8.02927 7.52345 8.28524 7.79254 8.27375 8.15024C8.26227 8.49153 8.0145 8.72945 7.66993 8.72781Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M10.6699 8.72781C10.3139 8.72781 10.0563 8.46035 10.0678 8.1043C10.0792 7.76957 10.3385 7.5218 10.6749 7.52345C11.0293 7.52345 11.2852 7.79254 11.2738 8.15024C11.2623 8.49153 11.0145 8.72945 10.6699 8.72781Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_2932">
          <rect width="44" height="44" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterOnboardingHelpIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_2988)">
        <path
          d="M32.9877 29.0856C33.9543 28.6549 34.8469 28.2587 35.7378 27.8609C37.6577 27.0013 37.49 27.2001 38.3629 25.2523C38.6193 24.6802 38.8724 24.1049 39.1453 23.5411C39.6352 22.5253 40.7562 22.0913 41.7655 22.5138C42.7649 22.9313 43.2679 24.0589 42.8602 25.0994C42.1288 26.9651 41.3693 28.8209 40.6017 30.6718C40.2746 31.4608 39.6993 32.0591 39.0237 32.572C36.5646 34.4393 34.1154 36.3198 31.653 38.1855C31.2322 38.5044 31.0465 38.9005 31.0497 39.415C31.0547 40.3306 31.0612 41.2478 31.0432 42.1634C31.0333 42.6713 30.5698 42.982 30.1802 42.7321C30.0125 42.6253 29.8432 42.3705 29.8383 42.1782C29.8103 41.1393 29.812 40.1005 29.8449 39.0616C29.8695 38.2726 30.2854 37.6775 30.9034 37.2058C33.4365 35.276 35.9745 33.3544 38.4845 31.3951C38.8889 31.0794 39.2538 30.6274 39.4576 30.1606C40.2434 28.3623 40.9584 26.5328 41.6932 24.7115C41.8543 24.312 41.7441 23.9504 41.4252 23.7334C41.1195 23.528 40.7973 23.5312 40.5113 23.7532C40.3798 23.8551 40.2812 24.0227 40.2088 24.1789C39.8225 25.0074 39.4346 25.8342 39.073 26.6741C38.6851 27.5733 38.0489 28.1913 37.1481 28.5793C35.6835 29.2105 34.237 29.8844 32.7741 30.5239C32.5226 30.634 32.3894 30.7803 32.3171 31.0416C32.2086 31.4345 32.0639 31.8191 31.9242 32.2038C31.773 32.6164 31.4492 32.784 31.0925 32.6509C30.7719 32.5309 30.6372 32.1956 30.7687 31.7994C31.4344 29.8088 32.105 27.8199 32.7724 25.8309C32.8069 25.7273 32.853 25.6271 32.876 25.5202C32.9467 25.2013 32.8299 24.9515 32.5554 24.7904C32.276 24.626 32.0015 24.6687 31.7664 24.8907C31.6892 24.9646 31.6201 25.0567 31.5741 25.1537C30.7982 26.7382 29.6361 28.027 28.3951 29.2417C26.6033 30.994 25.1831 33.0339 23.7596 35.0722C22.9427 36.2409 22.5975 37.5559 22.6106 38.9745C22.6188 39.9459 22.6139 40.9158 22.6106 41.8872C22.6106 42.05 22.6451 42.2604 22.5613 42.3656C22.415 42.5497 22.1882 42.7831 21.9942 42.7831C21.8019 42.7831 21.575 42.548 21.4304 42.3639C21.3466 42.2571 21.3811 42.0483 21.3811 41.8856C21.3729 40.7908 21.3761 39.6977 21.3646 38.603C21.3498 37.1581 20.7926 35.9204 20.0102 34.7253C18.2398 32.0197 16.0372 29.6872 13.8378 27.3464C13.5337 27.0226 13.3118 26.6199 13.0702 26.2418C12.8203 25.8506 12.605 25.438 12.3518 25.0485C12.1266 24.7016 11.7913 24.6096 11.4691 24.7723C11.147 24.935 11.0253 25.2457 11.1568 25.6402C11.6352 27.0752 12.1217 28.5086 12.6033 29.942C12.8121 30.5617 13.0225 31.1814 13.2247 31.8027C13.3545 32.2005 13.2198 32.5358 12.8992 32.6542C12.5425 32.7857 12.2253 32.6147 12.0675 32.2038C12.0527 32.166 12.0329 32.1282 12.028 32.0887C11.8751 30.9841 11.2045 30.4088 10.1771 30.0406C9.0117 29.6231 7.90544 29.0445 6.76795 28.548C5.92798 28.1798 5.32307 27.5864 4.95158 26.7481C4.57023 25.8884 4.17901 25.032 3.78615 24.1773C3.55273 23.6693 3.13686 23.4737 2.70455 23.6529C2.27881 23.8288 2.11772 24.2874 2.32319 24.7937C3.0333 26.5509 3.72368 28.3163 4.48146 30.0521C4.69187 30.5354 5.08308 30.9529 5.44307 31.3523C5.74717 31.6893 5.85072 31.995 5.62553 32.286C5.39211 32.5884 5.01733 32.6032 4.65077 32.3123C4.03765 31.8241 3.6004 31.2027 3.30617 30.4828C2.59441 28.742 1.8843 26.9996 1.18241 25.2539C0.728733 24.1247 1.15447 22.9888 2.19005 22.5318C3.24699 22.065 4.38612 22.5285 4.90391 23.648C5.30499 24.5142 5.66334 25.4019 6.09893 26.25C6.28468 26.6117 6.55262 27.0259 6.89452 27.1985C8.21447 27.8659 9.58044 28.4461 11.0138 29.0971C10.6522 28.0171 10.3267 27.0259 9.98481 26.0413C9.77276 25.4315 9.82701 24.8611 10.1788 24.3203C10.8626 23.2732 12.3518 23.1647 13.1803 24.1C13.2707 24.2019 13.3628 24.3088 13.4219 24.4304C14.2356 26.1268 15.526 27.4516 16.8328 28.7502C18.5998 30.5091 19.9641 32.572 21.3926 34.5889C21.6178 34.9078 21.7822 35.2711 21.9695 35.6048C22.4462 34.8864 22.9361 34.173 23.3996 33.4448C24.9645 30.9792 27.0389 28.9475 28.972 26.7925C29.5917 26.1005 30.0388 25.2506 30.5467 24.4616C30.9741 23.7975 31.5462 23.4392 32.3516 23.4704C33.4759 23.5148 34.3767 24.6342 34.0841 25.7208C33.8179 26.7054 33.4595 27.6653 33.1406 28.6368C33.0995 28.7617 33.0584 28.885 32.9927 29.0856H32.9877Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M30.162 8.5367C30.4726 9.33886 30.7586 10.1016 31.0693 10.8544C31.1104 10.953 31.2797 11.0533 31.3931 11.0582C31.8978 11.0813 32.4041 11.0599 32.9103 11.0697C33.7569 11.0862 34.3486 11.6631 34.3749 12.5146C34.393 13.1014 34.393 13.6916 34.3749 14.2784C34.3503 15.0592 33.8424 15.6312 33.0665 15.7085C32.5241 15.7627 31.975 15.7397 31.4277 15.7364C31.2288 15.7364 31.0973 15.7709 31.0332 15.9879C30.9197 16.366 30.7932 16.7408 30.6419 17.1057C30.4907 17.469 30.3017 17.8174 30.1011 18.2317C30.4776 18.5949 30.8786 18.9664 31.2616 19.3544C31.9882 20.0858 31.9882 20.8962 31.2715 21.6244C30.9066 21.9943 30.5433 22.3657 30.1685 22.7257C29.5061 23.3635 28.6727 23.3635 28.0185 22.7192C27.6108 22.3181 27.218 21.8989 26.8777 21.5471C26.0558 21.8677 25.3046 22.1504 24.5666 22.4611C24.4581 22.5071 24.3463 22.6846 24.3397 22.8079C24.3134 23.2715 24.3348 23.7366 24.3266 24.2018C24.3085 25.2029 23.7315 25.7684 22.7338 25.7716C22.2012 25.7733 21.667 25.7799 21.1344 25.7716C20.3092 25.7568 19.7125 25.1914 19.6731 24.3662C19.6484 23.8484 19.6796 23.3274 19.6517 22.8079C19.6451 22.683 19.535 22.5005 19.4265 22.4562C18.6868 22.1471 17.9356 21.8677 17.1137 21.5521C16.7833 21.8923 16.4003 22.3 16.0025 22.6929C15.3138 23.3734 14.4804 23.3717 13.7933 22.6978C13.4218 22.3345 13.0552 21.9663 12.6903 21.5965C12.0131 20.9044 12.0147 20.0842 12.6985 19.3856C13.0799 18.9944 13.4727 18.6147 13.8327 18.258C13.5204 17.4558 13.2278 16.6832 12.9122 15.9205C12.8761 15.8334 12.7117 15.7545 12.6032 15.7512C12.1397 15.7331 11.6728 15.7512 11.2093 15.7413C10.1885 15.72 9.61646 15.1348 9.60989 14.1189C9.6066 13.6275 9.6066 13.1343 9.60989 12.6428C9.61811 11.6533 10.2 11.0796 11.1994 11.073C11.6646 11.0697 12.1298 11.0862 12.5933 11.0632C12.7084 11.0582 12.8777 10.9596 12.9188 10.861C13.2311 10.1081 13.5155 9.34543 13.8377 8.51533C13.4925 8.18 13.0832 7.797 12.6903 7.39921C12.0197 6.72197 12.0147 5.90173 12.6722 5.21957C13.0519 4.8267 13.4399 4.43877 13.8327 4.06071C14.4837 3.43443 15.2957 3.43114 15.9532 4.05413C16.3099 4.39275 16.66 4.73958 16.9954 5.09957C17.1449 5.25902 17.2616 5.24915 17.4507 5.16532C18.0835 4.88259 18.723 4.61137 19.3722 4.37138C19.5925 4.28919 19.6714 4.19385 19.6648 3.96701C19.65 3.48867 19.6517 3.01034 19.6648 2.532C19.6895 1.6082 20.2632 1.03781 21.1919 1.01644C21.7245 1.00494 22.2587 1.00494 22.7913 1.01644C23.7365 1.03781 24.3052 1.61313 24.3249 2.56652C24.3348 3.03171 24.3397 3.49689 24.3216 3.96044C24.3134 4.19385 24.4005 4.28426 24.6142 4.3648C25.2619 4.61137 25.9029 4.8793 26.5342 5.16203C26.7215 5.24587 26.8383 5.2623 26.9895 5.10121C27.3149 4.75109 27.6601 4.41905 28.0037 4.08536C28.676 3.43279 29.4814 3.41799 30.1537 4.05742C30.5598 4.44371 30.9559 4.8415 31.3422 5.24751C31.9602 5.89844 31.9537 6.72526 31.3307 7.36962C30.9411 7.77234 30.5433 8.16685 30.1505 8.56628C30.1406 8.57615 30.1373 8.59094 30.162 8.54163V8.5367ZM31.6972 14.5085C32.0391 14.5069 32.3811 14.4987 32.723 14.5085C33.0418 14.5184 33.1635 14.3787 33.152 14.0631C33.1355 13.6258 33.1339 13.1869 33.152 12.7513C33.1668 12.4209 33.0583 12.2829 32.7082 12.2944C32.0802 12.3157 31.4507 12.3042 30.8211 12.2993C30.2918 12.2944 30.0995 12.1546 29.9877 11.6451C29.7757 10.6736 29.3993 9.77446 28.8601 8.94271C28.6365 8.59587 28.6563 8.28684 28.9637 7.98932C29.4354 7.534 29.8842 7.05401 30.3609 6.60198C30.5976 6.37678 30.5844 6.20747 30.3543 5.99214C30.014 5.67489 29.6853 5.34449 29.368 5.00752C29.1741 4.80205 29.0212 4.8004 28.8239 5.00752C28.3785 5.47106 27.9199 5.92146 27.4629 6.37349C27.0733 6.75814 26.8498 6.77951 26.3796 6.48527C25.5742 5.98063 24.7161 5.61243 23.7841 5.41517C23.2433 5.30011 23.1036 5.12423 23.0987 4.56699C23.0921 3.93742 23.0872 3.30786 23.102 2.67994C23.1085 2.36762 23.0017 2.22955 22.6762 2.24105C22.2258 2.25749 21.7738 2.25749 21.3218 2.24105C20.9979 2.22955 20.8894 2.36598 20.896 2.67994C20.9108 3.32265 20.9059 3.96537 20.8977 4.60808C20.8911 5.11601 20.7464 5.30175 20.2533 5.40696C19.2933 5.60914 18.409 5.98556 17.5822 6.50828C17.1597 6.77457 16.9148 6.74992 16.563 6.40473C16.0946 5.94447 15.6277 5.48421 15.1724 5.01081C14.9752 4.80534 14.8223 4.80534 14.6283 5.01081C14.3193 5.33956 14.0037 5.66338 13.67 5.96584C13.4086 6.20254 13.3938 6.385 13.6618 6.63485C14.1319 7.0721 14.5708 7.54386 15.031 7.99096C15.3368 8.28684 15.3598 8.59587 15.1346 8.94435C14.5954 9.77774 14.2174 10.6752 14.007 11.6467C13.8968 12.1546 13.7029 12.2976 13.1736 12.3009C12.544 12.3059 11.9145 12.3174 11.2865 12.296C10.9381 12.2845 10.8279 12.4209 10.8427 12.753C10.8624 13.1902 10.8624 13.6291 10.8427 14.0647C10.8263 14.4181 10.9923 14.5167 11.3128 14.5118C11.9424 14.5003 12.5703 14.5052 13.1999 14.5102C13.7358 14.5134 13.8903 14.6203 14.002 15.1315C14.2157 16.1013 14.5954 16.9988 15.1247 17.8372C15.3795 18.2415 15.3532 18.4766 15.0146 18.8218C14.5659 19.282 14.1138 19.739 13.6486 20.1828C13.4267 20.3949 13.384 20.5658 13.6306 20.7927C13.9725 21.1083 14.2996 21.4403 14.6168 21.7789C14.8256 22.0025 14.9916 21.9844 15.1971 21.7723C15.6524 21.2989 16.1192 20.8387 16.5877 20.3784C16.9345 20.0365 17.1499 20.0119 17.5674 20.2765C18.3942 20.7992 19.2802 21.1707 20.2336 21.3877C20.7793 21.511 20.896 21.6721 20.8993 22.2458C20.9042 22.8753 20.9075 23.5032 20.8993 24.1328C20.8944 24.4237 20.993 24.5602 21.3037 24.5487C21.7672 24.5306 22.2341 24.5306 22.6976 24.5487C23.0083 24.5602 23.1053 24.4221 23.102 24.1328C23.0921 23.5032 23.0954 22.8753 23.1003 22.2458C23.1053 21.6737 23.222 21.5126 23.7677 21.3877C24.7227 21.1707 25.6087 20.7992 26.4355 20.2765C26.8531 20.0119 27.0684 20.0365 27.4152 20.3784C27.8837 20.8387 28.3505 21.2989 28.8059 21.7723C29.0113 21.986 29.1773 22.0025 29.3861 21.7789C29.6951 21.4502 30.0124 21.128 30.3444 20.8222C30.591 20.597 30.6074 20.4212 30.3543 20.1828C29.8776 19.7324 29.4239 19.2574 28.9587 18.7938C28.7056 18.5423 28.6168 18.253 28.8141 17.944C29.396 17.0383 29.8036 16.0635 30.0305 15.0132C30.1061 14.663 30.3313 14.5118 30.6765 14.5102C31.0184 14.5102 31.3603 14.5102 31.7022 14.5102L31.6972 14.5085Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M14.1733 40.6563C14.1733 41.1215 14.1798 41.5867 14.1716 42.0519C14.1634 42.5236 13.9201 42.8179 13.5552 42.8212C13.1837 42.8245 12.947 42.5401 12.9437 42.0601C12.9371 41.1988 12.9339 40.3374 12.9437 39.4761C12.9519 38.9139 12.7465 38.4948 12.2862 38.1496C10.5816 36.8707 8.89511 35.5672 7.20203 34.2703C6.78615 33.953 6.70397 33.616 6.95218 33.3021C7.19545 32.9947 7.5538 32.9914 7.9631 33.3021C9.68084 34.6105 11.3936 35.9255 13.113 37.2307C13.8166 37.7649 14.1634 38.4685 14.1733 39.3446C14.1782 39.7818 14.1733 40.2191 14.1733 40.658V40.6563Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M6.29797 8.14378C5.56156 8.07968 5.27554 7.88736 5.2854 7.4797C5.29526 7.07698 5.53526 6.92575 6.29139 6.86329C6.36207 6.42769 6.2766 5.82607 6.97191 5.84415C7.61463 5.86059 7.54723 6.40796 7.58175 6.83534C8.42501 6.9915 8.63048 7.12958 8.61897 7.51258C8.60582 7.91037 8.39213 8.04516 7.60805 8.15693C7.45518 8.97553 7.32039 9.17278 6.92424 9.15306C6.53138 9.13498 6.37358 8.88513 6.29961 8.14543L6.29797 8.14378Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M17.7795 13.2064C17.597 15.6852 19.9197 17.8993 22.3788 17.5985C24.182 17.3783 25.3803 16.4101 26.0148 14.7203C26.1825 14.2748 26.4964 14.0924 26.8564 14.2321C27.1917 14.3619 27.315 14.7006 27.1737 15.123C26.3731 17.5131 24.1836 18.9661 21.7772 18.8429C19.0962 18.7064 17.0201 16.8194 16.596 14.2567C16.1357 11.4755 17.9406 8.7649 20.7087 8.07781C23.4226 7.4055 26.2762 8.96709 27.1605 11.6086C27.3084 12.0508 27.1786 12.3796 26.8038 12.5111C26.457 12.6327 26.1759 12.4617 26.0033 12.0212C25.9786 11.9571 25.9556 11.893 25.9293 11.8305C25.3655 10.4974 24.3957 9.61145 22.9837 9.28269C20.3635 8.67121 17.98 10.4826 17.7795 13.2064Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M21.4252 13.6103C21.9874 13.0399 22.492 12.5204 23.0033 12.0076C23.3337 11.6756 23.7134 11.6377 23.9764 11.8942C24.241 12.1522 24.2081 12.5352 23.881 12.8656C23.2465 13.5067 22.6087 14.1445 21.966 14.779C21.5962 15.1456 21.2773 15.1456 20.9058 14.7856C20.6214 14.5094 20.3387 14.2316 20.0642 13.9456C19.7798 13.6481 19.7601 13.2815 19.9984 13.0317C20.2647 12.7539 20.605 12.767 20.9337 13.0826C21.0899 13.2322 21.2313 13.4015 21.4252 13.6119V13.6103Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_2988">
          <rect width="44" height="44" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterSalesIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="44"
      viewBox="0 0 36 44"
      fill="none"
    >
      <g clipPath="url(#clip0_1798_3017)">
        <path
          d="M9.06953 11.9765C9.02187 10.4909 9.00873 9.01515 8.92163 7.54432C8.76222 4.84095 9.86493 3.05459 12.376 2.04062C14.9364 1.00529 17.5856 0.788359 20.2988 1.1877C20.851 1.26823 21.1123 1.62977 20.9397 2.05212C20.8049 2.3808 20.5387 2.46626 20.2018 2.41695C18.7063 2.19674 17.2109 2.1573 15.7121 2.3923C14.4779 2.58622 13.2799 2.91326 12.169 3.47858C10.851 4.15073 10.1558 5.28137 10.1427 6.74892C10.1295 8.1951 10.2298 9.64128 10.2823 11.1877C10.828 10.6848 11.109 10.1474 11.1813 9.49008C11.2487 8.88039 11.3325 8.27234 11.3998 7.661C11.4376 7.31753 11.6283 7.12361 11.9455 7.02501C14.1772 6.33478 16.3481 6.34464 18.4237 7.56568C19.6776 8.30356 20.9742 8.5172 22.3103 7.78589C22.5946 7.62977 22.8493 7.41613 23.1057 7.21071C23.3604 7.00693 23.6201 6.91161 23.9356 7.06116C24.2643 7.21564 24.3727 7.48844 24.3645 7.83027C24.3333 9.09896 24.6932 10.251 25.4015 11.3011C25.4393 11.357 25.4508 11.4293 25.4754 11.4934C25.5132 11.4671 25.551 11.4424 25.5888 11.4161C25.6792 10.6503 25.7795 9.88614 25.8551 9.11868C25.9356 8.30356 26.0769 7.48351 26.0424 6.67332C25.9767 5.09074 25.1238 4.00282 23.6759 3.38326C23.3259 3.23372 22.9561 3.12854 22.6045 2.98557C22.2626 2.84588 22.1049 2.53199 22.215 2.23454C22.3448 1.88778 22.6143 1.73495 22.9627 1.83027C24.6932 2.30027 26.1246 3.15648 26.8855 4.88367C27.3078 5.84177 27.3522 6.83437 27.2339 7.85327C27.0761 9.20907 26.9282 10.5649 26.7704 11.965C27.7959 12.1425 28.6357 12.6158 29.155 13.5525C29.4524 14.0916 29.6036 14.6717 29.528 15.2847C29.3341 16.8557 28.3826 17.7711 26.68 18.0883C26.4007 18.9609 26.1772 19.8336 25.8452 20.6635C25.2141 22.2395 24.2018 23.5476 22.8855 24.6257C22.7441 24.7423 22.6357 24.9757 22.6242 25.1614C22.5913 25.7612 22.6192 26.3644 22.6094 26.9658C22.6045 27.311 22.7458 27.518 23.0728 27.6495C23.5478 27.8401 24.0194 27.9519 24.5387 27.942C25.5215 27.9239 26.5108 27.8993 27.4886 27.9847C28.8559 28.1031 29.768 28.8837 30.2314 30.1704C30.3152 30.4054 30.4352 30.5418 30.6636 30.652C31.8041 31.2008 32.6192 32.0669 33.1155 33.2354C34.3497 36.1441 34.9003 39.1811 34.8148 42.3348C34.8033 42.7637 34.5814 42.9823 34.1542 43.002C34.072 43.0053 33.9898 43.002 33.9077 43.002C23.2437 43.002 12.5798 43.002 1.91587 43.002C1.16156 43.002 1.01694 42.8459 1.00215 42.0932C0.944632 39.0776 1.49188 36.1803 2.63568 33.3898C3.20593 31.9995 4.17224 31.0316 5.55926 30.4646C5.70388 30.4054 5.8715 30.2822 5.92902 30.1458C6.55515 28.6651 7.61513 27.9453 9.23551 27.9354C9.9471 27.9305 10.6587 27.9091 11.3686 27.9437C11.8945 27.9683 12.3579 27.8073 12.8263 27.615C13.0909 27.5049 13.2059 27.3175 13.2076 27.0464C13.2092 26.3759 13.2191 25.707 13.1994 25.0365C13.1961 24.9116 13.1057 24.7522 13.0038 24.6717C10.9742 23.0497 9.77619 20.933 9.28974 18.4005C9.23222 18.1047 9.0646 18.0472 8.82795 17.9962C7.42943 17.7004 6.46969 16.5813 6.40232 15.1844C6.33494 13.7744 7.18457 12.5484 8.52393 12.1261C8.69155 12.0735 8.86246 12.034 9.06624 11.9782L9.06953 11.9765ZM5.77126 31.7284C4.79837 32.1803 4.18867 32.8722 3.80577 33.7875C3.04324 35.6117 2.55515 37.5065 2.3448 39.4704C2.26427 40.223 2.25605 40.9839 2.21497 41.7399H5.7729C5.7729 40.7522 5.76632 39.7974 5.77619 38.8409C5.78112 38.3315 6.20018 38.0308 6.62253 38.2148C6.91012 38.3414 7.00708 38.5747 7.00544 38.8755C7.00051 39.8286 7.00379 40.7801 7.00379 41.7481H29.155C29.1616 41.6084 29.1747 41.4884 29.1747 41.3685C29.1747 38.0324 29.178 34.698 29.1747 31.3619C29.1731 29.9913 28.3596 29.1663 26.9841 29.1647C21.063 29.1581 15.1435 29.1581 9.22236 29.1647C7.83042 29.1647 7.00708 30.0045 7.00544 31.3898C7.00544 32.9888 7.00051 34.5895 7.00872 36.1885C7.00872 36.5451 6.88876 36.8081 6.53707 36.91C6.12294 37.0299 5.77947 36.7062 5.77783 36.1951C5.77454 34.7325 5.77783 33.2699 5.77783 31.7267L5.77126 31.7284ZM23.1139 8.7785C23.0893 8.78507 23.0482 8.79 23.0137 8.80972C21.2914 9.78918 19.5708 9.66757 17.9208 8.68482C16.4483 7.80726 14.9035 7.64292 13.2684 7.95188C12.5535 8.08663 12.5404 8.09649 12.5026 8.8278C12.4335 10.1491 12.0145 11.3077 11.0153 12.2197C10.8345 12.3841 10.6751 12.5764 10.5272 12.7719C10.45 12.8738 10.3875 13.0069 10.3727 13.1335C10.2068 14.5484 10.1657 15.9634 10.3596 17.3816C10.6948 19.8401 11.6727 21.9535 13.6135 23.564C15.3012 24.9626 17.2043 25.5558 19.3719 25.0365C21.3785 24.5566 22.8526 23.2995 23.9553 21.6199C25.5823 19.14 25.7844 16.3611 25.4886 13.5049C25.4689 13.3126 25.3407 13.1006 25.201 12.9593C24.5009 12.2477 23.9438 11.4441 23.6053 10.5073C23.4031 9.94694 23.2766 9.36026 23.1155 8.78014L23.1139 8.7785ZM30.4253 41.7497H33.6349C33.5609 40.8015 33.5231 39.8779 33.4097 38.9626C33.1747 37.0595 32.6653 35.2271 31.8699 33.4786C31.5708 32.8212 31.0909 32.3167 30.4237 31.9157V41.7497H30.4253ZM14.3678 27.9042H21.4557C21.4179 27.1483 21.3818 26.4186 21.3423 25.6347C20.2446 26.1589 19.109 26.4449 17.9011 26.4449C16.6965 26.4449 15.5691 26.1409 14.4779 25.6363C14.4401 26.4153 14.404 27.1466 14.3678 27.9026V27.9042ZM26.7819 13.2715V16.7884C27.722 16.6142 28.3662 15.8204 28.3152 14.9543C28.261 14.0571 27.5905 13.334 26.7819 13.2715ZM9.04652 16.744V13.2912C8.23962 13.4063 7.6365 14.131 7.63321 15.0135C7.62828 15.8845 8.21497 16.6207 9.04652 16.744Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
        <path
          d="M20.7199 35.4806C20.715 36.9481 19.5548 38.1018 18.0872 38.0985C16.6098 38.0968 15.4414 36.9153 15.4512 35.4346C15.4611 33.995 16.6608 32.8216 18.1119 32.8282C19.5646 32.8347 20.7249 34.0147 20.7183 35.4789L20.7199 35.4806ZM19.4874 35.4559C19.4825 34.6687 18.8613 34.0525 18.079 34.059C17.3115 34.0656 16.6756 34.7131 16.6821 35.4806C16.6871 36.2612 17.3132 36.8725 18.102 36.8676C18.8941 36.8627 19.4923 36.2546 19.4874 35.4576V35.4559Z"
          fill="#7136ED"
          stroke="#7136ED"
        />
      </g>
      <defs>
        <clipPath id="clip0_1798_3017">
          <rect width="36" height="44" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const HelpCenterIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
    >
      <g clipPath="url(#clip0_7862_558)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.5 25C5.59625 25 0 19.4037 0 12.5C0 5.59625 5.59625 0 12.5 0C19.4037 0 25 5.59625 25 12.5C25 19.4037 19.4037 25 12.5 25ZM8.75 9.7275H10.7575C10.8263 8.66875 11.5362 7.99375 12.6375 7.99375C13.715 7.99375 14.4325 8.65125 14.4325 9.55625C14.4325 10.4025 14.0738 10.8637 13.0138 11.5063C11.835 12.1975 11.3388 12.9663 11.4163 14.2313L11.425 14.8375H13.4075V14.3425C13.4075 13.4875 13.7237 13.0425 14.8425 12.3938C16.005 11.7013 16.655 10.7875 16.655 9.48C16.6538 7.6 15.0887 6.25 12.7475 6.25C10.2125 6.25 8.81875 7.72 8.75 9.7275ZM12.4412 18.8538C13.1337 18.8538 13.68 18.3163 13.68 17.6413C13.68 16.9663 13.1338 16.435 12.4425 16.435C11.7488 16.435 11.1938 16.965 11.1938 17.64C11.1938 18.315 11.7487 18.8538 12.4412 18.8538Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_7862_558">
          <rect width="25" height="25" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const MessageSendIcon = ({
  width,
  height,
  sx,
  ...props
}: SvgIconProps) => {
  const size = width ?? height ?? 24;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="1 2.9 21 18.6"
        fill="none"
      >
        <path
          d="M3.52285 20.2737C3.15905 20.4198 2.81407 20.3902 2.48791 20.185C2.16174 19.9798 1.99911 19.6814 2 19.2898V13.9914L9.85753 12.1823L2 10.3732V5.07478C2 4.6823 2.16309 4.38392 2.48925 4.17962C2.81542 3.97532 3.1604 3.9453 3.5242 4.08956L20.3414 11.1716C20.7805 11.3732 21 11.7119 21 12.1877C21 12.6626 20.7805 12.9977 20.3414 13.1931L3.52285 20.2737Z"
          fill="white"
        />
      </svg>
    </SvgIcon>
  );
};

export const EndSessionIcon = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 15;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    </SvgIcon>
  );
};

export const ClockIcon = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 12;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </SvgIcon>
  );
};

export const ScaleDownIcon = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 19;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 14H10V20" />
        <path d="M20 10H14V4" />
        <path d="M14 10L21 3" />
        <path d="M3 21L10 14" />
      </svg>
    </SvgIcon>
  );
};

export const FilterIconForInput = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 24;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 6H7.5" />
        <path d="M9 7.5C8.60217 7.5 8.22064 7.34197 7.93934 7.06066C7.65803 6.77936 7.5 6.39782 7.5 6C7.5 5.60218 7.65803 5.22064 7.93934 4.93934C8.22064 4.65804 8.60217 4.5 9 4.5C9.19698 4.5 9.39204 4.5388 9.57402 4.61418C9.75601 4.68956 9.92137 4.80005 10.0607 4.93934C10.1999 5.07863 10.3104 5.24399 10.3858 5.42597C10.4612 5.60796 10.5 5.80302 10.5 6C10.5 6.19698 10.4612 6.39204 10.3858 6.57403C10.3104 6.75601 10.1999 6.92137 10.0607 7.06066C9.92137 7.19995 9.75601 7.31044 9.57402 7.38582C9.39204 7.4612 9.19698 7.5 9 7.5Z" />
        <path d="M10.5 6.00003L18 6" />
        <path d="M18 12H16.5" />
        <path d="M15 13.5C15.3978 13.5 15.7794 13.342 16.0607 13.0607C16.342 12.7794 16.5 12.3978 16.5 12C16.5 11.6022 16.342 11.2206 16.0607 10.9393C15.7794 10.658 15.3978 10.5 15 10.5C14.6022 10.5 14.2206 10.658 13.9393 10.9393C13.658 11.2206 13.5 11.6022 13.5 12C13.5 12.3978 13.658 12.7794 13.9393 13.0607C14.2206 13.342 14.6022 13.5 15 13.5Z" />
        <path d="M13.5 12L6 12" />
        <path d="M6 18H7.5" />
        <path d="M9 19.5C8.60217 19.5 8.22064 19.342 7.93934 19.0607C7.65803 18.7794 7.5 18.3978 7.5 18C7.5 17.6022 7.65803 17.2206 7.93934 16.9393C8.22064 16.658 8.60217 16.5 9 16.5C9.19698 16.5 9.39204 16.5388 9.57402 16.6142C9.75601 16.6896 9.92137 16.8001 10.0607 16.9393C10.1999 17.0786 10.3104 17.244 10.3858 17.426C10.4612 17.608 10.5 17.803 10.5 18C10.5 18.197 10.4612 18.392 10.3858 18.574C10.3104 18.756 10.1999 18.9214 10.0607 19.0607C9.92137 19.1999 9.75601 19.3104 9.57402 19.3858C9.39204 19.4612 9.19698 19.5 9 19.5Z" />
        <path d="M10.5 18L18 18" />
      </svg>
    </SvgIcon>
  );
};

export const ChatScaleUpIcon = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 19;

  return (
    <SvgIcon
      {...props}
      sx={{ fontSize: size, width: size, height: height ?? size, ...sx }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 3H21V9" />
        <path d="M9 21H3V15" />
        <path d="M21 3L14 10" />
        <path d="M3 21L10 14" />
      </svg>
    </SvgIcon>
  );
};

export const ClosedChatIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="45"
      height="45"
      viewBox="0 0 45 45"
      fill="none"
    >
      <path
        d="M42.3985 14.4821C41.3594 11.9264 39.8171 9.60576 37.8631 7.65813C35.9131 5.7059 33.5973 4.15719 31.0483 3.10053C28.4994 2.04388 25.7671 1.5 23.0078 1.5C20.2484 1.5 17.5162 2.04388 14.9672 3.10053C12.4182 4.15719 10.1025 5.7059 8.15242 7.65813C6.19092 9.60384 4.64133 11.9246 3.59608 14.4821C2.5436 17.0253 2.00139 19.7506 2.00031 22.503C1.98528 25.2188 2.51357 27.9102 3.55409 30.4188L2.50424 38.0827C2.29226 39.0684 2.45141 40.0976 2.95122 40.9733C3.45102 41.8489 4.25632 42.5093 5.21285 42.828C5.83646 43.0338 6.49996 43.0842 7.14457 42.975L14.8085 41.8412C17.3829 42.9427 20.1551 43.5071 22.9553 43.4999C26.4066 43.4994 29.8047 42.648 32.8486 41.0213C35.8925 39.3945 38.4885 37.0425 40.4068 34.1733C42.3251 31.3041 43.5066 28.0064 43.8467 24.5718C44.1867 21.1372 43.6749 17.6718 42.3565 14.4821H42.3985ZM14.0106 25.8835C13.5666 25.8833 13.1271 25.7958 12.717 25.6258C12.3069 25.4557 11.9343 25.2066 11.6205 24.8926C11.3067 24.5786 11.0578 24.2059 10.888 23.7957C10.7183 23.3855 10.631 22.9459 10.6311 22.5019C10.6312 22.058 10.7188 21.6184 10.8888 21.2083C11.0589 20.7983 11.308 20.4257 11.622 20.1119C11.936 19.798 12.3087 19.5491 12.7189 19.3794C13.1291 19.2096 13.5687 19.1223 14.0127 19.1225C14.9092 19.1225 15.7691 19.4786 16.4031 20.1126C17.037 20.7466 17.3932 21.6064 17.3932 22.503C17.3932 23.3995 17.037 24.2594 16.4031 24.8934C15.7691 25.5273 14.9071 25.8835 14.0106 25.8835ZM22.9994 25.8835C22.5554 25.8833 22.1159 25.7958 21.7058 25.6258C21.2957 25.4557 20.9231 25.2066 20.6093 24.8926C20.2955 24.5786 20.0466 24.2059 19.8768 23.7957C19.7071 23.3855 19.6198 22.9459 19.6199 22.5019C19.62 22.058 19.7076 21.6184 19.8776 21.2083C20.0476 20.7983 20.2968 20.4257 20.6108 20.1119C20.9248 19.798 21.2975 19.5491 21.7077 19.3794C22.1179 19.2096 22.5575 19.1223 23.0015 19.1225C23.898 19.1225 24.7579 19.4786 25.3918 20.1126C26.0258 20.7466 26.382 21.6064 26.382 22.503C26.382 23.3995 26.0258 24.2594 25.3918 24.8934C24.7579 25.5273 23.898 25.8835 23.0015 25.8835M31.9882 25.8835C31.5442 25.8833 31.1047 25.7958 30.6946 25.6258C30.2845 25.4557 29.9119 25.2066 29.5981 24.8926C29.2843 24.5786 29.0354 24.2059 28.8656 23.7957C28.6959 23.3855 28.6086 22.9459 28.6087 22.5019C28.6088 22.058 28.6964 21.6184 28.8664 21.2083C29.0364 20.7983 29.2856 20.4257 29.5996 20.1119C29.9136 19.798 30.2863 19.5491 30.6965 19.3794C31.1067 19.2096 31.5463 19.1223 31.9903 19.1225C32.8868 19.1225 33.7467 19.4786 34.3806 20.1126C35.0146 20.7466 35.3708 21.6064 35.3708 22.503C35.3708 23.3995 35.0146 24.2594 34.3806 24.8934C33.7467 25.5273 32.8847 25.8835 31.9882 25.8835Z"
        fill="white"
      />
    </svg>
  </SvgIcon>
);

export const OpenedChatIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="45"
      height="44"
      viewBox="0 0 45 44"
      fill="none"
    >
      <path
        d="M42.3985 13.9821C41.3594 11.4264 39.8171 9.10576 37.8631 7.15813C35.9131 5.2059 33.5973 3.65719 31.0483 2.60053C28.4994 1.54388 25.7671 1 23.0078 1C20.2484 1 17.5162 1.54388 14.9672 2.60053C12.4182 3.65719 10.1025 5.2059 8.15242 7.15813C6.19092 9.10384 4.64133 11.4247 3.59608 13.9821C2.5436 16.5253 2.00139 19.2506 2.00031 22.003C1.98528 24.7188 2.51357 27.4102 3.55409 29.9188L2.50424 37.5827C2.29226 38.5684 2.45141 39.5976 2.95122 40.4733C3.45102 41.3489 4.25632 42.0093 5.21285 42.328C5.83646 42.5338 6.49996 42.5842 7.14457 42.475L14.8085 41.3412C17.3829 42.4427 20.1551 43.0071 22.9553 42.9999C26.4066 42.9994 29.8047 42.148 32.8486 40.5213C35.8925 38.8945 38.4885 36.5424 40.4068 33.6733C42.3251 30.8041 43.5066 27.5064 43.8467 24.0718C44.1867 20.6372 43.6749 17.1718 42.3565 13.9821H42.3985Z"
        fill="white"
      />
      <path
        d="M17.7574 18.2426L26.2426 26.7279M17.7574 26.7279L26.2426 18.2426"
        stroke="var(--primary-600)"
        strokeWidth="5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const TVIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="39"
      height="39"
      viewBox="0 0 39 39"
      fill="none"
    >
      <path
        d="M19.866 11.4L30.3298 5.08887M19.1181 11.4L9.40219 5.08887M5.66602 14.7985C5.66602 12.921 7.07813 11.4 8.82157 11.4H30.9105C32.6539 11.4 34.066 12.921 34.066 14.7985V30.0903C34.066 31.9679 32.6539 33.4889 30.9105 33.4889H8.82157C7.07813 33.4889 5.66602 31.9679 5.66602 30.0903V14.7985Z"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="4.02333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const DesktopIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="39"
      height="39"
      viewBox="0 0 39 39"
      fill="none"
    >
      <path
        d="M8.82157 6.5H30.1105C31.8539 6.5 33.266 7.91208 33.266 9.65552V22.5C33.266 24.2434 31.8539 25.6555 30.1105 25.6555H8.82157C7.07813 25.6555 5.66602 24.2434 5.66602 22.5V9.65552C5.66602 7.91208 7.07813 6.5 8.82157 6.5Z"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="4.02333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.4661 25.6555V32.4889M13 32.4889H25.9321"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="4.02333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const TabletIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="39"
      height="39"
      viewBox="0 0 39 39"
      fill="none"
    >
      <path
        d="M17.322 9.8222H22.0553M28.7041 5.08887H10.6732C8.68047 5.08887 7.06641 6.36056 7.06641 7.92887V30.6489C7.06641 32.2172 8.68047 33.4889 10.6732 33.4889H28.7041C30.6968 33.4889 32.3109 32.2172 32.3109 30.6489V7.92887C32.3109 6.36056 30.6968 5.08887 28.7041 5.08887Z"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="4.02333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const MobileIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M12.3636 1.45459H5.0909C4.28757 1.45459 3.63635 2.10581 3.63635 2.90914V14.5455C3.63635 15.3488 4.28757 16 5.0909 16H12.3636C13.1669 16 13.8182 15.3488 13.8182 14.5455V2.90914C13.8182 2.10581 13.1669 1.45459 12.3636 1.45459Z"
        stroke={stroke}
        strokeWidth="1.45091"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.72766 13.0908H8.73555"
        stroke={stroke}
        strokeWidth="1.45091"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const KioskIcon = ({ fill = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="35"
      viewBox="0 0 30 35"
      fill="none"
    >
      <path
        d="M26.769 21.4853C28.0708 21.4853 29.1359 20.4202 29.1359 19.1184V3.73376C29.1359 2.43198 28.0708 1.36688 26.769 1.36688H3.10027C1.79849 1.36688 0.733398 2.43198 0.733398 3.73376V19.1184C0.733398 20.4202 1.79849 21.4853 3.10027 21.4853H11.3843V32.1363H0.733398V34.5031H29.1359V32.1363H18.485V21.4853H26.769ZM16.1181 32.1363H13.7512V21.4853H16.1181V32.1363ZM3.10027 19.1184V3.73376H26.769V19.1184H3.10027ZM14.9346 14.3847C14.2838 14.3847 13.7512 14.9172 13.7512 15.5681C13.7512 16.219 14.2838 16.7516 14.9346 16.7516C15.5855 16.7516 16.1181 16.219 16.1181 15.5681C16.1181 14.9172 15.5855 14.3847 14.9346 14.3847Z"
        fill={fill}
        fillOpacity="0.8"
      />
      <path
        d="M26.7686 1.11688C28.2084 1.11688 29.3857 2.29422 29.3857 3.73407V19.1188C29.3855 20.5585 28.2083 21.735 26.7686 21.735H18.7354V31.8864H29.3857V34.7536H0.483398V31.8864H11.1348V21.735H3.10059C1.66086 21.735 0.483612 20.5585 0.483398 19.1188V3.73407L0.49707 3.46747C0.631485 2.15158 1.7506 1.11688 3.10059 1.11688H26.7686ZM14.001 31.8864H15.8682V21.735H14.001V31.8864ZM3.35059 18.8688H26.5186V3.98407H3.35059V18.8688ZM14.9346 14.1345C15.7235 14.1345 16.3681 14.7791 16.3682 15.5681C16.3682 16.357 15.7235 17.0016 14.9346 17.0016C14.1456 17.0016 13.501 16.357 13.501 15.5681C13.501 14.7792 14.1457 14.1345 14.9346 14.1345Z"
        stroke={fill}
        strokeOpacity="0.8"
        strokeWidth="0.5"
      />
    </svg>
  </SvgIcon>
);

export const EyedropperIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M16.527 11.4597L8.53299 19.4237C8.2345 19.7199 7.84486 19.907 7.42698 19.9545C7.0091 20.002 6.5874 19.9073 6.22999 19.6857L4.99499 20.9147C4.89128 21.0205 4.7675 21.1045 4.63091 21.1619C4.49431 21.2192 4.34765 21.2488 4.19949 21.2488C4.05134 21.2488 3.90467 21.2192 3.76808 21.1619C3.63148 21.1045 3.50771 21.0205 3.40399 20.9147L3.08499 20.5677C2.97883 20.4637 2.89449 20.3397 2.83691 20.2027C2.77932 20.0658 2.74966 19.9187 2.74966 19.7702C2.74966 19.6216 2.77932 19.4745 2.83691 19.3376C2.89449 19.2006 2.97883 19.0766 3.08499 18.9727L4.30199 17.7527C4.07879 17.3934 3.98324 16.9694 4.03078 16.5492C4.07832 16.1289 4.26618 15.737 4.56399 15.4367L12.511 7.47266L16.527 11.4597Z"
        stroke="#2E2E2E"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.792 11.7968C18.528 12.0585 18.1712 12.2053 17.7995 12.2053C17.4278 12.2053 17.0711 12.0585 16.807 11.7968L12.127 7.1538C11.9656 6.98911 11.8469 6.78755 11.781 6.5666C11.7151 6.34565 11.7041 6.11196 11.7489 5.8858C11.7938 5.65963 11.8931 5.44781 12.0382 5.26868C12.1834 5.08955 12.37 4.94851 12.582 4.8578C12.8386 4.75011 13.1215 4.72189 13.3943 4.7768C13.6671 4.83171 13.9171 4.96723 14.112 5.1658L14.682 5.7278L16.527 3.5048C17.0591 3.0082 17.7634 2.73788 18.4912 2.75094C19.2189 2.764 19.913 3.05942 20.427 3.5748C20.9407 4.09119 21.2347 4.78632 21.2474 5.51462C21.2601 6.24291 20.9904 6.94785 20.495 7.4818L18.23 9.2368L18.792 9.8088C18.9236 9.93875 19.0281 10.0935 19.0994 10.2642C19.1707 10.4348 19.2074 10.6179 19.2074 10.8028C19.2074 10.9877 19.1707 11.1708 19.0994 11.3415C19.0281 11.5121 18.9236 11.6669 18.792 11.7968Z"
        stroke="#2E2E2E"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const SettingsIcon = ({
  width,
  height,
  color,
  sx,
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => {
  const size = width ?? height ?? 25;

  return (
    <SvgIcon
      {...props}
      sx={{
        fontSize: size,
        width: size,
        height: height ?? size,
        '& path': { fill: color ?? 'currentColor' },
        ...sx
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
      >
        <g clipPath="url(#clip0_6530_8765)">
          <path d="M13.2846 25H11.7156C10.4466 25 9.41417 23.9676 9.41417 22.6986V22.1678C8.87466 21.9954 8.35049 21.7778 7.84693 21.5172L7.47076 21.8934C6.55967 22.8056 5.10025 22.7789 4.21563 21.8931L3.1067 20.7842C2.22051 19.899 2.19503 18.44 3.10699 17.5291L3.48287 17.1532C3.22227 16.6496 3.00474 16.1255 2.83233 15.5859H2.30157C1.03262 15.5859 0.000152588 14.5535 0.000152588 13.2845V11.7155C0.000152588 10.4465 1.03262 9.41406 2.30162 9.41406H2.83238C3.00479 8.87451 3.22232 8.35039 3.48292 7.84683L3.10674 7.4707C2.19532 6.56025 2.22047 5.10117 3.10704 4.21558L4.21607 3.10659C5.10274 2.2187 6.56182 2.19653 7.47115 3.10688L7.84698 3.48271C8.35054 3.22217 8.87471 3.00459 9.41422 2.83218V2.30142C9.41422 1.03242 10.4466 0 11.7157 0H13.2847C14.5537 0 15.5861 1.03242 15.5861 2.30142V2.83223C16.1256 3.00459 16.6498 3.22217 17.1533 3.48276L17.5295 3.10659C18.4406 2.19438 19.9 2.22114 20.7846 3.10693L21.8936 4.21582C22.7797 5.10103 22.8052 6.55996 21.8933 7.47095L21.5174 7.84683C21.778 8.35039 21.9955 8.87446 22.1679 9.41406H22.6987C23.9677 9.41406 25.0002 10.4465 25.0002 11.7155V13.2845C25.0002 14.5535 23.9677 15.5859 22.6987 15.5859H22.1679C21.9955 16.1255 21.778 16.6496 21.5174 17.1532L21.8936 17.5293C22.805 18.4398 22.7798 19.8989 21.8933 20.7845L20.7842 21.8935C19.8976 22.7813 18.4385 22.8035 17.5292 21.8932L17.1533 21.5173C16.6498 21.7779 16.1256 21.9955 15.5861 22.1679V22.6987C15.5861 23.9676 14.5537 25 13.2846 25ZM8.0918 19.979C8.79136 20.3927 9.54429 20.7053 10.3296 20.908C10.6531 20.9914 10.8791 21.2831 10.8791 21.6171V22.6986C10.8791 23.1599 11.2544 23.5352 11.7157 23.5352H13.2847C13.746 23.5352 14.1213 23.1599 14.1213 22.6986V21.6171C14.1213 21.2831 14.3473 20.9914 14.6707 20.908C15.4561 20.7053 16.209 20.3927 16.9086 19.979C17.1964 19.8088 17.5628 19.8551 17.7993 20.0916L18.5653 20.8576C18.8956 21.1883 19.426 21.1806 19.7481 20.858L20.8579 19.7483C21.1793 19.4273 21.19 18.8968 20.8582 18.5654L20.0918 17.7991C19.8554 17.5626 19.8091 17.1961 19.9793 16.9083C20.393 16.2088 20.7055 15.4559 20.9082 14.6705C20.9917 14.3471 21.2834 14.1211 21.6173 14.1211H22.6987C23.16 14.1211 23.5354 13.7458 23.5354 13.2846V11.7155C23.5354 11.2542 23.16 10.879 22.6987 10.879H21.6173C21.2833 10.879 20.9917 10.653 20.9082 10.3296C20.7055 9.54419 20.3929 8.79126 19.9793 8.09175C19.8091 7.80395 19.8554 7.43745 20.0918 7.20103L20.8579 6.43496C21.189 6.1042 21.1804 5.57393 20.8582 5.2521L19.7485 4.14248C19.4269 3.82041 18.8964 3.81099 18.5657 4.14219L17.7994 4.90855C17.5629 5.14502 17.1963 5.19131 16.9086 5.02109C16.209 4.60737 15.4561 4.29482 14.6708 4.09214C14.3473 4.00869 14.1213 3.71699 14.1213 3.38296V2.30142C14.1213 1.84014 13.746 1.46484 13.2847 1.46484H11.7157C11.2544 1.46484 10.8791 1.84014 10.8791 2.30142V3.38286C10.8791 3.71689 10.6531 4.00859 10.3297 4.09204C9.54434 4.29473 8.79141 4.60728 8.09185 5.021C7.80396 5.19116 7.43751 5.14487 7.20108 4.90845L6.43506 4.14238C6.10479 3.81172 5.57437 3.81938 5.25225 4.14204L4.14254 5.25171C3.82115 5.57271 3.81041 6.10313 4.14224 6.43457L4.9086 7.20093C5.14503 7.43735 5.19131 7.80386 5.02115 8.09165C4.60743 8.79116 4.29493 9.54409 4.09224 10.3295C4.00875 10.6529 3.71705 10.8789 3.38306 10.8789H2.30162C1.84034 10.8789 1.465 11.2542 1.465 11.7155V13.2845C1.465 13.7458 1.84034 14.1211 2.30162 14.1211H3.38301C3.71705 14.1211 4.0087 14.3471 4.09219 14.6705C4.29488 15.4559 4.60743 16.2088 5.0211 16.9083C5.19127 17.1961 5.14498 17.5626 4.90855 17.799L4.14249 18.5651C3.81133 18.8959 3.81998 19.4261 4.14219 19.7479L5.25181 20.8576C5.57344 21.1796 6.10391 21.1891 6.43467 20.8579L7.20098 20.0915C7.3752 19.9173 7.73765 19.7695 8.0918 19.979Z" />
          <path d="M12.5002 17.9395C9.50079 17.9395 7.0607 15.4993 7.0607 12.5C7.0607 9.50068 9.50079 7.06055 12.5002 7.06055C15.4995 7.06055 17.9396 9.50068 17.9396 12.5C17.9396 15.4993 15.4995 17.9395 12.5002 17.9395ZM12.5002 8.52539C10.3085 8.52539 8.52554 10.3084 8.52554 12.5C8.52554 14.6916 10.3086 16.4746 12.5002 16.4746C14.6918 16.4746 16.4748 14.6916 16.4748 12.5C16.4748 10.3084 14.6918 8.52539 12.5002 8.52539Z" />
        </g>
        <defs>
          <clipPath id="clip0_6530_8765">
            <rect width="25" height="25" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </SvgIcon>
  );
};

export const LogoutIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
    >
      <path
        d="M15.5 3H7.5C6.96957 3 6.46086 3.21071 6.08579 3.58579C5.71071 3.96086 5.5 4.46957 5.5 5V19C5.5 19.5304 5.71071 20.0391 6.08579 20.4142C6.46086 20.7893 6.96957 21 7.5 21H15.5M19.5 12L15.5 8M19.5 12L15.5 16M19.5 12H9.5"
        stroke="#727475"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const WorldIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="square"
      />
      <path
        d="M12 22C14.6667 19.5758 16 16.2424 16 12C16 7.75758 14.6667 4.42424 12 2C9.33333 4.42424 8 7.75758 8 12C8 16.2424 9.33333 19.5758 12 22Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
      <path
        d="M2.5 9H21.5M2.5 15H21.5"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  </SvgIcon>
);
export const DownloadIcon = ({
  stroke = '#727475',
  ...props
}: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 15 17"
      fill="none"
    >
      <path
        d="M7.5 1.25V10.75M7.5 10.75L4.03688 7.28687M7.5 10.75L10.9631 7.28687"
        stroke={stroke}
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.859375 9.35938V12.8125C0.859375 13.164 0.999031 13.5011 1.24775 13.7498C1.49647 13.9985 1.83356 14.1382 2.1875 14.1382H12.8125C13.1664 14.1382 13.5035 13.9985 13.7523 13.7498C14.001 13.5011 14.1406 13.164 14.1406 12.8125V9.35938"
        stroke={stroke}
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const UploadImageIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="27"
      height="26"
      viewBox="0 0 27 26"
      fill="none"
    >
      <path
        d="M24.3518 4.0635L21.7302 1.44189C21.6296 1.34127 21.5102 1.26146 21.3788 1.207C21.2474 1.15254 21.1065 1.12451 20.9642 1.12451C20.822 1.12451 20.6811 1.15254 20.5497 1.207C20.4183 1.26146 20.2988 1.34127 20.1983 1.44189L15.2365 6.40364C15.0334 6.60677 14.9192 6.88227 14.9191 7.16956V9.79119C14.9191 10.0785 15.0333 10.354 15.2364 10.5572C15.4396 10.7604 15.7151 10.8745 16.0024 10.8745H18.6246C18.9119 10.8744 19.1874 10.7603 19.3905 10.5571L24.3518 5.59534C24.4524 5.49477 24.5322 5.37537 24.5866 5.24395C24.6411 5.11253 24.6691 4.97167 24.6691 4.82942C24.6691 4.68717 24.6411 4.54631 24.5866 4.41489C24.5322 4.28347 24.4524 4.16407 24.3518 4.0635ZM18.176 8.70785H17.0858V7.61817L20.9642 3.73975L22.0539 4.82942L18.176 8.70785ZM21.3869 11.9166C21.0996 11.9166 20.824 12.0307 20.6208 12.2339C20.4177 12.4371 20.3035 12.7126 20.3035 12.9999V15.5908L18.6997 13.987C18.135 13.4234 17.3698 13.1068 16.5719 13.1068C15.7741 13.1068 15.0088 13.4234 14.4441 13.987L13.688 14.7431L10.9947 12.0498C10.4307 11.4866 9.66614 11.1703 8.86904 11.1703C8.07194 11.1703 7.30742 11.4866 6.74337 12.0498L5.13688 13.6564V7.58325C5.13725 7.29605 5.25151 7.02072 5.45459 6.81763C5.65768 6.61455 5.93301 6.50029 6.22021 6.49992H12.7202C13.0075 6.49992 13.2831 6.38578 13.4862 6.18262C13.6894 5.97945 13.8035 5.70391 13.8035 5.41659C13.8035 5.12927 13.6894 4.85372 13.4862 4.65055C13.2831 4.44739 13.0075 4.33325 12.7202 4.33325H6.22021C5.35855 4.33419 4.53245 4.67691 3.92316 5.2862C3.31387 5.89548 2.97116 6.72159 2.97021 7.58325V20.5833C2.97116 21.4449 3.31387 22.271 3.92316 22.8803C4.53245 23.4896 5.35855 23.8323 6.22021 23.8333H19.2202C20.0819 23.8323 20.908 23.4896 21.5173 22.8803C22.1266 22.271 22.4693 21.4449 22.4702 20.5833V12.9999C22.4702 12.7126 22.3561 12.4371 22.1529 12.2339C21.9497 12.0307 21.6742 11.9166 21.3869 11.9166ZM6.22021 21.6666C5.93301 21.6662 5.65768 21.552 5.45459 21.3489C5.25151 21.1458 5.13725 20.8705 5.13688 20.5833V16.7202L8.2753 13.5818C8.43595 13.4304 8.64834 13.3461 8.86907 13.3461C9.08979 13.3461 9.30219 13.4304 9.46284 13.5818L12.8948 17.0128C12.904 17.0227 12.9074 17.0364 12.917 17.046L17.5379 21.6666H6.22021ZM20.3035 20.5833C20.2982 20.7921 20.2308 20.9946 20.1099 21.1651L15.2199 16.275L15.976 15.5189C16.1343 15.3613 16.3486 15.2729 16.5719 15.2729C16.7953 15.2729 17.0095 15.3613 17.1678 15.5189L20.3035 18.6546V20.5833Z"
        fill="#2E2E2E"
        fillOpacity="0.8"
      />
    </svg>
  </SvgIcon>
);

export const EyOffIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="27"
      height="26"
      viewBox="0 0 27 26"
      fill="none"
    >
      <path
        d="M12.2704 11.4692C11.8641 11.8756 11.6359 12.4268 11.636 13.0015C11.6361 13.5761 11.8645 14.1272 12.2709 14.5334C12.6773 14.9397 13.2285 15.1679 13.8032 15.1678C14.3778 15.1677 14.9289 14.9393 15.3351 14.5329"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.8744 18.0624C17.3551 19.0141 15.596 19.5128 13.8033 19.5C9.90328 19.5 6.65328 17.3333 4.05328 13C5.43128 10.7033 6.99128 9.0155 8.73328 7.9365M11.8316 6.695C12.4805 6.56322 13.1411 6.49788 13.8033 6.5C17.7033 6.5 20.9533 8.66667 23.5533 13C22.8311 14.2025 22.059 15.2389 21.2371 16.1092M4.05328 3.25L23.5533 22.75"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const EyIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
    >
      <path
        d="M11.0757 13.1581C11.0757 13.7323 11.3038 14.283 11.7098 14.689C12.1158 15.095 12.6665 15.3231 13.2407 15.3231C13.8148 15.3231 14.3655 15.095 14.7715 14.689C15.1775 14.283 15.4056 13.7323 15.4056 13.1581C15.4056 12.5839 15.1775 12.0333 14.7715 11.6273C14.3655 11.2213 13.8148 10.9932 13.2407 10.9932C12.6665 10.9932 12.1158 11.2213 11.7098 11.6273C11.3038 12.0333 11.0757 12.5839 11.0757 13.1581Z"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.9828 13.158C20.3848 17.4879 17.1374 19.6529 13.2404 19.6529C9.34347 19.6529 6.09601 17.4879 3.49805 13.158C6.09601 8.82806 9.34347 6.66309 13.2404 6.66309C17.1374 6.66309 20.3848 8.82806 22.9828 13.158Z"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const LeftArrowIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="31"
      height="31"
      viewBox="0 0 31 31"
      fill="none"
    >
      <path
        d="M5.54958 15.4998L16.5184 4.22742C16.6087 4.13658 16.6801 4.02873 16.7285 3.91012C16.7768 3.7915 16.8012 3.66447 16.8001 3.53639C16.799 3.4083 16.7726 3.28169 16.7223 3.1639C16.672 3.0461 16.5988 2.93945 16.507 2.85012C16.4152 2.76079 16.3066 2.69056 16.1874 2.64348C16.0683 2.59641 15.941 2.57342 15.8129 2.57586C15.6849 2.5783 15.5586 2.60611 15.4413 2.65769C15.3241 2.70926 15.2182 2.78358 15.1299 2.87634L3.50487 14.8243C3.32892 15.0051 3.23047 15.2475 3.23047 15.4998C3.23047 15.7521 3.32892 15.9945 3.50487 16.1753L15.1299 28.1233C15.2182 28.216 15.3241 28.2903 15.4413 28.3419C15.5586 28.3935 15.6849 28.4213 15.8129 28.4237C15.941 28.4262 16.0683 28.4032 16.1874 28.3561C16.3066 28.309 16.4152 28.2388 16.507 28.1495C16.5988 28.0601 16.672 27.9535 16.7223 27.8357C16.7726 27.7179 16.799 27.5913 16.8001 27.4632C16.8012 27.3351 16.7768 27.2081 16.7285 27.0895C16.6801 26.9709 16.6087 26.863 16.5184 26.7722L5.54958 15.4998Z"
        fill="white"
      />
    </svg>
  </SvgIcon>
);

export const RightArrowIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="31"
      height="31"
      viewBox="0 0 31 31"
      fill="none"
    >
      <path
        d="M25.4504 15.4998L14.4816 4.22742C14.3913 4.13658 14.3199 4.02873 14.2715 3.91012C14.2232 3.7915 14.1988 3.66447 14.1999 3.53639C14.201 3.4083 14.2274 3.28169 14.2777 3.1639C14.328 3.0461 14.4012 2.93945 14.493 2.85012C14.5848 2.76079 14.6934 2.69056 14.8126 2.64348C14.9317 2.59641 15.059 2.57342 15.1871 2.57586C15.3151 2.5783 15.4414 2.60611 15.5587 2.65769C15.6759 2.70926 15.7818 2.78358 15.8701 2.87634L27.4951 14.8243C27.6711 15.0051 27.7695 15.2475 27.7695 15.4998C27.7695 15.7521 27.6711 15.9945 27.4951 16.1753L15.8701 28.1233C15.7818 28.216 15.6759 28.2903 15.5587 28.3419C15.4414 28.3935 15.3151 28.4213 15.1871 28.4237C15.059 28.4262 14.9317 28.4032 14.8126 28.3561C14.6934 28.309 14.5848 28.2388 14.493 28.1495C14.4012 28.0601 14.328 27.9535 14.2777 27.8357C14.2274 27.7179 14.201 27.5913 14.1999 27.4632C14.1988 27.3351 14.2232 27.2081 14.2715 27.0895C14.3199 26.9709 14.3913 26.863 14.4816 26.7722L25.4504 15.4998Z"
        fill="white"
      />
    </svg>
  </SvgIcon>
);

export const DoorIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="22"
      viewBox="0 0 19 22"
      fill="none"
    >
      <path
        d="M8.30031 0.237809C8.40439 0.152762 8.52603 0.091856 8.65648 0.059479C8.78693 0.0271021 8.92294 0.02406 9.05471 0.0505717L18.1439 1.86841C18.3499 1.90958 18.5352 2.02081 18.6685 2.1832C18.8017 2.34559 18.8745 2.54911 18.8747 2.75915V19.1142C18.8745 19.3243 18.8017 19.5278 18.6685 19.6902C18.5352 19.8526 18.3499 19.9638 18.1439 20.005L9.05471 21.8228C8.9229 21.8492 8.78688 21.846 8.65646 21.8134C8.52604 21.7808 8.40446 21.7198 8.30049 21.6346C8.19651 21.5494 8.11273 21.4422 8.05518 21.3207C7.99762 21.1992 7.96772 21.0665 7.96764 20.9321V0.941313M13.4212 10.9358C13.4212 10.6947 13.3254 10.4635 13.1549 10.2931C12.9845 10.1226 12.7533 10.0269 12.5122 10.0269C12.2712 10.0269 12.04 10.1226 11.8695 10.2931C11.6991 10.4635 11.6033 10.6947 11.6033 10.9358C11.6033 11.1768 11.6991 11.408 11.8695 11.5785C12.04 11.7489 12.2712 11.8447 12.5122 11.8447C12.7533 11.8447 12.9845 11.7489 13.1549 11.5785C13.3254 11.408 13.4212 11.1768 13.4212 10.9358ZM6.1498 1.85023H1.60521C1.36415 1.85023 1.13296 1.94599 0.962506 2.11645C0.79205 2.2869 0.696289 2.51809 0.696289 2.75915V19.1142C0.696289 19.3553 0.79205 19.5865 0.962506 19.7569C1.13296 19.9274 1.36415 20.0232 1.60521 20.0232H6.1498V1.85023ZM8.30031 0.237809C8.19623 0.32313 8.11241 0.430498 8.05488 0.552158C7.99735 0.673819 7.96755 0.806736 7.96764 0.941313L8.30031 0.237809Z"
        fill="currentColor"
        fillOpacity="0.8"
      />
    </svg>
  </SvgIcon>
);

export const PreviewIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="13"
      height="12"
      viewBox="0 0 13 12"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.65384 0.922852C4.88517 0.922852 3.31386 1.72056 2.19504 2.70802C1.63404 3.20366 1.17077 3.75958 0.844578 4.31423C0.523463 4.8581 0.307693 5.44766 0.307693 5.99977C0.307693 6.55189 0.523463 7.14144 0.844578 7.68531C1.17077 8.23933 1.6334 8.79588 2.19504 9.29152C3.31386 10.279 4.88581 11.0767 6.65384 11.0767C8.42251 11.0767 9.99382 10.279 11.1126 9.29152C11.6743 8.79588 12.1369 8.23933 12.4631 7.68531C12.7842 7.14144 13 6.55189 13 5.99977C13 5.44766 12.7842 4.8581 12.4631 4.31423C12.1369 3.76021 11.6743 3.20366 11.1126 2.70802C9.99382 1.72056 8.42188 0.922852 6.65384 0.922852ZM7.92307 5.99977C8.15153 5.99977 8.3654 5.93948 8.55071 5.83414C8.58453 6.22328 8.49783 6.61341 8.30237 6.95161C8.10692 7.2898 7.81217 7.55969 7.45811 7.72467C7.10405 7.88965 6.70781 7.94173 6.32314 7.87384C5.93847 7.80596 5.58399 7.62139 5.30779 7.34519C5.03158 7.06899 4.84702 6.71451 4.77914 6.32984C4.71125 5.94517 4.76333 5.54893 4.92831 5.19487C5.09329 4.84081 5.36318 4.54606 5.70137 4.3506C6.03957 4.15515 6.4297 4.06845 6.81884 4.10227C6.70881 4.29542 6.65143 4.51409 6.65245 4.73638C6.65347 4.95867 6.71286 5.1768 6.82467 5.36893C6.93648 5.56106 7.09679 5.72046 7.28955 5.83117C7.48232 5.94188 7.70078 6.00002 7.92307 5.99977Z"
        fill="white"
      />
    </svg>
  </SvgIcon>
);

export const AddIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23"
      height="23"
      viewBox="0 0 18 18"
      fill="none"
    >
      <rect
        x="0.75"
        y="0.75"
        width="16.5"
        height="16.5"
        rx="8.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5 9H13M9 13V5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    ;
  </SvgIcon>
);

export const ImageUploadIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M13 21H3.6C3.44087 21 3.28826 20.9368 3.17574 20.8243C3.06321 20.7117 3 20.5591 3 20.4V3.6C3 3.44087 3.06321 3.28826 3.17574 3.17574C3.28826 3.06321 3.44087 3 3.6 3H20.4C20.5591 3 20.7117 3.06321 20.8243 3.17574C20.9368 3.28826 21 3.44087 21 3.6V13"
        stroke="#718EBF"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 16L10 13L15.5 15.5M16 19H19M19 19H22M19 19V16M19 19V22M16 10C15.4696 10 14.9609 9.78929 14.5858 9.41421C14.2107 9.03914 14 8.53043 14 8C14 7.46957 14.2107 6.96086 14.5858 6.58579C14.9609 6.21071 15.4696 6 16 6C16.5304 6 17.0391 6.21071 17.4142 6.58579C17.7893 6.96086 18 7.46957 18 8C18 8.53043 17.7893 9.03914 17.4142 9.41421C17.0391 9.78929 16.5304 10 16 10Z"
        stroke="#718EBF"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const PlayIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="12"
      viewBox="0 0 8 12"
      fill="none"
    >
      <path
        d="M2.97186 1.16853C1.65439 0.400334 0.000244141 1.3503 0.000244141 2.87563V9.12393C0.000244141 10.6493 1.65439 11.5992 2.97186 10.831L8.32788 7.70609C9.63428 6.94422 9.63428 5.05534 8.32788 4.29347L2.97186 1.16853Z"
        fill="white"
      />
    </svg>
  </SvgIcon>
);

export const EditBlackIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M8.5 2.125H3.54167C3.16594 2.125 2.80561 2.27426 2.53993 2.53993C2.27426 2.80561 2.125 3.16594 2.125 3.54167V13.4583C2.125 13.8341 2.27426 14.1944 2.53993 14.4601C2.80561 14.7257 3.16594 14.875 3.54167 14.875H13.4583C13.8341 14.875 14.1944 14.7257 14.4601 14.4601C14.7257 14.1944 14.875 13.8341 14.875 13.4583V8.5"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.0156 1.85905C13.2974 1.57725 13.6796 1.41895 14.0781 1.41895C14.4766 1.41895 14.8588 1.57725 15.1406 1.85905C15.4224 2.14084 15.5807 2.52303 15.5807 2.92155C15.5807 3.32006 15.4224 3.70225 15.1406 3.98405L8.7564 10.369C8.58821 10.537 8.38042 10.66 8.15219 10.7267L6.11715 11.3217C6.0562 11.3394 5.99159 11.3405 5.93009 11.3248C5.86858 11.309 5.81245 11.277 5.76755 11.2321C5.72266 11.1872 5.69066 11.1311 5.6749 11.0696C5.65914 11.0081 5.66021 10.9435 5.67798 10.8825L6.27298 8.84746C6.33994 8.61942 6.4632 8.41188 6.6314 8.24396L13.0156 1.85905Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const ConfigureIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M12.665 12.7092H14.625M1.93166 7.625L0.625 7.65925M1.93166 7.625C1.93166 8.65593 2.7674 9.49167 3.79833 9.49167C4.82926 9.49167 5.665 8.65593 5.665 7.625C5.665 6.59407 4.82926 5.75833 3.79833 5.75833C2.7674 5.75833 1.93166 6.59407 1.93166 7.625ZM6.20087 7.65931H14.625M8.24797 2.6093L0.625 2.6093M14.625 2.6093H12.665M0.625 12.7092H8.24797M12.1983 12.7583C12.1983 13.7893 11.3626 14.625 10.3317 14.625C9.30073 14.625 8.465 13.7893 8.465 12.7583C8.465 11.7274 9.30073 10.8917 10.3317 10.8917C11.3626 10.8917 12.1983 11.7274 12.1983 12.7583ZM12.1983 2.49167C12.1983 3.5226 11.3626 4.35833 10.3317 4.35833C9.30073 4.35833 8.465 3.5226 8.465 2.49167C8.465 1.46074 9.30073 0.625 10.3317 0.625C11.3626 0.625 12.1983 1.46074 12.1983 2.49167Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  </SvgIcon>
);

export const FileUploadIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="77"
      height="77"
      viewBox="0 0 77 77"
      fill="none"
    >
      <path
        d="M37.6357 76.3848C58.0657 76.3848 74.6274 59.823 74.6274 39.3931C74.6274 18.9631 58.0657 2.40137 37.6357 2.40137C17.2058 2.40137 0.644043 18.9631 0.644043 39.3931C0.644043 59.823 17.2058 76.3848 37.6357 76.3848Z"
        fill="#DCFAF8"
      />
      <path
        d="M30.5 51.5C29.7044 51.5 28.9413 51.1839 28.3787 50.6213C27.8161 50.0587 27.5 49.2956 27.5 48.5V24.5H41L48.5 32V48.5C48.5 49.2956 48.184 50.0587 47.6214 50.6213C47.0587 51.1839 46.2957 51.5 45.5 51.5H30.5Z"
        stroke="#16DBCC"
        strokeWidth="2.76923"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M39.5001 24.5V33.5H48.5001"
        stroke="#16DBCC"
        strokeWidth="2.76923"
        strokeLinejoin="round"
      />
      <path
        d="M32 39H40.5714M32 42.4286H44M32 45.8571H37.1429"
        stroke="#16DBCC"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M34.4967 65.1153C34.6636 64.4239 35.6471 64.4239 35.814 65.1153L36.3758 67.4426C36.4354 67.6897 36.6284 67.8826 36.8755 67.9422L39.2027 68.504C39.8941 68.6709 39.8941 69.6544 39.2027 69.8214L36.8755 70.3831C36.6284 70.4428 36.4354 70.6357 36.3758 70.8828L35.814 73.21C35.6471 73.9015 34.6636 73.9015 34.4967 73.21L33.9349 70.8828C33.8752 70.6357 33.6823 70.4428 33.4352 70.3831L31.108 69.8214C30.4165 69.6544 30.4165 68.6709 31.108 68.504L33.4352 67.9422C33.6823 67.8826 33.8752 67.6897 33.9349 67.4426L34.4967 65.1153Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M7.72889 3.19445C7.8958 2.50299 8.87933 2.50299 9.04624 3.19445L9.60802 5.52165C9.66766 5.76875 9.86059 5.96168 10.1077 6.02133L12.4349 6.5831C13.1264 6.75002 13.1264 7.73354 12.4349 7.90046L10.1077 8.46223C9.86059 8.52188 9.66766 8.71481 9.60802 8.96191L9.04624 11.2891C8.87933 11.9806 7.8958 11.9806 7.72889 11.2891L7.16711 8.96191C7.10746 8.71481 6.91454 8.52188 6.66744 8.46223L4.34023 7.90046C3.64877 7.73354 3.64877 6.75002 4.34023 6.5831L6.66744 6.02133C6.91454 5.96168 7.10746 5.76875 7.16711 5.52165L7.72889 3.19445Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.1607 3.57147C36.7844 3.57147 37.29 3.06585 37.29 2.44214C37.29 1.81843 36.7844 1.31282 36.1607 1.31282C35.537 1.31282 35.0314 1.81843 35.0314 2.44214C35.0314 3.06585 35.537 3.57147 36.1607 3.57147ZM36.1607 4.41846C37.2522 4.41846 38.137 3.53363 38.137 2.44214C38.137 1.35065 37.2522 0.46582 36.1607 0.46582C35.0692 0.46582 34.1844 1.35065 34.1844 2.44214C34.1844 3.53363 35.0692 4.41846 36.1607 4.41846Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M71.2771 34.6681C71.1697 34.2469 71.5586 33.8674 71.977 33.985L74.8017 34.7788C75.2202 34.8964 75.3544 35.4229 75.0433 35.7265L72.9436 37.7758C72.6325 38.0794 72.1094 37.9324 72.002 37.5112L71.2771 34.6681Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M5.06645 29.8201C5.48779 29.7132 5.8668 30.1026 5.74866 30.5209L4.95124 33.3445C4.8331 33.7628 4.30642 33.8964 4.00322 33.5849L1.95659 31.4825C1.65339 31.1711 1.80106 30.6482 2.2224 30.5413L5.06645 29.8201Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M58.9247 68.8426C59.3461 68.7357 59.7251 69.125 59.607 69.5434L58.8095 72.367C58.6914 72.7853 58.1647 72.9189 57.8615 72.6074L55.8149 70.505C55.5117 70.1935 55.6594 69.6706 56.0807 69.5638L58.9247 68.8426Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M73.894 20.0197C74.5177 20.0197 75.0233 19.5141 75.0233 18.8904C75.0233 18.2667 74.5177 17.7611 73.894 17.7611C73.2703 17.7611 72.7647 18.2667 72.7647 18.8904C72.7647 19.5141 73.2703 20.0197 73.894 20.0197ZM73.894 20.8667C74.9855 20.8667 75.8703 19.9819 75.8703 18.8904C75.8703 17.7989 74.9855 16.9141 73.894 16.9141C72.8025 16.9141 71.9177 17.7989 71.9177 18.8904C71.9177 19.9819 72.8025 20.8667 73.894 20.8667Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
    </svg>
  </SvgIcon>
);

export const VideoIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="77"
      height="77"
      viewBox="0 0 77 77"
      fill="none"
    >
      <path
        d="M37.6357 76.3843C58.0657 76.3843 74.6274 59.8226 74.6274 39.3926C74.6274 18.9627 58.0657 2.40093 37.6357 2.40093C17.2058 2.40093 0.644043 18.9627 0.644043 39.3926C0.644043 59.8226 17.2058 76.3843 37.6357 76.3843Z"
        fill="#DCFAF8"
      />
      <path
        d="M29.0078 29.8418C29.1544 27.6868 31.4619 26.3606 33.3975 27.3193L33.5889 27.4229L47.2812 35.4111V35.4121C49.2345 36.5513 49.296 39.3241 47.4648 40.5654L47.2812 40.6807L33.5889 48.6689C31.5541 49.8552 29.0003 48.3884 29 46.0332V30.0596L29.0078 29.8418Z"
        stroke="#16DBCC"
        strokeWidth="4"
      />
      <path
        d="M34.4967 65.1152C34.6636 64.4238 35.6471 64.4238 35.814 65.1152L36.3758 67.4424C36.4355 67.6895 36.6284 67.8825 36.8755 67.9421L39.2027 68.5039C39.8941 68.6708 39.8941 69.6543 39.2027 69.8212L36.8755 70.383C36.6284 70.4427 36.4355 70.6356 36.3758 70.8827L35.814 73.2099C35.6471 73.9014 34.6636 73.9014 34.4967 73.2099L33.9349 70.8827C33.8753 70.6356 33.6823 70.4427 33.4352 70.383L31.108 69.8212C30.4166 69.6543 30.4166 68.6708 31.108 68.5039L33.4352 67.9421C33.6823 67.8825 33.8753 67.6895 33.9349 67.4424L34.4967 65.1152Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M7.72886 3.19445C7.89577 2.50299 8.8793 2.50299 9.04621 3.19445L9.60798 5.52165C9.66763 5.76875 9.86056 5.96168 10.1077 6.02133L12.4349 6.5831C13.1263 6.75002 13.1263 7.73354 12.4349 7.90046L10.1077 8.46223C9.86056 8.52188 9.66763 8.71481 9.60798 8.96191L9.04621 11.2891C8.8793 11.9806 7.89577 11.9806 7.72886 11.2891L7.16708 8.96191C7.10743 8.71481 6.91451 8.52188 6.66741 8.46223L4.3402 7.90046C3.64874 7.73354 3.64874 6.75002 4.3402 6.5831L6.66741 6.02133C6.91451 5.96168 7.10743 5.76875 7.16708 5.52165L7.72886 3.19445Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.1606 3.57147C36.7844 3.57147 37.29 3.06585 37.29 2.44214C37.29 1.81843 36.7844 1.31282 36.1606 1.31282C35.5369 1.31282 35.0313 1.81843 35.0313 2.44214C35.0313 3.06585 35.5369 3.57147 36.1606 3.57147ZM36.1606 4.41846C37.2521 4.41846 38.137 3.53363 38.137 2.44214C38.137 1.35065 37.2521 0.46582 36.1606 0.46582C35.0692 0.46582 34.1843 1.35065 34.1843 2.44214C34.1843 3.53363 35.0692 4.41846 36.1606 4.41846Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M71.2771 34.6685C71.1698 34.2473 71.5586 33.8678 71.9771 33.9854L74.8017 34.7792C75.2202 34.8967 75.3544 35.4233 75.0434 35.7269L72.9436 37.7762C72.6325 38.0798 72.1094 37.9328 72.002 37.5116L71.2771 34.6685Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M5.06651 29.8198C5.48786 29.713 5.86686 30.1023 5.74872 30.5206L4.9513 33.3442C4.83316 33.7626 4.30648 33.8961 4.00328 33.5846L1.95665 31.4822C1.65345 31.1708 1.80112 30.6479 2.22246 30.541L5.06651 29.8198Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M58.9248 68.8427C59.3461 68.7358 59.7251 69.1252 59.607 69.5435L58.8096 72.3671C58.6914 72.7854 58.1648 72.919 57.8616 72.6075L55.8149 70.5051C55.5117 70.1937 55.6594 69.6708 56.0807 69.5639L58.9248 68.8427Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M73.8939 20.0192C74.5176 20.0192 75.0233 19.5136 75.0233 18.8899C75.0233 18.2662 74.5176 17.7606 73.8939 17.7606C73.2702 17.7606 72.7646 18.2662 72.7646 18.8899C72.7646 19.5136 73.2702 20.0192 73.8939 20.0192ZM73.8939 20.8662C74.9854 20.8662 75.8702 19.9814 75.8702 18.8899C75.8702 17.7984 74.9854 16.9136 73.8939 16.9136C72.8024 16.9136 71.9176 17.7984 71.9176 18.8899C71.9176 19.9814 72.8024 20.8662 73.8939 20.8662Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
    </svg>
  </SvgIcon>
);

export const MusicIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="77"
      height="77"
      viewBox="0 0 77 77"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M37.6357 76.3843C58.0657 76.3843 74.6274 59.8226 74.6274 39.3926C74.6274 18.9627 58.0657 2.40093 37.6357 2.40093C17.2058 2.40093 0.644043 18.9627 0.644043 39.3926C0.644043 59.8226 17.2058 76.3843 37.6357 76.3843Z"
        fill="#DCFAF8"
      />
      <path
        d="M30.5 51.5C29.7044 51.5 28.9413 51.1839 28.3787 50.6213C27.8161 50.0587 27.5 49.2956 27.5 48.5V24.5H41L48.5 32V48.5C48.5 49.2956 48.1839 50.0587 47.6213 50.6213C47.0587 51.1839 46.2957 51.5 45.5 51.5H30.5Z"
        stroke="#16DBCC"
        strokeWidth="2.76923"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M39.5 24.5V33.5H48.5"
        stroke="#16DBCC"
        strokeWidth="2.76923"
        strokeLinejoin="round"
      />
      <path
        d="M36.5 46.9996C37.3284 46.9996 38 46.3281 38 45.4996C38 44.6712 37.3284 43.9996 36.5 43.9996C35.6716 43.9996 35 44.6712 35 45.4996C35 46.3281 35.6716 46.9996 36.5 46.9996Z"
        stroke="#16DBCC"
        strokeWidth="2.76923"
      />
      <path
        d="M38 45.5V38L41 41"
        stroke="#16DBCC"
        strokeWidth="2.76923"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M34.4967 65.1152C34.6636 64.4238 35.6471 64.4238 35.814 65.1152L36.3758 67.4424C36.4355 67.6895 36.6284 67.8825 36.8755 67.9421L39.2027 68.5039C39.8941 68.6708 39.8941 69.6543 39.2027 69.8212L36.8755 70.383C36.6284 70.4427 36.4355 70.6356 36.3758 70.8827L35.814 73.2099C35.6471 73.9014 34.6636 73.9014 34.4967 73.2099L33.9349 70.8827C33.8753 70.6356 33.6823 70.4427 33.4352 70.383L31.108 69.8212C30.4166 69.6543 30.4166 68.6708 31.108 68.5039L33.4352 67.9421C33.6823 67.8825 33.8753 67.6895 33.9349 67.4424L34.4967 65.1152Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M7.72886 3.19445C7.89577 2.50299 8.8793 2.50299 9.04621 3.19445L9.60798 5.52165C9.66763 5.76875 9.86056 5.96168 10.1077 6.02133L12.4349 6.5831C13.1263 6.75002 13.1263 7.73354 12.4349 7.90046L10.1077 8.46223C9.86056 8.52188 9.66763 8.71481 9.60798 8.96191L9.04621 11.2891C8.8793 11.9806 7.89577 11.9806 7.72886 11.2891L7.16708 8.96191C7.10743 8.71481 6.91451 8.52188 6.66741 8.46223L4.3402 7.90046C3.64874 7.73354 3.64874 6.75002 4.3402 6.5831L6.66741 6.02133C6.91451 5.96168 7.10743 5.76875 7.16708 5.52165L7.72886 3.19445Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.1606 3.57147C36.7844 3.57147 37.29 3.06585 37.29 2.44214C37.29 1.81843 36.7844 1.31282 36.1606 1.31282C35.5369 1.31282 35.0313 1.81843 35.0313 2.44214C35.0313 3.06585 35.5369 3.57147 36.1606 3.57147ZM36.1606 4.41846C37.2521 4.41846 38.137 3.53363 38.137 2.44214C38.137 1.35065 37.2521 0.46582 36.1606 0.46582C35.0692 0.46582 34.1843 1.35065 34.1843 2.44214C34.1843 3.53363 35.0692 4.41846 36.1606 4.41846Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M71.2771 34.6685C71.1698 34.2473 71.5586 33.8678 71.9771 33.9854L74.8017 34.7792C75.2202 34.8967 75.3544 35.4233 75.0434 35.7269L72.9436 37.7762C72.6325 38.0798 72.1094 37.9328 72.002 37.5116L71.2771 34.6685Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M5.06651 29.8198C5.48786 29.713 5.86686 30.1023 5.74872 30.5206L4.9513 33.3442C4.83316 33.7626 4.30648 33.8961 4.00328 33.5846L1.95665 31.4822C1.65345 31.1708 1.80112 30.6479 2.22246 30.541L5.06651 29.8198Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        d="M58.9248 68.8427C59.3461 68.7358 59.7251 69.1252 59.607 69.5435L58.8096 72.3671C58.6914 72.7854 58.1648 72.919 57.8616 72.6075L55.8149 70.5051C55.5117 70.1937 55.6594 69.6708 56.0807 69.5639L58.9248 68.8427Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M73.8939 20.0192C74.5176 20.0192 75.0233 19.5136 75.0233 18.8899C75.0233 18.2662 74.5176 17.7606 73.8939 17.7606C73.2702 17.7606 72.7646 18.2662 72.7646 18.8899C72.7646 19.5136 73.2702 20.0192 73.8939 20.0192ZM73.8939 20.8662C74.9854 20.8662 75.8702 19.9814 75.8702 18.8899C75.8702 17.7984 74.9854 16.9136 73.8939 16.9136C72.8024 16.9136 71.9176 17.7984 71.9176 18.8899C71.9176 19.9814 72.8024 20.8662 73.8939 20.8662Z"
        fill="#16DBCC"
        fillOpacity="0.5"
      />
    </svg>
  </SvgIcon>
);

export const CloseIconWhite = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M1.71803 1.71751L15.1531 15.1525M1.71803 15.1525L15.1531 1.71751"
        stroke="white"
        strokeWidth="1.74702"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const DeleteBlackIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M13.8125 3.89616L13.3733 10.9972C13.2614 12.8112 13.2055 13.7186 12.75 14.371C12.5252 14.6934 12.2357 14.9655 11.9 15.17C11.2221 15.5837 10.3133 15.5837 8.49575 15.5837C6.67533 15.5837 5.76512 15.5837 5.08583 15.1693C4.75 14.9644 4.46053 14.6918 4.23583 14.3689C3.78108 13.7158 3.72583 12.807 3.61675 10.9901L3.1875 3.89616M6.375 8.31262H10.625M7.4375 11.0886H9.5625M2.125 3.89616H14.875M11.3723 3.89616L10.8892 2.89883C10.5676 2.23583 10.4075 1.90503 10.1306 1.6982C10.0691 1.65238 10.0039 1.61164 9.93579 1.57637C9.62908 1.41699 9.26075 1.41699 8.52408 1.41699C7.76971 1.41699 7.39217 1.41699 7.07979 1.58274C7.01074 1.61973 6.94488 1.66237 6.88287 1.71024C6.60237 1.92487 6.44583 2.26841 6.13275 2.95478L5.70421 3.89616"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const ArrowDownIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
    >
      <path
        d="M3.0256 5.15699L7.7106 9.76799C7.81283 9.86912 7.95081 9.92584 8.0946 9.92584C8.2384 9.92584 8.37638 9.86912 8.4786 9.76799L13.1636 5.15799C13.2665 5.05691 13.4049 5.00028 13.5491 5.00028C13.6933 5.00028 13.8317 5.05691 13.9346 5.15799C13.9853 5.20737 14.0255 5.26638 14.053 5.33156C14.0805 5.39674 14.0946 5.46675 14.0946 5.53749C14.0946 5.60822 14.0805 5.67824 14.053 5.74342C14.0255 5.8086 13.9853 5.86761 13.9346 5.91699L9.2506 10.527C8.94208 10.8299 8.52698 10.9996 8.0946 10.9996C7.66223 10.9996 7.24713 10.8299 6.9386 10.527L2.2546 5.91699C2.2038 5.8676 2.16342 5.80852 2.13585 5.74325C2.10827 5.67798 2.09406 5.60784 2.09406 5.53699C2.09406 5.46613 2.10827 5.396 2.13585 5.33073C2.16342 5.26546 2.2038 5.20638 2.2546 5.15699C2.35746 5.05591 2.4959 4.99928 2.6401 4.99928C2.78431 4.99928 2.92275 5.05591 3.0256 5.15699Z"
        fill={props.fill || '#2E2E2E'}
      />
    </svg>
  </SvgIcon>
);

export const ArrowLeftIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
    >
      <path
        d="M11.7125 13.0689L7.10152 8.38395C7.00039 8.28173 6.94367 8.14374 6.94367 7.99995C6.94367 7.85616 7.00039 7.71817 7.10152 7.61595L11.7115 2.93095C11.8126 2.82809 11.8692 2.68965 11.8692 2.54545C11.8692 2.40124 11.8126 2.2628 11.7115 2.15995C11.6621 2.1093 11.6031 2.06905 11.5379 2.04156C11.4728 2.01408 11.4028 1.99992 11.332 1.99992C11.2613 1.99992 11.1913 2.01408 11.1261 2.04156C11.0609 2.06905 11.0019 2.1093 10.9525 2.15995L6.34252 6.84395C6.03959 7.15247 5.86987 7.56757 5.86987 7.99995C5.86987 8.43232 6.03959 8.84743 6.34252 9.15595L10.9525 13.8399C11.0019 13.8907 11.061 13.9311 11.1263 13.9587C11.1915 13.9863 11.2617 14.0005 11.3325 14.0005C11.4034 14.0005 11.4735 13.9863 11.5388 13.9587C11.604 13.9311 11.6631 13.8907 11.7125 13.8399C11.8136 13.7371 11.8702 13.5987 11.8702 13.4544C11.8702 13.3102 11.8136 13.1718 11.7125 13.0689Z"
        fill={props.fill || '#2E2E2E'}
      />
    </svg>
  </SvgIcon>
);

export const ArrowRightBoldIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="18"
      viewBox="0 0 11 18"
      fill="none"
    >
      <path
        d="M0.181406 1.47109L1.65388 0L9.67408 8.01742C9.80336 8.14589 9.90596 8.29866 9.97598 8.46693C10.046 8.6352 10.082 8.81566 10.082 8.99792C10.082 9.18017 10.046 9.36063 9.97598 9.5289C9.90596 9.69718 9.80336 9.84994 9.67408 9.97841L1.65388 18L0.182794 16.5289L7.71032 9L0.181406 1.47109Z"
        fill={props.fill || 'black'}
        fillOpacity="0.8"
      />
    </svg>
  </SvgIcon>
);

export const ArrowRightIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
    >
      <path
        d="M5.28748 13.0689L9.89848 8.38395C9.99961 8.28173 10.0563 8.14374 10.0563 7.99995C10.0563 7.85616 9.99961 7.71817 9.89848 7.61595L5.28848 2.93095C5.18741 2.82809 5.13077 2.68965 5.13077 2.54545C5.13077 2.40124 5.18741 2.2628 5.28848 2.15995C5.33786 2.1093 5.39687 2.06905 5.46205 2.04156C5.52723 2.01408 5.59725 1.99992 5.66798 1.99992C5.73872 1.99992 5.80873 2.01408 5.87391 2.04156C5.93909 2.06905 5.9981 2.1093 6.04748 2.15995L10.6575 6.84395C10.9604 7.15247 11.1301 7.56757 11.1301 7.99995C11.1301 8.43232 10.9604 8.84743 10.6575 9.15595L6.04748 13.8399C5.99809 13.8907 5.93901 13.9311 5.87374 13.9587C5.80847 13.9863 5.73834 14.0005 5.66748 14.0005C5.59663 14.0005 5.52649 13.9863 5.46122 13.9587C5.39595 13.9311 5.33687 13.8907 5.28748 13.8399C5.18641 13.7371 5.12977 13.5987 5.12977 13.4544C5.12977 13.3102 5.18641 13.1718 5.28748 13.0689Z"
        fill={props.fill || '#2E2E2E'}
      />
    </svg>
  </SvgIcon>
);

export const InputSearchIcon = ({
  fill = 'var(--dark-2)',
  style,
  ...props
}: SvgIconProps) => (
  <SvgIcon style={{ color: fill, ...style }} {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <g clipPath="url(#clip0_1412_183)">
        <path
          d="M19.3359 18.2109L14.7344 13.6094C15.875 12.2188 16.5625 10.4375 16.5625 8.49609C16.5625 4.04297 12.9492 0.429688 8.49609 0.429688C4.03906 0.429688 0.429688 4.04297 0.429688 8.49609C0.429688 12.9492 4.03906 16.5625 8.49609 16.5625C10.4375 16.5625 12.2148 15.8789 13.6055 14.7383L18.207 19.3359C18.5195 19.6484 19.0234 19.6484 19.3359 19.3359C19.6484 19.0273 19.6484 18.5195 19.3359 18.2109ZM8.49609 14.957C4.92969 14.957 2.03125 12.0586 2.03125 8.49609C2.03125 4.93359 4.92969 2.03125 8.49609 2.03125C12.0586 2.03125 14.9609 4.93359 14.9609 8.49609C14.9609 12.0586 12.0586 14.957 8.49609 14.957Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_1412_183">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const CalendarIcon = ({ fill = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M19.2648 3.28015C19.1283 3.14532 18.9643 3.03822 18.7828 2.96536C18.6012 2.89251 18.4059 2.85544 18.2087 2.85641H16.6958V1.78517C16.6993 1.55023 16.6519 1.31711 16.5565 1.10019C16.4611 0.88327 16.3198 0.687151 16.1414 0.52393C15.9689 0.355141 15.7617 0.221592 15.5326 0.131516C15.3035 0.0414393 15.0574 -0.00325616 14.8093 0.000184582H14.0556C13.8074 -0.00309463 13.5612 0.0417972 13.3321 0.132091C13.1031 0.222384 12.8959 0.356165 12.7236 0.52518C12.545 0.688356 12.4037 0.884466 12.3083 1.1014C12.2129 1.31833 12.1656 1.55147 12.1691 1.78642V2.85766H7.64241V1.78517C7.64587 1.55023 7.59846 1.31711 7.5031 1.10019C7.40774 0.88327 7.26645 0.687151 7.08795 0.52393C6.91562 0.355296 6.70866 0.221837 6.47981 0.131765C6.25097 0.0416939 6.00508 -0.00308484 5.75727 0.000184582H5.00348C4.75536 -0.00309463 4.50916 0.0417972 4.28007 0.132091C4.05098 0.222384 3.84386 0.356165 3.67148 0.52518C3.49298 0.688401 3.3517 0.88452 3.25634 1.10144C3.16098 1.31836 3.11357 1.55148 3.11703 1.78642V2.85766H1.60813C1.41093 2.85669 1.2156 2.89376 1.03402 2.96661C0.852454 3.03947 0.688456 3.14657 0.552032 3.2814C0.409637 3.41058 0.29652 3.56586 0.219579 3.73779C0.142639 3.90971 0.103487 4.09467 0.10451 4.2814V18.57C0.1096 18.9467 0.26945 19.3067 0.550154 19.5736C0.830858 19.8406 1.21034 19.9935 1.60813 20H18.2034C18.6019 19.9948 18.9825 19.8426 19.2643 19.5758C19.5461 19.309 19.7068 18.9486 19.7123 18.5713V4.28515C19.714 4.09758 19.6752 3.91167 19.5983 3.73884C19.5213 3.566 19.4078 3.40991 19.2648 3.28015ZM5.00744 18.57H1.60813V15.3563H5.00216L5.00744 18.57ZM5.00744 14.6413H1.60813V11.0701H5.00216L5.00744 14.6413ZM5.00744 10.3551H1.60813V7.14137H5.00216L5.00744 10.3551ZM4.74342 5.24889C4.7077 5.21666 4.67932 5.17786 4.66002 5.13487C4.64072 5.09188 4.63092 5.0456 4.63121 4.99889V1.78517C4.63257 1.69076 4.67279 1.60058 4.7433 1.53381C4.81381 1.46704 4.90905 1.42896 5.00876 1.42767H5.75727C5.85698 1.42896 5.95222 1.46704 6.02273 1.53381C6.09324 1.60058 6.13346 1.69076 6.13483 1.78517V5.00014C6.13346 5.09455 6.09324 5.18473 6.02273 5.2515C5.95222 5.31826 5.85698 5.35634 5.75727 5.35764H5.00348C4.95362 5.35802 4.90421 5.34867 4.85833 5.33018C4.81245 5.31168 4.77109 5.28443 4.73682 5.25014L4.74342 5.24889ZM9.53414 18.5688H5.75727V15.3563H9.52886L9.53414 18.5688ZM9.53414 14.6401H5.75727V11.0701H9.52886L9.53414 14.6401ZM9.53414 10.3538H5.75727V7.14137H9.52886L9.53414 10.3538ZM14.0608 18.5675H10.2827V15.3563H14.0542L14.0608 18.5675ZM14.0608 14.6388H10.2827V11.0701H14.0542L14.0608 14.6388ZM14.0608 10.3526H10.2827V7.14137H14.0542L14.0608 10.3526ZM13.7968 5.24639C13.7604 5.21509 13.7311 5.17713 13.7107 5.1348C13.6903 5.09248 13.6792 5.04666 13.678 5.00014V1.78517C13.679 1.69066 13.7191 1.60029 13.7897 1.53346C13.8603 1.46663 13.9557 1.42865 14.0556 1.42767H14.8093C14.9092 1.42865 15.0046 1.46663 15.0752 1.53346C15.1458 1.60029 15.1859 1.69066 15.1869 1.78517V5.00014C15.1859 5.09465 15.1458 5.18502 15.0752 5.25185C15.0046 5.31868 14.9092 5.35666 14.8093 5.35764H14.0556C14.0057 5.35809 13.9562 5.34878 13.9104 5.33028C13.8645 5.31178 13.8231 5.28449 13.7889 5.25014L13.7968 5.24639ZM18.2113 18.5663H14.8093V15.3563H18.2034L18.2113 18.5663ZM18.2113 14.6376H14.8093V11.0701H18.2034L18.2113 14.6376ZM18.2113 10.3513H14.8093V7.14137H18.2034L18.2113 10.3513Z"
        fill={fill}
      />
    </svg>
  </SvgIcon>
);

export const ProfileIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 18.1115C3 15.6984 4.69732 13.643 7.00404 13.2627L7.21182 13.2284C9.05892 12.9239 10.9411 12.9239 12.7882 13.2284L12.996 13.2627C15.3027 13.643 17 15.6984 17 18.1115C17 19.1545 16.1815 20 15.1719 20H4.82813C3.81848 20 3 19.1545 3 18.1115Z"
        stroke="#191B1B"
        strokeWidth="1.67"
      />
      <path
        d="M14.0834 5.9375C14.0834 8.11212 12.2552 9.875 10 9.875C7.74486 9.875 5.91669 8.11212 5.91669 5.9375C5.91669 3.76288 7.74486 2 10 2C12.2552 2 14.0834 3.76288 14.0834 5.9375Z"
        stroke="#191B1B"
        strokeWidth="1.67"
      />
    </svg>
  </SvgIcon>
);

export const BackIcon = ({ fill = '#2E2E2E', ...props }) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M18.9999 11H7.82992L12.7099 6.11998C13.0999 5.72998 13.0999 5.08998 12.7099 4.69998C12.6174 4.60727 12.5075 4.53372 12.3865 4.48354C12.2656 4.43336 12.1359 4.40753 12.0049 4.40753C11.874 4.40753 11.7443 4.43336 11.6233 4.48354C11.5023 4.53372 11.3924 4.60727 11.2999 4.69998L4.70992 11.29C4.61722 11.3825 4.54367 11.4924 4.49349 11.6134C4.44331 11.7343 4.41748 11.864 4.41748 11.995C4.41748 12.1259 4.44331 12.2556 4.49349 12.3766C4.54367 12.4976 4.61722 12.6075 4.70992 12.7L11.2999 19.29C11.3925 19.3826 11.5024 19.456 11.6234 19.5061C11.7443 19.5562 11.874 19.582 12.0049 19.582C12.1359 19.582 12.2655 19.5562 12.3865 19.5061C12.5074 19.456 12.6173 19.3826 12.7099 19.29C12.8025 19.1974 12.8759 19.0875 12.9261 18.9665C12.9762 18.8456 13.0019 18.7159 13.0019 18.585C13.0019 18.454 12.9762 18.3244 12.9261 18.2034C12.8759 18.0825 12.8025 17.9726 12.7099 17.88L7.82992 13H18.9999C19.5499 13 19.9999 12.55 19.9999 12C19.9999 11.45 19.5499 11 18.9999 11Z"
        fill={fill}
      />
    </svg>
  </SvgIcon>
);

export const ArrowLeftStraight = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="14"
      viewBox="0 0 9 14"
      fill="none"
    >
      <path d="M8 13L2 7L8 1" stroke="currentColor" strokeWidth="2" />
    </svg>
  </SvgIcon>
);

export const ArrowRightStraight = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="14"
      viewBox="0 0 9 14"
      fill="none"
    >
      <path
        d="M1 13L7 7L1 1"
        stroke="currentColor"
        // strokeWidth="2"
        strokeWidth={props.strokeWidth || '2'}
      />
    </svg>
  </SvgIcon>
);

export const FilterIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M3 2.25H15C15.1989 2.25 15.3897 2.32902 15.5303 2.46967C15.671 2.61032 15.75 2.80109 15.75 3V4.1895C15.75 4.3884 15.6709 4.57913 15.5303 4.71975L10.7198 9.53025C10.5791 9.67087 10.5 9.8616 10.5 10.0605V14.7892C10.5 14.9033 10.474 15.0158 10.424 15.1182C10.374 15.2207 10.3013 15.3104 10.2114 15.3805C10.1215 15.4506 10.0169 15.4994 9.90532 15.523C9.79378 15.5466 9.67834 15.5445 9.56775 15.5167L8.06775 15.1417C7.90557 15.1011 7.76161 15.0075 7.65874 14.8757C7.55588 14.7438 7.5 14.5814 7.5 14.4142V10.0605C7.49996 9.8616 7.42091 9.67087 7.28025 9.53025L2.46975 4.71975C2.32909 4.57913 2.25004 4.3884 2.25 4.1895V3C2.25 2.80109 2.32902 2.61032 2.46967 2.46967C2.61032 2.32902 2.80109 2.25 3 2.25Z"
        stroke="#718EBF"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const MoreIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M10.4938 19.3817L10.4847 19.3833L10.4255 19.4125L10.4088 19.4158L10.3972 19.4125L10.338 19.3833C10.3291 19.3805 10.3224 19.3819 10.318 19.3875L10.3147 19.3958L10.3005 19.7525L10.3047 19.7692L10.313 19.78L10.3997 19.8417L10.4122 19.845L10.4222 19.8417L10.5088 19.78L10.5188 19.7667L10.5222 19.7525L10.508 19.3967C10.5058 19.3878 10.501 19.3828 10.4938 19.3817ZM10.7147 19.2875L10.7038 19.2892L10.5497 19.3667L10.5413 19.375L10.5388 19.3842L10.5538 19.7425L10.558 19.7525L10.5647 19.7583L10.7322 19.8358C10.7427 19.8386 10.7508 19.8364 10.7563 19.8292L10.7597 19.8175L10.7313 19.3058C10.7285 19.2958 10.723 19.2897 10.7147 19.2875ZM10.1188 19.2892C10.1151 19.2869 10.1108 19.2862 10.1066 19.2871C10.1024 19.2881 10.0987 19.2906 10.0963 19.2942L10.0913 19.3058L10.063 19.8175C10.0635 19.8275 10.0683 19.8342 10.0772 19.8375L10.0897 19.8358L10.2572 19.7583L10.2655 19.7517L10.2688 19.7425L10.283 19.3842L10.2805 19.3742L10.2722 19.3658L10.1188 19.2892Z"
        fill="#2E2E2E"
      />
      <path
        d="M9.99984 1.66663C14.6023 1.66663 18.3332 5.39746 18.3332 9.99996C18.3332 14.6025 14.6023 18.3333 9.99984 18.3333C5.39734 18.3333 1.6665 14.6025 1.6665 9.99996C1.6665 5.39746 5.39734 1.66663 9.99984 1.66663ZM9.99984 3.33329C8.23173 3.33329 6.53603 4.03567 5.28579 5.28591C4.03555 6.53616 3.33317 8.23185 3.33317 9.99996C3.33317 11.7681 4.03555 13.4638 5.28579 14.714C6.53603 15.9642 8.23173 16.6666 9.99984 16.6666C11.7679 16.6666 13.4636 15.9642 14.7139 14.714C15.9641 13.4638 16.6665 11.7681 16.6665 9.99996C16.6665 8.23185 15.9641 6.53616 14.7139 5.28591C13.4636 4.03567 11.7679 3.33329 9.99984 3.33329ZM6.24984 8.74996C6.58136 8.74996 6.8993 8.88166 7.13372 9.11608C7.36814 9.3505 7.49984 9.66844 7.49984 9.99996C7.49984 10.3315 7.36814 10.6494 7.13372 10.8838C6.8993 11.1183 6.58136 11.25 6.24984 11.25C5.91832 11.25 5.60037 11.1183 5.36595 10.8838C5.13153 10.6494 4.99984 10.3315 4.99984 9.99996C4.99984 9.66844 5.13153 9.3505 5.36595 9.11608C5.60037 8.88166 5.91832 8.74996 6.24984 8.74996ZM9.99984 8.74996C10.3314 8.74996 10.6493 8.88166 10.8837 9.11608C11.1181 9.3505 11.2498 9.66844 11.2498 9.99996C11.2498 10.3315 11.1181 10.6494 10.8837 10.8838C10.6493 11.1183 10.3314 11.25 9.99984 11.25C9.66832 11.25 9.35037 11.1183 9.11595 10.8838C8.88153 10.6494 8.74984 10.3315 8.74984 9.99996C8.74984 9.66844 8.88153 9.3505 9.11595 9.11608C9.35037 8.88166 9.66832 8.74996 9.99984 8.74996ZM13.7498 8.74996C14.0814 8.74996 14.3993 8.88166 14.6337 9.11608C14.8681 9.3505 14.9998 9.66844 14.9998 9.99996C14.9998 10.3315 14.8681 10.6494 14.6337 10.8838C14.3993 11.1183 14.0814 11.25 13.7498 11.25C13.4183 11.25 13.1004 11.1183 12.866 10.8838C12.6315 10.6494 12.4998 10.3315 12.4998 9.99996C12.4998 9.66844 12.6315 9.3505 12.866 9.11608C13.1004 8.88166 13.4183 8.74996 13.7498 8.74996Z"
        fill="#2E2E2E"
      />
    </svg>
  </SvgIcon>
);

export const TableArrowDownIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="13"
      height="7"
      viewBox="0 0 13 7"
      fill="none"
    >
      <path
        d="M1.686 1L6.843 5.2L12 1"
        stroke="#718EBF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const CloseIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M1.71803 1.71751L15.1531 15.1525M1.71803 15.1525L15.1531 1.71751"
        stroke={stroke}
        strokeWidth="1.74702"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const ListViewIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M5.24999 7.59375V10.4062C5.24999 10.8722 4.87223 11.25 4.40624 11.25H0.84375C0.377754 11.25 0 10.8722 0 10.4062V7.59375C0 7.12775 0.377754 6.75 0.84375 6.75H4.40624C4.87223 6.75 5.24999 7.12775 5.24999 7.59375ZM0 13.2188V16.0312C0 16.4972 0.377754 16.875 0.84375 16.875H4.40624C4.87223 16.875 5.24999 16.4972 5.24999 16.0312V13.2188C5.24999 12.7528 4.87223 12.375 4.40624 12.375H0.84375C0.377754 12.375 0 12.7528 0 13.2188ZM4.40624 1.125H0.84375C0.377754 1.125 0 1.50275 0 1.96875V4.78125C0 5.24725 0.377754 5.625 0.84375 5.625H4.40624C4.87223 5.625 5.24999 5.24725 5.24999 4.78125V1.96875C5.24999 1.50275 4.87223 1.125 4.40624 1.125ZM7.21874 16.875H17.1562C17.6222 16.875 18 16.4972 18 16.0312V13.2188C18 12.7528 17.6222 12.375 17.1562 12.375H7.21874C6.75274 12.375 6.37499 12.7528 6.37499 13.2188V16.0312C6.37499 16.4972 6.75274 16.875 7.21874 16.875ZM6.37499 1.96875V4.78125C6.37499 5.24725 6.75274 5.625 7.21874 5.625H17.1562C17.6222 5.625 18 5.24725 18 4.78125V1.96875C18 1.50275 17.6222 1.125 17.1562 1.125H7.21874C6.75274 1.125 6.37499 1.50275 6.37499 1.96875ZM7.21874 11.25H17.1562C17.6222 11.25 18 10.8722 18 10.4062V7.59375C18 7.12775 17.6222 6.75 17.1562 6.75H7.21874C6.75274 6.75 6.37499 7.12775 6.37499 7.59375V10.4062C6.37499 10.8722 6.75274 11.25 7.21874 11.25Z"
        fill="#727475"
        fillOpacity="0.6"
      />
    </svg>
  </SvgIcon>
);

export const GridViewIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M6.5 18H11.5V13H6.5V18ZM13 18H14.75C15.612 18 16.4386 17.6576 17.0481 17.0481C17.6576 16.4386 18 15.612 18 14.75V13H13V18ZM18 11.5V6.5H13V11.5H18ZM18 5V3.25C18 2.38805 17.6576 1.5614 17.0481 0.951903C16.4386 0.34241 15.612 0 14.75 0H13V5H18ZM11.5 0H6.5V5H11.5V0ZM5 0H3.25C2.38805 0 1.5614 0.34241 0.951903 0.951903C0.34241 1.5614 0 2.38805 0 3.25V5H5V0ZM0 6.5V11.5H5V6.5H0ZM0 13V14.75C0 15.612 0.34241 16.4386 0.951903 17.0481C1.5614 17.6576 2.38805 18 3.25 18H5V13H0ZM11.5 6.5V11.5H6.5V6.5H11.5Z"
        fill="#727475"
        fillOpacity="0.6"
      />
    </svg>
  </SvgIcon>
);

export const ExportIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="25"
      viewBox="0 0 26 25"
      fill="none"
    >
      <path
        d="M4.83496 16.4268V17.4491C4.83496 18.2658 5.15938 19.049 5.73685 19.6265C6.31433 20.204 7.09755 20.5284 7.91421 20.5284H18.1784C18.9951 20.5284 19.7783 20.204 20.3558 19.6265C20.9332 19.049 21.2576 18.2658 21.2576 17.4491V16.4227M13.0463 15.9095V4.6189M13.0463 4.6189L16.6388 8.21136M13.0463 4.6189L9.45384 8.21136"
        stroke={props.stroke || '#718EBF'}
        strokeWidth="1.47804"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const DeleteIcon = ({
  strokeColor = 'var(--red-3)',
  ...props
}: StrokeColorIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M13.8125 3.89616L13.3733 10.9972C13.2614 12.8112 13.2055 13.7186 12.75 14.371C12.5252 14.6934 12.2357 14.9655 11.9 15.17C11.2221 15.5837 10.3133 15.5837 8.49575 15.5837C6.67533 15.5837 5.76512 15.5837 5.08583 15.1693C4.75 14.9644 4.46053 14.6918 4.23583 14.3689C3.78108 13.7158 3.72583 12.807 3.61675 10.9901L3.1875 3.89616M6.375 8.31262H10.625M7.4375 11.0886H9.5625M2.125 3.89616H14.875M11.3723 3.89616L10.8892 2.89883C10.5676 2.23583 10.4075 1.90503 10.1306 1.6982C10.0691 1.65238 10.0039 1.61164 9.93579 1.57637C9.62908 1.41699 9.26075 1.41699 8.52408 1.41699C7.76971 1.41699 7.39217 1.41699 7.07979 1.58274C7.01074 1.61973 6.94488 1.66237 6.88287 1.71024C6.60237 1.92487 6.44583 2.26841 6.13275 2.95478L5.70421 3.89616"
        style={{ stroke: strokeColor }}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const TriangleWarningIcon = ({
  stroke = 'var(--orange-1)',
  ...props
}: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9v4M12 17h.01"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const InfoIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 23.2421C18.0751 23.2421 23 18.2654 23 12.1263C23 5.98723 18.0751 1.01052 12 1.01052C5.92487 1.01052 1 5.98723 1 12.1263C1 18.2654 5.92487 23.2421 12 23.2421Z"
        stroke="#191B1B"
        strokeOpacity="0.6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 16.5727V12.1264M12 7.68005H12.011"
        stroke="#191B1B"
        strokeOpacity="0.6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const RejectIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M2 8.5C2 4.91056 4.91056 2 8.5 2C12.0894 2 15 4.91056 15 8.5C15 12.0894 12.0894 15 8.5 15C4.91056 15 2 12.0894 2 8.5Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.78931 4.33926L8.02829 7.57825L12.3469 11.8969"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const CircleCheckIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M2.125 8.5C2.125 4.97958 4.97958 2.125 8.5 2.125C12.0204 2.125 14.875 4.97958 14.875 8.5C14.875 12.0204 12.0204 14.875 8.5 14.875C4.97958 14.875 2.125 12.0204 2.125 8.5Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.66602 8.5026L7.79102 10.6276L11.3327 7.08594"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const CircleCloseIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M2.125 8.5C2.125 4.97958 4.97958 2.125 8.5 2.125C12.0204 2.125 14.875 4.97958 14.875 8.5C14.875 12.0204 12.0204 14.875 8.5 14.875C4.97958 14.875 2.125 12.0204 2.125 8.5Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 11L8.5 8.5M8.5 8.5L6 6M8.5 8.5L11 6M8.5 8.5L6 11"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const NotificationIcon = ({
  strokeWidth = 0.4,
  ...props
}: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.4419 25C9.28888 25 7.53564 23.2479 7.53564 21.0938C7.53564 20.6625 7.88564 20.3125 8.31689 20.3125C8.74815 20.3125 9.09814 20.6625 9.09814 21.0938C9.09814 22.3866 10.1502 23.4375 11.4419 23.4375C12.7336 23.4375 13.7856 22.3866 13.7856 21.0938C13.7856 20.6625 14.1356 20.3125 14.5669 20.3125C14.9981 20.3125 15.3481 20.6625 15.3481 21.0938C15.3481 23.2479 13.5951 25 11.4419 25Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
      <path
        d="M20.0357 21.875H2.84824C1.84307 21.875 1.02539 21.0573 1.02539 20.0521C1.02539 19.5187 1.25771 19.0136 1.66283 18.6666C1.68896 18.6438 1.717 18.623 1.74618 18.6041C3.2753 17.2697 4.15039 15.35 4.15039 13.3228V10.4166C4.15039 6.39591 7.42226 3.125 11.442 3.125C11.6087 3.125 11.7889 3.12805 11.9556 3.15628C12.3816 3.22704 12.6692 3.63026 12.5982 4.05521C12.5275 4.48017 12.117 4.7678 11.6993 4.69685C11.6159 4.6833 11.5244 4.6875 11.442 4.6875C8.28381 4.6875 5.71289 7.25727 5.71289 10.4166V13.3228C5.71289 15.8396 4.60968 18.2209 2.68898 19.8551C2.67334 19.8677 2.6598 19.8792 2.64301 19.8906C2.61497 19.9261 2.58789 19.9802 2.58789 20.0521C2.58789 20.1937 2.70672 20.3125 2.84824 20.3125H20.0357C20.1775 20.3125 20.2963 20.1937 20.2963 20.0521C20.2963 19.9791 20.2692 19.9261 20.24 19.8906C20.2244 19.8792 20.2108 19.8677 20.1952 19.8551C18.2734 18.2198 17.1713 15.8396 17.1713 13.3228V12.1876C17.1713 11.7563 17.5213 11.4063 17.9525 11.4063C18.3838 11.4063 18.7338 11.7563 18.7338 12.1876V13.3228C18.7338 15.3511 19.6098 17.2718 21.1411 18.6073C21.1691 18.626 21.1962 18.6459 21.2212 18.6678C21.6265 19.0136 21.8588 19.5187 21.8588 20.0521C21.8588 21.0573 21.0411 21.875 20.0357 21.875Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
      <path
        d="M18.7338 10.4166C15.8619 10.4166 13.5254 8.08029 13.5254 5.2084C13.5254 2.3365 15.8619 0 18.7338 0C21.6057 0 23.942 2.3365 23.942 5.2084C23.942 8.08029 21.6057 10.4166 18.7338 10.4166ZM18.7338 1.5625C16.7233 1.5625 15.0879 3.19786 15.0879 5.2084C15.0879 7.21874 16.7233 8.8541 18.7338 8.8541C20.7441 8.8541 22.3795 7.21874 22.3795 5.2084C22.3795 3.19786 20.7441 1.5625 18.7338 1.5625Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
    </svg>
  </SvgIcon>
);

export const DotsIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="25"
      viewBox="0 0 26 25"
      fill="none"
    >
      <path
        d="M9.44069 3.28745C8.51732 3.07085 7.55635 3.07085 6.63297 3.28745C5.42361 3.57112 4.47933 4.5154 4.19565 5.72477C3.97905 6.64814 3.97905 7.60912 4.19565 8.53249C4.47933 9.74186 5.42361 10.6861 6.63297 10.9698C7.55635 11.1864 8.51732 11.1864 9.44069 10.9698C10.6501 10.6861 11.5943 9.74185 11.878 8.53249C12.0946 7.60912 12.0946 6.64815 11.878 5.72477C11.5943 4.51541 10.6501 3.57112 9.44069 3.28745Z"
        fill="#2E2E2E"
      />
      <path
        d="M9.44069 14.0302C8.51732 13.8136 7.55635 13.8136 6.63297 14.0302C5.42361 14.3139 4.47933 15.2581 4.19565 16.4675C3.97905 17.3909 3.97905 18.3519 4.19565 19.2752C4.47933 20.4846 5.42361 21.4289 6.63297 21.7126C7.55635 21.9291 8.51732 21.9291 9.44069 21.7126C10.6501 21.4289 11.5943 20.4846 11.878 19.2752C12.0946 18.3519 12.0946 17.3909 11.878 16.4675C11.5943 15.2581 10.6501 14.3139 9.44069 14.0302Z"
        fill="#2E2E2E"
      />
      <path
        d="M20.1834 3.28745C19.2601 3.07085 18.2991 3.07085 17.3757 3.28745C16.1663 3.57112 15.2221 4.5154 14.9384 5.72477C14.7218 6.64814 14.7218 7.60912 14.9384 8.53249C15.2221 9.74186 16.1664 10.6861 17.3757 10.9698C18.2991 11.1864 19.2601 11.1864 20.1834 10.9698C21.3928 10.6861 22.3371 9.74185 22.6208 8.53249C22.8374 7.60912 22.8374 6.64815 22.6208 5.72477C22.3371 4.51541 21.3928 3.57112 20.1834 3.28745Z"
        fill="#2E2E2E"
      />
      <path
        d="M20.1834 14.0302C19.2601 13.8136 18.2991 13.8136 17.3757 14.0302C16.1663 14.3139 15.2221 15.2581 14.9384 16.4675C14.7218 17.3909 14.7218 18.3519 14.9384 19.2752C15.2221 20.4846 16.1664 21.4289 17.3757 21.7126C18.2991 21.9291 19.2601 21.9291 20.1834 21.7126C21.3928 21.4289 22.3371 20.4846 22.6208 19.2752C22.8374 18.3519 22.8374 17.3909 22.6208 16.4675C22.3371 15.2581 21.3928 14.3139 20.1834 14.0302Z"
        fill="#2E2E2E"
      />
    </svg>
  </SvgIcon>
);

export const ProfileEditIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="15" cy="15" r="15" fill="var(--primary)" />
      <g clipPath="url(#clip0_6513_11733)">
        <path
          d="M22.5872 11.1628L21.2366 12.5134C21.0989 12.6511 20.8763 12.6511 20.7386 12.5134L17.4866 9.26147C17.3489 9.12378 17.3489 8.90112 17.4866 8.76343L18.8372 7.41284C19.385 6.86499 20.2757 6.86499 20.8265 7.41284L22.5872 9.17358C23.138 9.72144 23.138 10.6121 22.5872 11.1628ZM16.3265 9.92358L8.63309 17.6169L8.012 21.1765C7.92704 21.657 8.34599 22.073 8.82645 21.991L12.386 21.3669L20.0794 13.6736C20.2171 13.5359 20.2171 13.3132 20.0794 13.1755L16.8274 9.92358C16.6868 9.78589 16.4642 9.78589 16.3265 9.92358ZM11.636 16.9578C11.4749 16.7966 11.4749 16.5388 11.636 16.3777L16.1477 11.866C16.3089 11.7048 16.5667 11.7048 16.7278 11.866C16.889 12.0271 16.889 12.2849 16.7278 12.446L12.2161 16.9578C12.055 17.1189 11.7972 17.1189 11.636 16.9578ZM10.5784 19.4216H11.9847V20.4851L10.095 20.8162L9.18388 19.905L9.51493 18.0154H10.5784V19.4216Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_6513_11733">
          <rect
            width="15"
            height="15"
            fill="white"
            transform="translate(8 7)"
          />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const FolderIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
    >
      <path
        d="M21.8734 7.29158H30.4015C30.9968 7.29151 31.5855 7.41551 32.1301 7.65564C32.6747 7.89578 33.1633 8.24679 33.5647 8.68628C33.9661 9.12578 34.2715 9.64411 34.4615 10.2082C34.6514 10.7723 34.7216 11.3698 34.6678 11.9626L33.3035 26.9544C33.2068 28.0186 32.7158 29.0083 31.9269 29.729C31.1379 30.4498 30.1081 30.8497 29.0394 30.8501H6.87515C5.80653 30.8497 4.77666 30.4498 3.98773 29.729C3.19879 29.0083 2.70777 28.0186 2.61106 26.9544L1.24681 11.9626C1.15736 10.9707 1.41637 9.97859 1.97926 9.15698L1.89574 7.29158C1.89574 6.15556 2.34702 5.06607 3.15031 4.26278C3.95359 3.45949 5.04308 3.00821 6.1791 3.00821H14.0434C15.1793 3.00845 16.2686 3.45989 17.0717 4.26324L18.845 6.03655C19.6481 6.83989 20.7374 7.29133 21.8734 7.29158ZM4.05027 7.54858C4.51002 7.37867 4.99832 7.293 5.51518 7.29158H17.0717L15.5575 5.77741C15.156 5.37573 14.6113 5.15001 14.0434 5.14989H6.1791C5.61807 5.14979 5.07941 5.36984 4.67891 5.76272C4.27842 6.15561 4.04808 6.68996 4.03742 7.25088L4.05027 7.54858Z"
        fill={props.fill || '#718EBF'}
      />
    </svg>
  </SvgIcon>
);

export const StrokeFolderIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M0.573625 4.11187L0.531125 3.1875C0.531125 2.62391 0.755008 2.08341 1.15352 1.6849C1.55204 1.28638 2.09254 1.0625 2.65613 1.0625H6.55763C7.12116 1.06262 7.66158 1.28658 8.06 1.68512L8.93975 2.56488C9.33817 2.96342 9.87859 3.18738 10.4421 3.1875H14.673C14.9683 3.18747 15.2603 3.24898 15.5305 3.36812C15.8007 3.48725 16.0431 3.66139 16.2423 3.87942C16.4414 4.09746 16.5929 4.3546 16.6871 4.63446C16.7814 4.91431 16.8162 5.21073 16.7895 5.50481L16.1127 12.9423C16.0647 13.4703 15.8211 13.9613 15.4297 14.3188C15.0383 14.6764 14.5274 14.8748 13.9972 14.875H3.0025C2.47235 14.8748 1.96143 14.6764 1.57003 14.3188C1.17864 13.9613 0.935041 13.4703 0.887063 12.9423L0.21025 5.50481C0.165877 5.01272 0.294369 4.52054 0.573625 4.11294V4.11187ZM2.32675 4.25C2.17915 4.24999 2.03317 4.28073 1.89812 4.34027C1.76306 4.3998 1.64189 4.48682 1.54233 4.59578C1.44277 4.70474 1.367 4.83325 1.31985 4.97311C1.2727 5.11298 1.25522 5.26113 1.2685 5.40812L1.94531 12.8456C1.96917 13.1096 2.09084 13.3552 2.28644 13.534C2.48203 13.7129 2.73743 13.8123 3.0025 13.8125H13.9972C14.2623 13.8123 14.5177 13.7129 14.7133 13.534C14.9089 13.3552 15.0306 13.1096 15.0544 12.8456L15.7312 5.40812C15.7445 5.26113 15.727 5.11298 15.6799 4.97311C15.6328 4.83325 15.557 4.70474 15.4574 4.59578C15.3579 4.48682 15.2367 4.3998 15.1016 4.34027C14.9666 4.28073 14.8206 4.24999 14.673 4.25H2.32675ZM7.30988 2.43631C7.21111 2.33752 7.09383 2.25917 6.96475 2.20575C6.83567 2.15233 6.69732 2.12489 6.55763 2.125H2.65613C2.3778 2.12495 2.11056 2.23412 1.91187 2.42903C1.71319 2.62394 1.59891 2.88903 1.59363 3.16731L1.6 3.315C1.82879 3.23071 2.07104 3.18821 2.32675 3.1875H8.06L7.30988 2.43631Z"
        fill="#0052B4"
      />
    </svg>
  </SvgIcon>
);

export const SortIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        d="M9.75 14.7503V6.37281L13.41 10.0328C13.7025 10.3253 14.1825 10.3253 14.475 10.0328C14.5445 9.96342 14.5997 9.88101 14.6373 9.79028C14.675 9.69955 14.6943 9.60229 14.6943 9.50406C14.6943 9.40583 14.675 9.30857 14.6373 9.21784C14.5997 9.12711 14.5445 9.04469 14.475 8.97531L9.5325 4.03281C9.46312 3.96328 9.3807 3.90812 9.28997 3.87049C9.19924 3.83285 9.10198 3.81348 9.00375 3.81348C8.90553 3.81348 8.80826 3.83285 8.71753 3.87049C8.6268 3.90812 8.54439 3.96328 8.475 4.03281L3.5325 8.97531C3.46307 9.04475 3.40799 9.12718 3.37041 9.2179C3.33283 9.30863 3.31349 9.40586 3.31349 9.50406C3.31349 9.60226 3.33283 9.69949 3.37041 9.79022C3.40799 9.88094 3.46307 9.96337 3.5325 10.0328C3.60194 10.1022 3.68437 10.1573 3.77509 10.1949C3.86582 10.2325 3.96306 10.2518 4.06125 10.2518C4.15945 10.2518 4.25669 10.2325 4.34741 10.1949C4.43813 10.1573 4.52057 10.1022 4.59 10.0328L8.25 6.37281V14.7503C8.25 15.1628 8.5875 15.5003 9 15.5003C9.4125 15.5003 9.75 15.1628 9.75 14.7503Z"
        fill="#718EBF"
      />
    </svg>
  </SvgIcon>
);

export const AddMediaIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="202"
      height="201"
      viewBox="0 0 202 201"
      fill="none"
    >
      <path
        d="M101.041 200.345C156.364 200.345 201.213 155.496 201.213 100.172C201.213 44.8487 156.364 0 101.041 0C45.7169 0 0.868164 44.8487 0.868164 100.172C0.868164 155.496 45.7169 200.345 101.041 200.345Z"
        fill="#DCFAF8"
      />
      <path
        d="M79.9998 100.608H122.218M101.109 121.717V79.499"
        stroke="#16DBCC"
        strokeWidth="5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const UploadMediaIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
    >
      <g clipPath="url(#clip0_1970_7328)">
        <path
          d="M33.4417 3.1207H14.1743V11.1107H37.5567V7.23412C37.5567 4.96576 35.7107 3.1207 33.4417 3.1207Z"
          fill="#CED9F9"
        />
        <path
          d="M22.5352 12.3403H0V4.92636C0 2.20972 2.21068 0 4.92828 0H12.1336C12.8497 0 13.5396 0.150925 14.1664 0.434509C15.0418 0.828964 15.7939 1.47913 16.3213 2.3286L22.5352 12.3403Z"
          // fill="#7136ED"
          fill={'var(--primary)'}
        />
        <path
          d="M42 14.0001V37.8814C42 40.1527 40.1511 42 37.8789 42H4.12111C1.84891 42 0 40.1527 0 37.8814V9.88058H37.8789C40.1511 9.88058 42 11.7285 42 14.0001Z"
          // fill="#9665FC"
          fill={'var(--primary-700)'}
        />
        <path
          d="M42 14.0001V37.8814C42 40.1527 40.1511 42 37.8789 42H21V9.88058H37.8789C40.1511 9.88058 42 11.7285 42 14.0001Z"
          // fill="#8E59FE"
          fill={'var(--primary-800)'}
        />
        <path
          d="M32.048 25.9398C32.048 32.0322 27.0919 36.9887 21.0001 36.9887C14.9083 36.9887 9.95215 32.0322 9.95215 25.9398C9.95215 19.8484 14.9083 14.8919 21.0001 14.8919C27.0919 14.8919 32.048 19.8484 32.048 25.9398Z"
          fill="#E7ECFC"
        />
        <path
          d="M32.0479 25.9398C32.0479 32.0322 27.0918 36.9887 21 36.9887V14.8919C27.0918 14.8919 32.0479 19.8484 32.0479 25.9398Z"
          fill="#CED9F9"
        />
        <path
          d="M24.5612 26.0754C24.3308 26.2705 24.0485 26.3657 23.7688 26.3657C23.4185 26.3657 23.0705 26.2173 22.827 25.9283L22.2307 25.2214V29.8494C22.2307 30.5288 21.6795 31.0799 21.0002 31.0799C20.3209 31.0799 19.7698 30.5288 19.7698 29.8494V25.2214L19.1734 25.9283C18.7344 26.4477 17.9587 26.514 17.4392 26.0754C16.9201 25.6373 16.8535 24.8612 17.2915 24.3418L19.7271 21.4544C20.0447 21.0788 20.508 20.8629 21.0002 20.8629C21.4924 20.8629 21.9558 21.0788 22.2733 21.4544L24.7089 24.3418C25.147 24.8612 25.0803 25.6373 24.5612 26.0754Z"
          // fill="#8045FB"
          fill={'var(--primary)'}
        />
        <path
          d="M24.561 26.0754C24.3306 26.2705 24.0483 26.3657 23.7686 26.3657C23.4183 26.3657 23.0703 26.2173 22.8268 25.9283L22.2305 25.2214V29.8494C22.2305 30.5288 21.6793 31.0799 21 31.0799V20.8629C21.4922 20.8629 21.9555 21.0788 22.2731 21.4544L24.7087 24.3418C25.1467 24.8612 25.0801 25.6373 24.561 26.0754Z"
          // fill="#7136ED"
          fill={'var(--primary)'}
        />
      </g>
      <defs>
        <clipPath id="clip0_1970_7328">
          <rect width="42" height="42" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const FileIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="35"
      height="44"
      viewBox="0 0 35 44"
      fill="none"
    >
      <path
        d="M17.4393 0.438995L17.6928 0.454162C18.1764 0.511197 18.6267 0.7295 18.9711 1.07385C19.3155 1.41821 19.5338 1.86853 19.5908 2.35216L19.606 2.60566V11.2723L19.6168 11.5973C19.6943 12.629 20.1384 13.599 20.8688 14.3317C21.5992 15.0645 22.5677 15.5118 23.5991 15.5927L23.9393 15.6057H32.606L32.8595 15.6208C33.3431 15.6779 33.7934 15.8962 34.1378 16.2405C34.4821 16.5849 34.7004 17.0352 34.7575 17.5188L34.7726 17.7723V37.2723C34.7727 38.9303 34.1393 40.5256 33.0018 41.7319C31.8644 42.9382 30.3091 43.6642 28.654 43.7615L28.2726 43.7723H6.60596C4.948 43.7724 3.35267 43.139 2.14639 42.0015C0.940109 40.8641 0.214057 39.3088 0.116791 37.6537L0.105957 37.2723V6.93899C0.105865 5.28104 0.739328 3.68571 1.87674 2.47943C3.01415 1.27315 4.56952 0.547096 6.22462 0.449829L6.60596 0.438995H17.4393Z"
        fill={props.fill || '#DBCBFF'}
      />
      <path
        d="M32.6058 11.2724H23.9392L23.937 2.60355L32.6058 11.2724Z"
        fill={props.fill || '#DBCBFF'}
      />
    </svg>
  </SvgIcon>
);

export const FolderMinimizeIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
    >
      <path
        d="M1.5083 7.16702L1.45022 5.90385C1.45022 5.1337 1.75616 4.39509 2.30074 3.85052C2.84531 3.30594 3.58392 3 4.35407 3H9.68553C10.4556 3.00016 11.1941 3.30621 11.7385 3.85083L12.9407 5.05302C13.4852 5.59763 14.2237 5.90368 14.9938 5.90385H20.7753C21.1788 5.9038 21.5779 5.98786 21.9472 6.15066C22.3164 6.31346 22.6476 6.55142 22.9197 6.84937C23.1919 7.14732 23.3989 7.49871 23.5277 7.88114C23.6564 8.26356 23.7041 8.66863 23.6675 9.07049L22.7427 19.234C22.6771 19.9554 22.3442 20.6264 21.8094 21.115C21.2745 21.6036 20.5763 21.8747 19.8519 21.875H4.82739C4.10294 21.8747 3.40475 21.6036 2.86991 21.115C2.33506 20.6264 2.00218 19.9554 1.93661 19.234L1.01174 9.07049C0.951102 8.39804 1.12669 7.72547 1.5083 7.16847V7.16702ZM3.90397 7.35577C3.70228 7.35576 3.50279 7.39777 3.31824 7.47912C3.13368 7.56048 2.9681 7.67939 2.83204 7.82829C2.69599 7.97718 2.59245 8.15279 2.52803 8.34391C2.4636 8.53504 2.4397 8.73749 2.45785 8.93837L3.38273 19.1018C3.41533 19.4626 3.58159 19.7981 3.84888 20.0426C4.11617 20.287 4.46518 20.4227 4.82739 20.4231H19.8519C20.2141 20.4227 20.5631 20.287 20.8304 20.0426C21.0977 19.7981 21.264 19.4626 21.2966 19.1018L22.2214 8.93837C22.2396 8.73749 22.2157 8.53504 22.1513 8.34391C22.0868 8.15279 21.9833 7.97718 21.8472 7.82829C21.7112 7.67939 21.5456 7.56048 21.361 7.47912C21.1765 7.39777 20.977 7.35576 20.7753 7.35577H3.90397ZM10.7135 4.87734C10.5785 4.74233 10.4183 4.63527 10.2419 4.56227C10.0655 4.48927 9.87643 4.45177 9.68553 4.45192H4.35407C3.97372 4.45185 3.60854 4.60103 3.33703 4.86738C3.06552 5.13373 2.90937 5.49599 2.90214 5.87626L2.91085 6.07808C3.2235 5.96289 3.55454 5.90481 3.90397 5.90385H11.7385L10.7135 4.87734Z"
        fill="#2E2E2E"
      />
      <path
        d="M15.1791 15.5712C15.0212 15.7049 14.8278 15.7701 14.6361 15.7701C14.3962 15.7701 14.1578 15.6685 13.9909 15.4705L13.5824 14.9862V18.157C13.5824 18.6224 13.2048 19 12.7393 19C12.2739 19 11.8963 18.6224 11.8963 18.157V14.9862L11.4877 15.4705C11.187 15.8263 10.6555 15.8718 10.2996 15.5712C9.94394 15.2711 9.89828 14.7394 10.1984 14.3835L11.8671 12.4053C12.0847 12.148 12.4021 12 12.7393 12C13.0765 12 13.394 12.148 13.6116 12.4053L15.2803 14.3835C15.5804 14.7394 15.5347 15.2711 15.1791 15.5712Z"
        fill="#2E2E2E"
        stroke="#F8F9FD"
        strokeWidth="0.5"
      />
    </svg>
  </SvgIcon>
);

export const ConfigurationIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M6.18359 15.2285C7.09946 13.6538 9.19542 13.4682 10.389 14.671C10.656 14.9402 10.7899 15.0747 10.9082 15.091C11.0265 15.1073 11.7688 14.6809 12.0323 14.53C12.3015 14.3756 13.0466 13.9485 13.0934 13.8366C13.1401 13.7247 13.0913 13.5341 12.9928 13.153C12.6365 11.7668 13.5014 10.3077 14.8826 9.93154C15.2524 9.83096 15.4373 9.78067 15.5102 9.68362C15.5832 9.588 15.5832 8.74154 15.5832 8.43129C15.5832 8.12104 15.5832 7.27529 15.5102 7.17896C15.4373 7.08263 15.2524 7.03233 14.8826 6.93104C13.5014 6.55562 12.6358 5.09646 12.9914 3.71025C13.0905 3.32846 13.1387 3.13792 13.092 3.026C13.0452 2.91408 12.3 2.48767 12.0316 2.33325C11.7681 2.18167 11.0258 1.75596 10.9068 1.77225C10.7885 1.78854 10.6553 1.92242 10.3883 2.19158C9.88695 2.69182 9.20768 2.97277 8.49948 2.97277C7.79129 2.97277 7.11201 2.69182 6.61071 2.19158C6.34367 1.92242 6.2098 1.78783 6.0915 1.77225C5.97321 1.75667 5.23088 2.18167 4.96738 2.33325C4.69821 2.48696 3.95305 2.91408 3.9063 3.02671C3.85955 3.13792 3.90842 3.32917 4.00688 3.71025C4.36317 5.09646 3.4983 6.55563 2.11705 6.93175C1.7473 7.03233 1.56242 7.08263 1.48946 7.17896C1.4165 7.27529 1.4165 8.12175 1.4165 8.43129C1.4165 8.74083 1.4165 9.588 1.48946 9.68433C1.56242 9.78067 1.7473 9.83096 2.11705 9.93154L2.1298 9.93508"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.76282 12.8796C2.52782 12.1146 5.12881 9.54054 5.38381 9.24304C5.65298 8.92854 5.43481 8.50354 5.56515 7.18675C5.62748 6.54925 5.7649 6.07183 6.15731 5.71696C6.62481 5.27496 7.00731 5.27496 8.32481 5.24521C9.47231 5.27496 9.60831 5.14746 9.72731 5.44496C9.81231 5.65746 9.55731 5.78496 9.25131 6.12496C8.57131 6.80496 8.17181 7.14425 8.13356 7.35675C7.85731 8.29175 8.94531 8.84354 9.54031 8.24925C9.76556 8.024 10.8068 6.97425 10.9088 6.88925C10.9853 6.82125 11.1681 6.82479 11.2573 6.93175C11.3338 7.00683 11.3423 7.01675 11.3338 7.35675C11.326 7.67125 11.3296 8.12317 11.3303 8.58925C11.3317 9.19204 11.2998 9.86283 11.0448 10.2028C10.5348 10.9678 9.68481 11.0103 8.91981 11.0443C8.19731 11.0868 7.60231 11.0103 7.41531 11.1463C7.26231 11.2228 6.45481 12.0721 5.47731 13.0489L3.73481 14.7914C2.28981 15.9382 0.700315 14.1539 1.76282 12.8789"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const AttachReleaseIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M2.83325 11.3362V12.0417C2.83325 12.6053 3.05714 13.1458 3.45565 13.5443C3.85416 13.9428 4.39467 14.1667 4.95825 14.1667H12.0416C12.6052 14.1667 13.1457 13.9428 13.5442 13.5443C13.9427 13.1458 14.1666 12.6053 14.1666 12.0417V11.3333M8.49992 10.9792V3.1875M8.49992 3.1875L10.9791 5.66667M8.49992 3.1875L6.02075 5.66667"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const PublishIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M8.85425 14.1667H2.47925C2.19746 14.1667 1.9272 14.0547 1.72795 13.8555C1.52869 13.6562 1.41675 13.386 1.41675 13.1042V3.89583C1.41675 3.61404 1.52869 3.34378 1.72795 3.14453C1.9272 2.94527 2.19746 2.83333 2.47925 2.83333H14.5209C14.8027 2.83333 15.073 2.94527 15.2722 3.14453C15.4715 3.34378 15.5834 3.61404 15.5834 3.89583V8.83327"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.41675 3.89583C1.41675 3.61404 1.52869 3.34378 1.72795 3.14453C1.9272 2.94527 2.19746 2.83333 2.47925 2.83333H14.5209C14.8027 2.83333 15.073 2.94527 15.2722 3.14453C15.4715 3.34378 15.5834 3.61404 15.5834 3.89583V7.08333H1.41675V3.89583Z"
        stroke="#727475"
        strokeWidth="1.25"
      />
      <path
        d="M11.3335 12.3958H15.5835M13.4585 10.2708V14.5208"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.83333 4.95832C2.83333 4.56712 3.15047 4.24999 3.54167 4.24999C3.93287 4.24999 4.25 4.56712 4.25 4.95832C4.25 5.34952 3.93287 5.66666 3.54167 5.66666C3.15047 5.66666 2.83333 5.34952 2.83333 4.95832Z"
        fill="#727475"
      />
      <path
        d="M4.95833 4.95832C4.95833 4.56712 5.27547 4.24999 5.66667 4.24999C6.05787 4.24999 6.375 4.56712 6.375 4.95832C6.375 5.34952 6.05787 5.66666 5.66667 5.66666C5.27547 5.66666 4.95833 5.34952 4.95833 4.95832Z"
        fill="#727475"
      />
    </svg>
  </SvgIcon>
);

export const UnpublishIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M8.85425 14.1667H2.47925C2.19746 14.1667 1.9272 14.0547 1.72795 13.8555C1.52869 13.6562 1.41675 13.386 1.41675 13.1042V3.89584C1.41675 3.61404 1.52869 3.34379 1.72795 3.14453C1.9272 2.94528 2.19746 2.83334 2.47925 2.83334H14.5209C14.8027 2.83334 15.073 2.94528 15.2722 3.14453C15.4715 3.34379 15.5834 3.61404 15.5834 3.89584V8.16673"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.4585 14.5208C14.6321 14.5208 15.5835 13.5694 15.5835 12.3958C15.5835 11.2222 14.6321 10.2708 13.4585 10.2708C12.2849 10.2708 11.3335 11.2222 11.3335 12.3958C11.3335 13.5694 12.2849 14.5208 13.4585 14.5208Z"
        stroke="#727475"
        strokeWidth="1.25"
      />
      <path
        d="M12.0417 10.9792L14.8751 13.8125"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.41675 3.89584C1.41675 3.61404 1.52869 3.34379 1.72795 3.14453C1.9272 2.94528 2.19746 2.83334 2.47925 2.83334H14.5209C14.8027 2.83334 15.073 2.94528 15.2722 3.14453C15.4715 3.34379 15.5834 3.61404 15.5834 3.89584V7.08334H1.41675V3.89584Z"
        stroke="#727475"
        strokeWidth="1.25"
      />
      <path
        d="M2.83333 4.95834C2.83333 4.56714 3.15047 4.25001 3.54167 4.25001C3.93287 4.25001 4.25 4.56714 4.25 4.95834C4.25 5.34954 3.93287 5.66667 3.54167 5.66667C3.15047 5.66667 2.83333 5.34954 2.83333 4.95834Z"
        fill="#727475"
      />
      <path
        d="M4.95833 4.95834C4.95833 4.56714 5.27547 4.25001 5.66667 4.25001C6.05787 4.25001 6.375 4.56714 6.375 4.95834C6.375 5.34954 6.05787 5.66667 5.66667 5.66667C5.27547 5.66667 4.95833 5.34954 4.95833 4.95834Z"
        fill="#727475"
      />
    </svg>
  </SvgIcon>
);

export const BurgerMenuIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 16 11">
    <svg
      width="16"
      height="11"
      viewBox="0 0 16 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.0884 0.675781H0.675537M15.0884 5.17982H0.675537M15.0884 9.68385H0.675537"
        stroke="currentColor"
        strokeWidth="1.35121"
        strokeLinecap="round"
      />
    </svg>
  </SvgIcon>
);

export const BrandingNotificationIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 18 18">
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.07882 5.74642L3.55474 5.60505L3.55474 5.60505L4.07882 5.74642ZM7.25924 2.40434L7.42729 2.92048V2.92048L7.25924 2.40434ZM4.0392 5.89329L4.56327 6.03466L4.56327 6.03466L4.0392 5.89329ZM3.89025 9.03984L3.35516 9.13106L3.35516 9.13106L3.89025 9.03984ZM3.90756 9.14138L3.37247 9.23259H3.37247L3.90756 9.14138ZM3.93945 9.30427L4.15589 9.80206C4.39414 9.69847 4.52558 9.44059 4.46942 9.18694L3.93945 9.30427ZM4.28091 12.6046L4.17209 13.1364H4.1721L4.28091 12.6046ZM4.55279 12.6602L4.66161 12.1284L4.66161 12.1284L4.55279 12.6602ZM12.8182 12.6602L12.7093 12.1284V12.1284L12.8182 12.6602ZM13.09 12.6046L13.1989 13.1364V13.1364L13.09 12.6046ZM13.4306 9.30387L12.9012 9.18369C12.8435 9.43822 12.975 9.69785 13.2144 9.80179L13.4306 9.30387ZM13.4667 9.12186L14.0018 9.21307L14.0018 9.21307L13.4667 9.12186ZM13.4851 9.01425L12.95 8.92304V8.92304L13.4851 9.01425ZM13.3508 5.91508L12.8256 6.05226V6.05226L13.3508 5.91508ZM13.2978 5.71225L13.823 5.57507V5.57507L13.2978 5.71225ZM10.2085 2.40911L10.3794 1.89391L10.3794 1.89391L10.2085 2.40911ZM10.7158 13.4968L10.1854 13.3813V13.3813L10.7158 13.4968ZM10.6633 13.7377L11.1937 13.8532V13.8532L10.6633 13.7377ZM6.70762 13.7377L7.238 13.6222V13.6222L6.70762 13.7377ZM6.65516 13.4968L6.12478 13.6123V13.6123L6.65516 13.4968ZM9.32598 15.1061L9.19971 14.5782L9.19971 14.5782L9.32598 15.1061ZM9.18417 15.14L9.31044 15.6679H9.31044L9.18417 15.14ZM8.18678 15.14L8.31305 14.6121H8.31305L8.18678 15.14ZM8.04497 15.1061L7.9187 15.634H7.91871L8.04497 15.1061ZM7.3578 12.7075C7.5547 12.4815 7.53106 12.1386 7.305 11.9417C7.07894 11.7448 6.73606 11.7685 6.53917 11.9945L6.94849 12.351L7.3578 12.7075ZM10.8318 11.9945C10.6349 11.7685 10.292 11.7448 10.066 11.9417C9.8399 12.1386 9.81625 12.4815 10.0131 12.7075L10.4225 12.351L10.8318 11.9945ZM4.07882 5.74642L4.60289 5.88779C4.98031 4.48868 6.0526 3.36806 7.42729 2.92048L7.25924 2.40434L7.09119 1.8882C5.37048 2.44845 4.02841 3.84913 3.55474 5.60505L4.07882 5.74642ZM4.0392 5.89329L4.56327 6.03466L4.60289 5.88779L4.07882 5.74642L3.55474 5.60505L3.51512 5.75192L4.0392 5.89329ZM3.89025 9.03984L4.42534 8.94863C4.26001 7.97877 4.30709 6.98435 4.56327 6.03466L4.0392 5.89329L3.51512 5.75192C3.21803 6.85327 3.16345 8.00639 3.35516 9.13106L3.89025 9.03984ZM3.90756 9.14138L4.44265 9.05017L4.42534 8.94863L3.89025 9.03984L3.35516 9.13106L3.37247 9.23259L3.90756 9.14138ZM3.93945 9.30427L4.46942 9.18694C4.45943 9.1418 4.45049 9.0962 4.44265 9.05017L3.90756 9.14138L3.37247 9.23259C3.3833 9.29614 3.39565 9.35916 3.40947 9.42161L3.93945 9.30427ZM2.89551 10.9008H3.43832C3.43832 10.4083 3.73378 9.9856 4.15589 9.80206L3.93945 9.30427L3.723 8.80649C2.91671 9.15708 2.3527 9.96297 2.3527 10.9008H2.89551ZM4.28091 12.6046L4.38973 12.0728C3.83776 11.9598 3.43832 11.4711 3.43832 10.9008H2.89551H2.3527C2.3527 11.9835 3.11164 12.9194 4.17209 13.1364L4.28091 12.6046ZM4.55279 12.6602L4.66161 12.1284L4.38973 12.0728L4.28091 12.6046L4.1721 13.1364L4.44398 13.192L4.55279 12.6602ZM12.8182 12.6602L12.7093 12.1284C10.054 12.6718 7.31693 12.6718 4.66161 12.1284L4.55279 12.6602L4.44398 13.192C7.2429 13.7647 10.1281 13.7647 12.927 13.192L12.8182 12.6602ZM13.09 12.6046L12.9812 12.0728L12.7093 12.1284L12.8182 12.6602L12.927 13.192L13.1989 13.1364L13.09 12.6046ZM14.4754 10.9008H13.9326C13.9326 11.4711 13.5332 11.9598 12.9812 12.0728L13.09 12.6046L13.1989 13.1364C14.2593 12.9194 15.0183 11.9835 15.0183 10.9008H14.4754ZM13.4306 9.30387L13.2144 9.80179C13.6369 9.98518 13.9326 10.4081 13.9326 10.9008H14.4754H15.0183C15.0183 9.9625 14.4537 9.15627 13.6467 8.80596L13.4306 9.30387ZM13.4667 9.12186L12.9316 9.03065C12.9228 9.08221 12.9127 9.13324 12.9012 9.18369L13.4306 9.30387L13.9599 9.42406C13.9757 9.35443 13.9897 9.28408 14.0018 9.21307L13.4667 9.12186ZM13.4851 9.01425L12.95 8.92304L12.9316 9.03065L13.4667 9.12186L14.0018 9.21307L14.0202 9.10546L13.4851 9.01425ZM13.3508 5.91508L12.8256 6.05226C13.0703 6.9893 13.1128 7.96813 12.95 8.92304L13.4851 9.01425L14.0202 9.10546C14.2088 7.99864 14.1597 6.86409 13.8759 5.7779L13.3508 5.91508ZM13.2978 5.71225L12.7726 5.84943L12.8256 6.05226L13.3508 5.91508L13.8759 5.7779L13.823 5.57507L13.2978 5.71225ZM10.2085 2.40911L10.0376 2.92431C11.3825 3.37046 12.4125 4.47083 12.7726 5.84943L13.2978 5.71225L13.823 5.57507C13.3704 3.84239 12.0749 2.45637 10.3794 1.89391L10.2085 2.40911ZM7.25924 2.40434L7.42729 2.92048C8.27394 2.64482 9.19493 2.64478 10.0376 2.92431L10.2085 2.40911L10.3794 1.89391C9.31431 1.54059 8.15575 1.54159 7.09119 1.8882L7.25924 2.40434ZM10.7158 13.4968L10.1854 13.3813L10.133 13.6222L10.6633 13.7377L11.1937 13.8532L11.2462 13.6123L10.7158 13.4968ZM6.70762 13.7377L7.238 13.6222L7.18554 13.3813L6.65516 13.4968L6.12478 13.6123L6.17724 13.8532L6.70762 13.7377ZM9.32598 15.1061L9.19971 14.5782L9.05791 14.6121L9.18417 15.14L9.31044 15.6679L9.45225 15.634L9.32598 15.1061ZM8.18678 15.14L8.31305 14.6121L8.17124 14.5782L8.04497 15.1061L7.91871 15.634L8.06051 15.6679L8.18678 15.14ZM6.65516 13.4968L7.18554 13.3813C7.13333 13.1415 7.1976 12.8915 7.3578 12.7075L6.94849 12.351L6.53917 11.9945C6.15314 12.4377 5.99973 13.038 6.12478 13.6123L6.65516 13.4968ZM10.7158 13.4968L11.2462 13.6123C11.3712 13.038 11.2178 12.4377 10.8318 11.9945L10.4225 12.351L10.0131 12.7075C10.1734 12.8915 10.2376 13.1415 10.1854 13.3813L10.7158 13.4968ZM9.18417 15.14L9.05791 14.6121C8.81303 14.6707 8.55792 14.6707 8.31305 14.6121L8.18678 15.14L8.06051 15.6679C8.4714 15.7662 8.89955 15.7662 9.31044 15.6679L9.18417 15.14ZM6.70762 13.7377L6.17724 13.8532C6.36861 14.732 7.04456 15.4249 7.9187 15.634L8.04497 15.1061L8.17124 14.5782C7.70468 14.4666 7.34114 14.0959 7.238 13.6222L6.70762 13.7377ZM10.6633 13.7377L10.133 13.6222C10.0298 14.0959 9.66628 14.4666 9.19971 14.5782L9.32598 15.1061L9.45225 15.634C10.3264 15.4249 11.0023 14.732 11.1937 13.8532L10.6633 13.7377Z"
        fill="currentColor"
      />
    </svg>
  </SvgIcon>
);

export const ConnectPmsIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="13"
      height="14"
      viewBox="0 0 13 14"
      fill="none"
    >
      <path
        d="M0.535004 7.73516V10.3752C0.535004 10.4706 0.573635 10.5622 0.642398 10.6297C0.711161 10.6972 0.804424 10.7352 0.90167 10.7352H4.20167M9.70167 7.73516V10.3752C9.70167 10.4706 9.66304 10.5622 9.59428 10.6297C9.52551 10.6972 9.43225 10.7352 9.335 10.7352H6.64611M6.64611 2.33516H9.335C9.43225 2.33516 9.52551 2.37308 9.59428 2.4406C9.66304 2.50811 9.70167 2.59968 9.70167 2.69516V5.33516M0.535004 5.33516V2.69516C0.535004 2.59968 0.573635 2.50811 0.642398 2.4406C0.711161 2.37308 0.804424 2.33516 0.90167 2.33516H4.20167M6.64611 10.7352V11.3352C6.64611 11.6534 6.51734 11.9586 6.28813 12.1837C6.05892 12.4087 5.74805 12.5352 5.42389 12.5352C5.09974 12.5352 4.78886 12.4087 4.55965 12.1837C4.33044 11.9586 4.20167 11.6534 4.20167 11.3352V10.7352M0.535004 5.33516H1.14611C1.47027 5.33516 1.78115 5.46158 2.01036 5.68663C2.23957 5.91167 2.36834 6.2169 2.36834 6.53516C2.36834 6.85342 2.23957 7.15864 2.01036 7.38368C1.78115 7.60873 1.47027 7.73516 1.14611 7.73516H0.535004M9.70167 5.33516H10.3128C10.6369 5.33516 10.9478 5.46158 11.177 5.68663C11.4062 5.91167 11.535 6.2169 11.535 6.53516C11.535 6.85342 11.4062 7.15864 11.177 7.38368C10.9478 7.60873 10.6369 7.73516 10.3128 7.73516H9.70167M6.64611 2.33516V1.73516C6.64611 1.4169 6.51734 1.11167 6.28813 0.886628C6.05892 0.661584 5.74805 0.535156 5.42389 0.535156C5.09974 0.535156 4.78886 0.661584 4.55965 0.886628C4.33044 1.11167 4.20167 1.4169 4.20167 1.73516V2.33516"
        stroke="#727475"
        strokeWidth="1.07"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const MoreVerticalIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="3"
      height="11"
      viewBox="0 0 3 11"
      fill="none"
    >
      <path
        d="M0 8.94221C0 8.60339 0.134595 8.27845 0.374175 8.03887C0.613756 7.79929 0.938697 7.6647 1.27751 7.6647C1.61633 7.6647 1.94127 7.79929 2.18085 8.03887C2.42043 8.27845 2.55503 8.60339 2.55503 8.94221C2.55503 9.28103 2.42043 9.60597 2.18085 9.84555C1.94127 10.0851 1.61633 10.2197 1.27751 10.2197C0.938697 10.2197 0.613756 10.0851 0.374175 9.84555C0.134595 9.60597 0 9.28103 0 8.94221ZM0 5.10967C0 4.77085 0.134595 4.44591 0.374175 4.20633C0.613756 3.96675 0.938697 3.83215 1.27751 3.83215C1.61633 3.83215 1.94127 3.96675 2.18085 4.20633C2.42043 4.44591 2.55503 4.77085 2.55503 5.10967C2.55503 5.44849 2.42043 5.77343 2.18085 6.01301C1.94127 6.25259 1.61633 6.38718 1.27751 6.38718C0.938697 6.38718 0.613756 6.25259 0.374175 6.01301C0.134595 5.77343 0 5.44849 0 5.10967ZM0 1.27712C0 0.938306 0.134595 0.613365 0.374175 0.373784C0.613756 0.134204 0.938697 -0.00039196 1.27751 -0.00039196C1.61633 -0.00039196 1.94127 0.134204 2.18085 0.373784C2.42043 0.613365 2.55503 0.938306 2.55503 1.27712C2.55503 1.61594 2.42043 1.94088 2.18085 2.18046C1.94127 2.42004 1.61633 2.55464 1.27751 2.55464C0.938697 2.55464 0.613756 2.42004 0.374175 2.18046C0.134595 1.94088 0 1.61594 0 1.27712Z"
        fill="#2E2E2E"
        fillOpacity="0.8"
      />
    </svg>
  </SvgIcon>
);

export const EditGuestIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        d="M7.08333 10.625H4.95833C3.39353 10.625 2.125 11.8935 2.125 13.4583V14.1667"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.1667 14.875H9.91675"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.1242 10.8619L12.2786 8.70747C12.4115 8.57463 12.5916 8.5 12.7795 8.5C12.9674 8.5 13.1475 8.57463 13.2804 8.70747L13.9593 9.38641C14.2359 9.66303 14.2359 10.1115 13.9593 10.3881L11.8049 12.5425C11.672 12.6754 11.4919 12.75 11.304 12.75H10.2709C10.0753 12.75 9.91675 12.5914 9.91675 12.3958V11.3627C9.91675 11.1749 9.99138 10.9947 10.1242 10.8619Z"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="7.79158"
        cy="4.95833"
        r="2.83333"
        stroke="#727475"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

const capabilityIconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg'
};

export const FoodDiningIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M4 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3M6 3v18"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 3c-1.66 0-3 2.5-3 5.5S15.34 14 17 14s0 0 0 0v7"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const HousekeepingIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M2 17v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 12V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 17v2M22 17v2"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const HotelServicesIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M3.5 18.5h17"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 18.5a7.5 7.5 0 0 1 15 0"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 7V5.2"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="4" r="1.2" fill={color} />
    </svg>
  </SvgIcon>
);

export const LocalNearbyIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M20 10c0 5.5-8 11-8 11s-8-5.5-8-11a8 8 0 0 1 16 0z"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.6" stroke={color} strokeWidth={1.9} />
    </svg>
  </SvgIcon>
);

export const TransportIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M5 12l1.5-4.5A2 2 0 0 1 8.4 6h7.2a2 2 0 0 1 1.9 1.5L19 12v6a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H8v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="15" r="0.6" fill={color} />
      <circle cx="16.5" cy="15" r="0.6" fill={color} />
    </svg>
  </SvgIcon>
);

export const HotelInfoIcon = ({
  color = 'currentColor',
  ...props
}: Omit<SvgIconProps, 'color'> & { color?: string }) => (
  <SvgIcon {...props}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth={1.9} />
      <path
        d="M12 11v5"
        stroke={color}
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="7.8" r="0.7" fill={color} />
    </svg>
  </SvgIcon>
);

export const SettingsHotelIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_19_5964)">
        <path
          d="M14.4 7.2H15.6C15.9183 7.2 16.2235 7.07357 16.4485 6.84853C16.6736 6.62348 16.8 6.31826 16.8 6C16.8 5.68174 16.6736 5.37652 16.4485 5.15147C16.2235 4.92643 15.9183 4.8 15.6 4.8H14.4C14.0817 4.8 13.7765 4.92643 13.5515 5.15147C13.3264 5.37652 13.2 5.68174 13.2 6C13.2 6.31826 13.3264 6.62348 13.5515 6.84853C13.7765 7.07357 14.0817 7.2 14.4 7.2ZM14.4 12H15.6C15.9183 12 16.2235 11.8736 16.4485 11.6485C16.6736 11.4235 16.8 11.1183 16.8 10.8C16.8 10.4817 16.6736 10.1765 16.4485 9.95147C16.2235 9.72643 15.9183 9.6 15.6 9.6H14.4C14.0817 9.6 13.7765 9.72643 13.5515 9.95147C13.3264 10.1765 13.2 10.4817 13.2 10.8C13.2 11.1183 13.3264 11.4235 13.5515 11.6485C13.7765 11.8736 14.0817 12 14.4 12ZM8.4 7.2H9.6C9.91826 7.2 10.2235 7.07357 10.4485 6.84853C10.6736 6.62348 10.8 6.31826 10.8 6C10.8 5.68174 10.6736 5.37652 10.4485 5.15147C10.2235 4.92643 9.91826 4.8 9.6 4.8H8.4C8.08174 4.8 7.77652 4.92643 7.55147 5.15147C7.32643 5.37652 7.2 5.68174 7.2 6C7.2 6.31826 7.32643 6.62348 7.55147 6.84853C7.77652 7.07357 8.08174 7.2 8.4 7.2ZM8.4 12H9.6C9.91826 12 10.2235 11.8736 10.4485 11.6485C10.6736 11.4235 10.8 11.1183 10.8 10.8C10.8 10.4817 10.6736 10.1765 10.4485 9.95147C10.2235 9.72643 9.91826 9.6 9.6 9.6H8.4C8.08174 9.6 7.77652 9.72643 7.55147 9.95147C7.32643 10.1765 7.2 10.4817 7.2 10.8C7.2 11.1183 7.32643 11.4235 7.55147 11.6485C7.77652 11.8736 8.08174 12 8.4 12ZM22.8 21.6H21.6V1.2C21.6 0.88174 21.4736 0.576515 21.2485 0.351472C21.0235 0.126428 20.7183 0 20.4 0H3.6C3.28174 0 2.97652 0.126428 2.75147 0.351472C2.52643 0.576515 2.4 0.88174 2.4 1.2V21.6H1.2C0.88174 21.6 0.576515 21.7264 0.351472 21.9515C0.126428 22.1765 0 22.4817 0 22.8C0 23.1183 0.126428 23.4235 0.351472 23.6485C0.576515 23.8736 0.88174 24 1.2 24H22.8C23.1183 24 23.4235 23.8736 23.6485 23.6485C23.8736 23.4235 24 23.1183 24 22.8C24 22.4817 23.8736 22.1765 23.6485 21.9515C23.4235 21.7264 23.1183 21.6 22.8 21.6ZM13.2 21.6H10.8V16.8H13.2V21.6ZM19.2 21.6H15.6V15.6C15.6 15.2817 15.4736 14.9765 15.2485 14.7515C15.0235 14.5264 14.7183 14.4 14.4 14.4H9.6C9.28174 14.4 8.97652 14.5264 8.75147 14.7515C8.52643 14.9765 8.4 15.2817 8.4 15.6V21.6H4.8V2.4H19.2V21.6Z"
          fill="#727475"
        />
      </g>
      <defs>
        <clipPath id="clip0_19_5964">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const LanguagesIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_19_5993)">
        <path
          d="M0 11.999C0.0358392 5.43836 5.20181 0.0307275 11.9519 8.19181e-06C18.7275 -0.0307112 24.001 5.37589 24 11.9949C23.999 18.5668 18.7695 23.9631 12.043 23.9836C5.28168 24.0051 0.046079 18.6118 0 11.999ZM3.35967 16.6151C3.39756 16.6018 3.42418 16.5987 3.44364 16.5854C4.43075 15.9618 5.47316 15.4518 6.57189 15.0545C6.63026 15.033 6.69682 14.9245 6.69272 14.86C6.66712 14.393 6.61592 13.9271 6.58827 13.4612C6.50328 12.0307 6.55346 10.6033 6.69784 9.17895C6.71627 8.9977 6.65176 8.94855 6.50431 8.89633C6.16844 8.77652 5.83053 8.65672 5.51207 8.50005C4.79017 8.14575 4.07851 7.76995 3.3607 7.40235C1.92713 9.82405 1.6742 13.578 3.35865 16.6161L3.35967 16.6151ZM17.3042 9.05402C17.3411 9.51072 17.3912 10.0012 17.4199 10.4927C17.5039 11.9314 17.4588 13.367 17.3114 14.7996C17.2929 14.9777 17.3513 15.03 17.5018 15.0832C18.5453 15.4559 19.5365 15.9362 20.4734 16.528C20.5277 16.5618 20.5881 16.5864 20.6618 16.6243C21.6489 14.7187 22.0196 12.725 21.7206 10.6146C21.5578 9.46566 21.2097 8.37922 20.6792 7.41668C19.5406 7.96861 18.4367 8.50517 17.3052 9.05402H17.3042ZM15.161 14.4299C15.3167 12.7936 15.3218 11.1757 15.159 9.55373C13.0424 9.90188 10.9525 9.90597 8.84922 9.54963C8.6905 11.1829 8.6905 12.8028 8.84922 14.435C10.9525 14.0777 13.0434 14.0828 15.161 14.4299ZM14.8487 7.38801C14.6449 6.33536 14.393 5.32879 13.9435 4.39083C13.6701 3.82047 13.3547 3.25319 12.9615 2.76168C12.3236 1.96502 11.7092 1.99369 11.0303 2.75758C10.5951 3.24704 10.2971 3.81945 10.0422 4.4154C9.63666 5.36258 9.35814 6.34663 9.16256 7.38494C11.06 7.7249 12.9349 7.7249 14.8487 7.38699V7.38801ZM9.17484 16.5956C9.18201 16.6663 9.18201 16.7154 9.19225 16.7636C9.45849 18.0312 9.80254 19.2774 10.4651 20.4017C10.7036 20.8062 11.0088 21.1861 11.3457 21.5138C11.7645 21.9213 12.2376 21.9101 12.6687 21.5138C12.941 21.2639 13.1858 20.9701 13.3906 20.6618C13.9251 19.8539 14.2476 18.9477 14.5087 18.021C14.6388 17.5592 14.7422 17.0892 14.8641 16.5987C12.94 16.2577 11.0651 16.2587 9.17484 16.5956ZM15.6341 21.1103C17.1353 20.4919 18.3671 19.6235 19.4412 18.3948C18.5913 17.9708 17.7865 17.5684 16.952 17.1506C16.6519 18.5422 16.2679 19.8304 15.6341 21.1103ZM8.37 2.87739C6.87089 3.48973 5.64519 4.3642 4.56592 5.58581C5.41787 6.01178 6.22374 6.41523 7.05726 6.83097C7.35523 5.43528 7.74435 4.14917 8.37 2.87636V2.87739ZM4.56489 18.3978C5.65646 19.6245 6.87601 20.5021 8.37 21.1032C7.74025 19.8304 7.35626 18.5432 7.05623 17.1516C6.22169 17.5694 5.41684 17.9718 4.56387 18.3978H4.56489ZM16.952 6.83301C17.7865 6.41523 18.5924 6.01281 19.4392 5.58888C18.3569 4.3642 17.1322 3.48973 15.6382 2.87841C16.2679 4.15326 16.6519 5.4404 16.953 6.83301H16.952Z"
          fill="#191B1B"
          fillOpacity="0.6"
        />
      </g>
      <defs>
        <clipPath id="clip0_19_5993">
          <rect width="24" height="23.9846" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const IntegrationsIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_19_5944)">
        <path
          d="M18.0717 24H3.61435C2.65576 24 1.73644 23.6192 1.05862 22.9414C0.380796 22.2636 0 21.3442 0 20.3857V8.33784C0 7.37925 0.380796 6.45993 1.05862 5.78211C1.73644 5.10429 2.65576 4.72349 3.61435 4.72349H4.81913C4.83939 3.70261 5.18339 2.71454 5.80152 1.90181C6.41965 1.08907 7.27995 0.493681 8.25834 0.201503C9.23673 -0.0906738 10.2826 -0.0645331 11.2452 0.276155C12.2078 0.616843 13.0373 1.25447 13.614 2.09707C14.1543 2.8671 14.4484 3.78287 14.4574 4.72349H18.0717C18.3913 4.72349 18.6977 4.85042 18.9236 5.07636C19.1496 5.3023 19.2765 5.60874 19.2765 5.92827V9.54262C20.2974 9.56288 21.2855 9.90689 22.0982 10.525C22.9109 11.1431 23.5063 12.0034 23.7985 12.9818C24.0907 13.9602 24.0645 15.0061 23.7238 15.9687C23.3832 16.9313 22.7455 17.7608 21.9029 18.3375C21.1329 18.8778 20.2171 19.1719 19.2765 19.1809V22.7952C19.2765 23.1147 19.1496 23.4212 18.9236 23.6471C18.6977 23.8731 18.3913 24 18.0717 24ZM3.61435 7.13305C3.29482 7.13305 2.98838 7.25999 2.76244 7.48593C2.5365 7.71187 2.40956 8.01831 2.40956 8.33784V20.3857C2.40956 20.7052 2.5365 21.0116 2.76244 21.2376C2.98838 21.4635 3.29482 21.5904 3.61435 21.5904H16.8669V17.7592C16.8659 17.5661 16.9113 17.3755 16.9994 17.2035C17.0875 17.0316 17.2156 16.8834 17.373 16.7713C17.5299 16.6594 17.7113 16.5868 17.9021 16.5596C18.0928 16.5323 18.2874 16.5512 18.4693 16.6147C18.8127 16.7548 19.1866 16.8029 19.5542 16.7543C19.9219 16.7057 20.2705 16.5621 20.5656 16.3376C20.8594 16.1584 21.1114 15.9184 21.3047 15.6337C21.498 15.349 21.628 15.0262 21.6861 14.687C21.7325 14.3449 21.7049 13.9969 21.6052 13.6664C21.5054 13.3359 21.3358 13.0307 21.1078 12.7714C20.7994 12.3969 20.3795 12.1306 19.9092 12.0114C19.439 11.8922 18.9429 11.9263 18.4934 12.1088C18.3115 12.1723 18.1169 12.1912 17.9262 12.1639C17.7354 12.1367 17.5539 12.0641 17.397 11.9522C17.2397 11.8401 17.1116 11.6919 17.0235 11.52C16.9354 11.348 16.89 11.1574 16.891 10.9643V7.13305H13.0357C12.8377 7.14428 12.64 7.10644 12.4601 7.02291C12.2803 6.93939 12.1238 6.81274 12.0046 6.65423C11.8854 6.49571 11.8072 6.31023 11.7769 6.11423C11.7466 5.91824 11.7652 5.7178 11.831 5.53069C11.971 5.18734 12.0191 4.8134 11.9705 4.44577C11.9219 4.07814 11.7783 3.72954 11.5539 3.43437C11.376 3.13217 11.1324 2.87387 10.8411 2.67861C10.5498 2.48334 10.2184 2.35612 9.87124 2.30637C9.52412 2.25661 9.17025 2.2856 8.83586 2.39119C8.50147 2.49678 8.19512 2.67625 7.93951 2.91632C7.70214 3.15175 7.51636 3.434 7.39398 3.74513C7.27161 4.05625 7.21532 4.38943 7.22869 4.72349C7.23148 4.99983 7.28455 5.27336 7.38531 5.53069C7.44901 5.71288 7.468 5.9077 7.44069 6.09876C7.41338 6.28981 7.34058 6.47151 7.2284 6.62856C7.11622 6.78561 6.96796 6.91341 6.79608 7.0012C6.62421 7.089 6.43376 7.13422 6.24077 7.13305H3.61435Z"
          fill="#727475"
        />
      </g>
      <defs>
        <clipPath id="clip0_19_5944">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const ApplicationsIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2244_9308)">
        <path
          d="M20.9189 14.9727V21.459H14.4326V14.9727H20.9189ZM9.02734 14.9727V21.459H2.54102V14.9727H9.02734ZM17.6758 2.54108C18.6792 2.54112 19.642 2.93894 20.3516 3.6485C21.0611 4.35807 21.459 5.32081 21.459 6.32428C21.459 7.32774 21.0611 8.29048 20.3516 9.00006C19.642 9.70962 18.6792 10.1084 17.6758 10.1085C16.6723 10.1085 15.7096 9.70964 15 9.00006C14.2904 8.29047 13.8916 7.3278 13.8916 6.32428C13.8916 5.32079 14.2904 4.35807 15 3.6485C15.7096 2.93898 16.6723 2.54108 17.6758 2.54108ZM9.02734 3.08112V9.56744H2.54102V3.08112H9.02734Z"
          stroke="#727475"
          strokeWidth="1.08108"
        />
        <path
          d="M2 2.5406H9.56754V10.1081H2V2.5406ZM13.8919 14.4325H21.4594V22H13.8919V14.4325ZM2 14.4325H9.56754V22H2V14.4325ZM21.9999 6.32437C21.9999 7.47125 21.5443 8.57116 20.7334 9.38212C19.9224 10.1931 18.8225 10.6487 17.6756 10.6487C16.5287 10.6487 15.4288 10.1931 14.6179 9.38212C13.8069 8.57116 13.3513 7.47125 13.3513 6.32437C13.3513 5.17749 13.8069 4.07759 14.6179 3.26662C15.4288 2.45566 16.5287 2.00006 17.6756 2.00006C18.8225 2.00006 19.9224 2.45566 20.7334 3.26662C21.5443 4.07759 21.9999 5.17749 21.9999 6.32437Z"
          stroke="#727475"
          strokeWidth="2.16216"
        />
      </g>
      <defs>
        <clipPath id="clip0_2244_9308">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const DesktopAppIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.25 20.875H13.25M10.25 20.875L10.625 17.875H12.875L13.25 20.875M10.25 20.875H8M13.25 20.875H15.5M14.75 4.375H3.5C3.10218 4.375 2.72064 4.53304 2.43934 4.81434C2.15804 5.09564 2 5.47718 2 5.875V16.375C2 16.7728 2.15804 17.1544 2.43934 17.4357C2.72064 17.717 3.10218 17.875 3.5 17.875H20C20.3978 17.875 20.7794 17.717 21.0607 17.4357C21.342 17.1544 21.5 16.7728 21.5 16.375V10.6251"
        stroke="#727475"
        strokeWidth="2.16"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.75 7.50006V3.00006M21.75 5.50006L19.75 8.00006L17.75 5.50006"
        stroke="#727475"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const InternalServerConfigIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 7.5H8M6 16.5H8M3 5.7C3 5.037 3.448 4.5 4 4.5H20C20.552 4.5 21 5.037 21 5.7V9.3C21 9.963 20.552 10.5 20 10.5H4C3.448 10.5 3 9.963 3 9.3V5.7ZM3 14.7C3 14.037 3.448 13.5 4 13.5H20C20.552 13.5 21 14.037 21 14.7V18.3C21 18.963 20.552 19.5 20 19.5H4C3.448 19.5 3 18.963 3 18.3V14.7Z"
        stroke="#727475"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const WifiIcon = ({
  fill = 'currentColor',
  stroke,
  ...props
}: SvgIconProps & { stroke?: string }) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.86 2.86 7.14 2.86 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"
      fill={stroke ?? fill}
      fillOpacity={0.85}
    />
  </SvgIcon>
);

export const AiConciergeIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="2 1 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fill="#727475">
        <path d="M4 11C4 5.48 8.48 1 14 1C19.52 1 24 5.48 24 11V13H22V11C22 6.59 18.41 3 14 3C9.59 3 6 6.59 6 11V13H4V11Z" />
        <rect x="2" y="10" width="4" height="8" rx="2" />
        <rect x="22" y="10" width="4" height="8" rx="2" />
        <path d="M24 17C24 21.42 20.42 25 16 25H13.5V23H16C19.31 23 22 20.31 22 17H24Z" />
        <rect x="10" y="22" width="5" height="3" rx="1.5" />
        <path d="M14 7L15.4 10.6L19 12L15.4 13.4L14 17L12.6 13.4L9 12L12.6 10.6L14 7Z" />
      </g>
    </svg>
  </SvgIcon>
);

export const StaffIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.344 12.5256C12.9269 12.0211 13.3944 11.397 13.7149 10.6959C14.0353 9.99469 14.2011 9.2328 14.2011 8.46189C14.2011 7.01329 13.6256 5.62402 12.6013 4.5997C11.577 3.57538 10.1877 2.99993 8.73914 2.99993C7.29054 2.99993 5.90127 3.57538 4.87695 4.5997C3.85263 5.62402 3.27718 7.01329 3.27718 8.46189C3.27717 9.2328 3.443 9.99469 3.76343 10.6959C4.08385 11.397 4.55137 12.0211 5.13424 12.5256C3.60505 13.218 2.30764 14.3363 1.39715 15.7466C0.486656 17.1569 0.00160784 18.7995 0 20.4782C0 20.7679 0.115091 21.0458 0.319954 21.2506C0.524818 21.4555 0.802672 21.5706 1.09239 21.5706C1.38211 21.5706 1.65997 21.4555 1.86483 21.2506C2.06969 21.0458 2.18478 20.7679 2.18478 20.4782C2.18478 18.7399 2.87533 17.0728 4.10451 15.8436C5.33369 14.6144 7.00082 13.9239 8.73914 13.9239C10.4775 13.9239 12.1446 14.6144 13.3738 15.8436C14.6029 17.0728 15.2935 18.7399 15.2935 20.4782C15.2935 20.7679 15.4086 21.0458 15.6134 21.2506C15.8183 21.4555 16.0962 21.5706 16.3859 21.5706C16.6756 21.5706 16.9535 21.4555 17.1583 21.2506C17.3632 21.0458 17.4783 20.7679 17.4783 20.4782C17.4767 18.7995 16.9916 17.1569 16.0811 15.7466C15.1706 14.3363 13.8732 13.218 12.344 12.5256ZM8.73914 11.7391C8.09097 11.7391 7.45737 11.5469 6.91844 11.1868C6.37951 10.8267 5.95946 10.3148 5.71142 9.71601C5.46338 9.11719 5.39848 8.45825 5.52493 7.82254C5.65138 7.18683 5.9635 6.6029 6.42183 6.14458C6.88015 5.68625 7.46408 5.37413 8.09979 5.24768C8.7355 5.12123 9.39443 5.18613 9.99326 5.43417C10.5921 5.68221 11.1039 6.10226 11.464 6.64119C11.8241 7.18012 12.0163 7.81373 12.0163 8.46189C12.0163 9.33105 11.671 10.1646 11.0565 10.7792C10.4419 11.3938 9.6083 11.7391 8.73914 11.7391ZM19.379 12.0886C20.0781 11.3014 20.5348 10.3288 20.6941 9.28811C20.8533 8.24737 20.7084 7.18278 20.2767 6.22249C19.8451 5.26219 19.145 4.44712 18.2609 3.8754C17.3768 3.30367 16.3463 2.99965 15.2935 2.99993C15.0038 2.99993 14.7259 3.11502 14.5211 3.31988C14.3162 3.52475 14.2011 3.8026 14.2011 4.09232C14.2011 4.38204 14.3162 4.65989 14.5211 4.86476C14.7259 5.06962 15.0038 5.18471 15.2935 5.18471C16.1627 5.18471 16.9962 5.52999 17.6108 6.14458C18.2254 6.75917 18.5707 7.59273 18.5707 8.46189C18.5691 9.03566 18.417 9.59896 18.1294 10.0955C17.8419 10.592 17.429 11.0043 16.9321 11.2912C16.7701 11.3846 16.6349 11.518 16.5392 11.6787C16.4436 11.8394 16.3908 12.0219 16.3859 12.2088C16.3813 12.3942 16.424 12.5778 16.5101 12.7422C16.5961 12.9065 16.7225 13.0463 16.8775 13.1483L17.3035 13.4323L17.4455 13.5087C18.7623 14.1333 19.8731 15.1211 20.6473 16.3559C21.4214 17.5906 21.8266 19.0209 21.8151 20.4782C21.8151 20.7679 21.9302 21.0458 22.135 21.2506C22.3399 21.4555 22.6177 21.5706 22.9075 21.5706C23.1972 21.5706 23.475 21.4555 23.6799 21.2506C23.8848 21.0458 23.9999 20.7679 23.9999 20.4782C24.0088 18.8019 23.5889 17.1511 22.7802 15.6827C21.9714 14.2143 20.8006 12.9771 19.379 12.0886Z"
        fill="#727475"
      />
    </svg>
  </SvgIcon>
);

export const LockIcon = ({
  stroke = 'var(--gray-2)',
  ...props
}: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <rect
        x="4"
        y="11"
        width="16"
        height="10"
        rx="2"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 11V7a4 4 0 0 1 8 0v4"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const EditPasswordIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1483_10141)">
        <path
          d="M1.43129 8.27275C2.22757 4.87812 4.87812 2.22757 8.27275 1.43129C10.7243 0.856236 13.2757 0.856235 15.7273 1.43129C19.1219 2.22757 21.7724 4.87812 22.5687 8.27275C23.1438 10.7243 23.1438 13.2757 22.5687 15.7272C21.7724 19.1219 19.1219 21.7724 15.7272 22.5687C13.2757 23.1438 10.7243 23.1438 8.27275 22.5687C4.87812 21.7724 2.22757 19.1219 1.43129 15.7273C0.856235 13.2757 0.856235 10.7243 1.43129 8.27275Z"
          stroke="#727475"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M12 6.91422C10.2445 6.91422 8.82139 8.33733 8.82139 10.0928C8.82139 11.1953 9.38263 12.1666 10.235 12.7368L9.3624 15.4213C9.09537 16.2428 9.70774 17.0858 10.5716 17.0858H13.4284C14.2923 17.0858 14.9046 16.2428 14.6376 15.4213L13.765 12.7368C14.6174 12.1666 15.1786 11.1953 15.1786 10.0928C15.1786 8.33733 13.7555 6.91422 12 6.91422Z"
          stroke="#727475"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1483_10141">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const BrandingIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1502_42853)">
        <path
          d="M15.7857 9.2857C16.2783 9.2857 16.7506 9.09003 17.0989 8.74175C17.4472 8.39347 17.6429 7.9211 17.6429 7.42855C17.6429 6.93601 17.4472 6.46364 17.0989 6.11536C16.7506 5.76707 16.2783 5.57141 15.7857 5.57141C15.2932 5.57141 14.8208 5.76707 14.4725 6.11536C14.1242 6.46364 13.9286 6.93601 13.9286 7.42855C13.9286 7.9211 14.1242 8.39347 14.4725 8.74175C14.8208 9.09003 15.2932 9.2857 15.7857 9.2857ZM8.35714 18.5714C8.60342 18.5714 8.8396 18.4736 9.01374 18.2994C9.18788 18.1253 9.28571 17.8891 9.28571 17.6428C9.28571 17.3966 9.18788 17.1604 9.01374 16.9862C8.8396 16.8121 8.60342 16.7143 8.35714 16.7143C8.11087 16.7143 7.87468 16.8121 7.70054 16.9862C7.5264 17.1604 7.42857 17.3966 7.42857 17.6428C7.42857 17.8891 7.5264 18.1253 7.70054 18.2994C7.87468 18.4736 8.11087 18.5714 8.35714 18.5714ZM8.35714 12.0714C8.84969 12.0714 9.32206 11.8757 9.67034 11.5275C10.0186 11.1792 10.2143 10.7068 10.2143 10.2143C10.2143 9.72172 10.0186 9.24935 9.67034 8.90107C9.32206 8.55279 8.84969 8.35713 8.35714 8.35713C7.8646 8.35713 7.39223 8.55279 7.04394 8.90107C6.69566 9.24935 6.5 9.72172 6.5 10.2143C6.5 10.7068 6.69566 11.1792 7.04394 11.5275C7.39223 11.8757 7.8646 12.0714 8.35714 12.0714Z"
          stroke="#727475"
          strokeWidth="1.95"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.6799 22.8057C17.6592 22.4455 17.534 22.0991 17.3196 21.8088C17.1052 21.5186 16.811 21.2971 16.4727 21.1714C15.6328 20.8953 14.9188 20.3287 14.4593 19.5733C13.9997 18.8179 13.8247 17.9234 13.9657 17.0505C14.1067 16.1776 14.5545 15.3837 15.2285 14.8114C15.9025 14.2392 16.7586 13.9261 17.6427 13.9286H21.1156C21.7112 13.9302 22.2985 13.7886 22.828 13.5157C23.3574 13.2427 23.8134 12.8464 24.1575 12.3603C24.5017 11.8741 24.7239 11.3123 24.8053 10.7222C24.8868 10.1322 24.8251 9.53122 24.6256 8.96999C23.8869 6.88436 22.5893 5.04186 20.8744 3.64379C19.1595 2.24572 17.0933 1.34587 14.9016 1.04254C12.7099 0.739212 10.4771 1.04408 8.44691 1.92384C6.41676 2.8036 4.66746 4.2244 3.39012 6.03102C2.11277 7.83763 1.35653 9.96057 1.20401 12.1679C1.05149 14.3752 1.50856 16.582 2.52529 18.5471C3.54202 20.5122 5.07928 22.1601 6.96914 23.3107C8.85899 24.4613 11.0287 25.0704 13.2413 25.0714C14.334 25.0745 15.4217 24.9244 16.4727 24.6257C16.8588 24.5175 17.1931 24.2742 17.4147 23.9401C17.6362 23.606 17.7304 23.2034 17.6799 22.8057Z"
          stroke="#727475"
          strokeWidth="1.95"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1502_42853">
          <rect width="26" height="26" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

export const CopyIcon = ({ stroke = '#2E2E2E', ...props }: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <rect
        x="9"
        y="9"
        width="11"
        height="11"
        rx="2.5"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 5.5V4.5C15 3.39543 14.1046 2.5 13 2.5H4.5C3.39543 2.5 2.5 3.39543 2.5 4.5V13C2.5 14.1046 3.39543 15 4.5 15H5.5"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const OrganizationIcon = ({
  stroke = '#2E2E2E',
  ...props
}: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M3 21H21"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 21V5C5 3.89543 5.89543 3 7 3H14C15.1046 3 16 3.89543 16 5V21"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 9H18C19.1046 9 20 9.89543 20 11V21"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 7H12M9 11H12M9 15H12"
        stroke={stroke}
        strokeOpacity="0.8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const SearchEmptyStateIcon = ({
  width = 80,
  height = 80,
  ...props
}: { width?: number; height?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="40" cy="40" r="38" fill="var(--primary-100)" />
    <circle
      cx="36"
      cy="36"
      r="14"
      stroke="var(--primary)"
      strokeWidth="2.25"
      strokeLinecap="round"
    />
    <path
      d="M46 46L53.5 53.5"
      stroke="var(--primary)"
      strokeWidth="2.25"
      strokeLinecap="round"
    />
  </svg>
);

export const ChatEmptyStateIcon = ({
  width = 110,
  height = 110,
  ...props
}: { width?: number; height?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 72 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M15 25a7 7 0 0 1 7-7h16a7 7 0 0 1 7 7v8a7 7 0 0 1-7 7H26l-7.4 5.5A1.1 1.1 0 0 1 16.9 44l.4-4.4A7 7 0 0 1 15 33Z"
      fill="#E9E1FC"
    />
    <circle cx="24.5" cy="29" r="2.1" fill="#B9A6EE" />
    <circle cx="30.5" cy="29" r="2.1" fill="#B9A6EE" />
    <circle cx="36.5" cy="29" r="2.1" fill="#B9A6EE" />
    <path
      d="M36 40a7 7 0 0 1 7-7h15a7 7 0 0 1 7 7v8a7 7 0 0 1-7 7h-1.2l3.6 4.3a1 1 0 0 1-1.3 1.5L51 55h-8a7 7 0 0 1-7-7Z"
      fill="#FFFFFF"
      stroke="var(--primary)"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M44 44h13M44 49h8"
      stroke="var(--primary)"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

export const MoveToGroupIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="15"
      viewBox="0 0 16 15"
      fill="none"
    >
      <path
        d="M1.43457 7.12548V2.24405C1.43457 1.81465 1.60515 1.40284 1.90878 1.09921C2.21241 0.795578 2.62422 0.625 3.05362 0.625H3.06171L12.776 0.671952C13.204 0.674092 13.6137 0.845614 13.9156 1.14901C14.2175 1.45241 14.387 1.863 14.387 2.291V12.0053C14.387 12.4347 14.2164 12.8465 13.9127 13.1501C13.6091 13.4538 13.1973 13.6243 12.7679 13.6243H12.7598L3.04552 13.5782C2.61753 13.5761 2.20779 13.4045 1.90591 13.1011C1.60403 12.7977 1.43456 12.3871 1.43457 11.9591V10.3943M1.43457 3.91814H14.387"
        stroke="#746C82"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.29167 6.34668L8.72024 8.77525L6.29167 11.2038M8.72024 8.77525H0.625"
        stroke="#746C82"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </SvgIcon>
);
